import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import api, { templatesApi, jobsApi, adminApi, bcApi, nsApi, groupsApi } from '../services/api'
import { formatDate, formatDateTime, formatRecent, formatTimeAgo } from '../utils/dateUtils'
import { useToast } from '../context/ToastContext'
import { useAuth } from '../context/AuthContext'
import { useFeatures } from '../contexts/FeaturesContext'
import { isReadOnlyUser, READ_ONLY_MESSAGE } from '../utils/readerAccess'
import {
  bidFieldsFromMappings,
  excludedBidLabels,
} from '../utils/nsBidFields'
import UserAvatar from '../components/UserAvatar'
import Toggle from '../components/Toggle'
import CollectionGrid from '../components/collection/CollectionGrid'
import { trackSpotlight, useCardLink, useRowGlow } from '../components/collection/cardInteractions'
import runCurtainTransition from '../utils/curtainTransition'
import WizardStepper from '../components/WizardStepper'
import useStoredViewMode from '../hooks/useStoredViewMode'
import { nsEntityDisplayName } from '../utils/nsEntityLabels'
import NsRecordTypePicker from '../components/NsRecordTypePicker'
import TemplateJsonImportModal from '../components/TemplateJsonImportModal'
import { TemplateOpCoChip, TemplateOpCoPicker, sameTemplateOpco, useTemplateOpCoPicker } from '../components/opco/TemplateOpCo'
import { useOpCo } from '../context/OpCoContext'
import ViewModeToggle from '../components/collection/ViewModeToggle'
import SuggestionQuestions from '../components/ai-suggest/SuggestionQuestions'
import { isOptionalSuggestion, selectedColumns } from '../utils/applySuggestionPlan'
import { waitForSuggestionJob } from '../utils/suggestionJobs'

import {
  Plus,
  FileStack,
  Bot,
  Wrench,
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Loader2,
  AlertCircle,
  AlertTriangle,
  CheckCircle2,
  FileText,
  Search,
  RefreshCw,
  Building2,
  Info,
  X,
  UploadCloud,
  Link2,
  Layers,
  Globe,
  UsersRound,
  ChevronRight,
  ChevronDown,
  Pencil,
  Trash2,
  ShieldCheck,
  Check,
  Zap,
  Eye,
  EyeOff,
  Copy,
  Lock,
  Boxes,
} from 'lucide-react'

// Rich descriptions for well-known NetSuite PO field keys, mirroring TemplateDetail.jsx's
// NS_FIELD_HINTS so wizard-time field descriptions match the post-creation Fields tab.
const NS_FIELD_HINTS = {
  entity: 'The vendor/supplier company name \u2014 the business that issued this purchase order and that goods or services are being bought from. This is NOT the buyer, "Deliver To", or "Ship To" company.',
  tranDate: 'The transaction/order date the purchase order was issued.',
  tranId: 'The purchase order number (PO number) as printed on the document, e.g. "PO-1234" or "Order No: 1587626".',
  subsidiary: 'The subsidiary or business entity this purchase order belongs to.',
  location: 'The delivery/ship-to location, site, or warehouse for this purchase order.',
  currency: 'The currency the amounts on this purchase order are stated in (e.g. USD, AUD, GBP).',
  terms: 'The payment terms shown on the document (e.g. "Net 30", "30 Days from EOM").',
  memo: 'Any notes, comments, or remarks printed on the purchase order.',
}

const NS_MANDATORY_KEYS = ['entity', 'tranDate', 'subsidiary']

// Rich descriptions for well-known Business Central PO field keys, mirroring the NS
// hints above so wizard-time field descriptions read consistently across integrations.
const BC_FIELD_HINTS = {
  vendorNumber: 'The vendor/supplier company name or number \u2014 the business that issued this purchase order and that goods or services are being bought from. This is NOT the buyer, "Deliver To", or "Ship To" company.',
  buyFromVendorName: 'The vendor/supplier company name as printed on the document.',
  orderDate: 'The transaction/order date the purchase order was issued.',
  yourReference: 'The purchase order number (PO number) or reference as printed on the document, e.g. "PO-1234" or "Order No: 1587626".',
  currencyCode: 'The currency the amounts on this purchase order are stated in (e.g. USD, AUD, GBP).',
  paymentTermsCode: 'The payment terms shown on the document (e.g. "Net 30", "30 Days from EOM").',
  shipToAddressLine1: 'The delivery/ship-to address, site, or warehouse for this purchase order.',
  shipToCity: 'The delivery/ship-to city for this purchase order.',
  shipToCountry: 'The delivery/ship-to country for this purchase order.',
  shipToPostCode: 'The delivery/ship-to postal code for this purchase order.',
  requestedReceiptDate: 'The requested delivery/receipt date for this purchase order.',
}

const BC_MANDATORY_KEYS = ['vendorNumber', 'orderDate']

// ─── Compact searchable field-mapping select ──────────────────────────────────
// Replaces the plain <select> used for the NS/BC Mapping column — a NetSuite
// Purchase Order template's own catalog runs to 250+ fields, and scrolling a
// native dropdown that long to find one field is exactly the kind of search
// gap already fixed for the request-page vendor/reseller pickers. Mirrors
// TemplateNSSettings.jsx's LookupSelect (search box + filtered list, click
// outside to close), sized down to fit a dense table cell.
function groupNsMappingOptions(flatFields) {
  if (!flatFields || flatFields.length === 0) return []
  const required = flatFields.filter((f) => f.mandatory)
  const custom = flatFields.filter((f) => f.is_custom && !f.mandatory)
  const standard = flatFields.filter((f) => !f.is_custom && !f.mandatory)
  const groups = []
  if (required.length) groups.push({ group: 'Required', fields: required })
  if (standard.length) groups.push({ group: 'Standard', fields: standard })
  if (custom.length) groups.push({ group: 'Custom', fields: custom })
  return groups
}

// Scope is part of a field's identity, not decoration: mapping a header value
// onto a LINE field silently moves it into the line table at review time,
// which is invisible from the label alone.
const nsOptionScopeSuffix = (option) => (option?.scope === 'line' ? ' · Line' : '')

function MappingFieldSelect({ value, options, onChange }) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [coords, setCoords] = useState(null)
  const btnRef = useRef(null)
  const panelRef = useRef(null)

  const selected = options.find((o) => o.key === value)

  const filtered = useMemo(() => {
    if (!query.trim()) return options
    const q = query.toLowerCase()
    return options
      .filter((o) => (o.label || '').toLowerCase().includes(q) || (o.key || '').toLowerCase().includes(q))
  }, [options, query])

  // Same Required / Standard / Custom grouping the persisted Fields tab uses,
  // so the wizard and the saved template read identically.
  const groupedFiltered = useMemo(() => groupNsMappingOptions(filtered), [filtered])

  // Rendered through a portal at fixed coordinates, not a plain absolute
  // child — every field row's mapping cell sits inside a table wrapper with
  // overflow-hidden (for the rounded-corner card look), which would clip an
  // absolutely-positioned popover the instant it's near the bottom/edge of
  // that box. Mirrors ItemSearchDropdown in NSRequestView.jsx, which the
  // codebase already uses for the identical problem.
  const updateCoords = useCallback(() => {
    if (!btnRef.current) return
    const rect = btnRef.current.getBoundingClientRect()
    const maxPanelHeight = 240 // matches max-h-60 below
    const spaceBelow = window.innerHeight - rect.bottom
    const openUpward = spaceBelow < maxPanelHeight && rect.top > spaceBelow
    setCoords({
      left: rect.left,
      // Wide enough for the full custrecord_* internal id on its own line —
      // several NetSuite custom fields share a label ("Reseller", "Reseller
      // Postal Address"), so a truncated id makes them indistinguishable.
      width: Math.max(rect.width, 300),
      top: openUpward ? null : rect.bottom + 2,
      bottom: openUpward ? window.innerHeight - rect.top + 2 : null,
    })
  }, [])

  useEffect(() => {
    if (!open) return
    updateCoords()
    function handleClickOutside(e) {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return
      setOpen(false)
    }
    document.addEventListener('mousedown', handleClickOutside)
    window.addEventListener('scroll', updateCoords, true)
    window.addEventListener('resize', updateCoords)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
      window.removeEventListener('scroll', updateCoords, true)
      window.removeEventListener('resize', updateCoords)
    }
  }, [open, updateCoords])

  return (
    <div className="relative w-full">
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title={selected ? `${selected.label || selected.key} (${selected.key})` : undefined}
        className={`w-full flex items-center justify-between gap-1 px-2 py-1 text-[11px] rounded border ${
          value
            ? 'border-navy-300 dark:border-navy-700 bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-300'
            : 'border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-500'
        }`}
      >
        <span className="min-w-0 flex-1 flex flex-col items-start leading-tight">
          <span className="w-full truncate">
            {selected
              ? `${selected.label || selected.key}${nsOptionScopeSuffix(selected)}`
              : (value || '— None —')}
          </span>
          {/* The NetSuite internal id is what actually gets posted, and several
              custom fields share a label, so it stays visible on the closed
              control rather than only inside the open list. */}
          {(selected || value) && (
            <span className="w-full truncate font-mono text-[9px] text-navy-500/80 dark:text-navy-400/80">
              {selected ? selected.key : value}
            </span>
          )}
        </span>
        <ChevronDown className={`h-3 w-3 flex-shrink-0 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && coords && createPortal(
        <div
          ref={panelRef}
          className="fixed z-[9999] bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-xl max-h-60 overflow-hidden flex flex-col"
          style={{
            top: coords.top != null ? `${coords.top}px` : undefined,
            bottom: coords.bottom != null ? `${coords.bottom}px` : undefined,
            left: `${coords.left}px`,
            width: `${coords.width}px`,
          }}
        >
          <div className="p-1.5 border-b border-gray-100 dark:border-gray-700 flex-shrink-0">
            <div className="flex items-center gap-1.5 px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <Search className="h-3 w-3 text-gray-400 flex-shrink-0" />
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search fields…"
                autoFocus
                className="flex-1 min-w-0 text-xs bg-transparent focus:outline-none text-gray-800 dark:text-gray-200"
              />
              {query && (
                <button type="button" onClick={() => setQuery('')}><X className="h-3 w-3 text-gray-400" /></button>
              )}
            </div>
          </div>
          <div className="overflow-y-auto">
            <button
              type="button"
              onClick={() => { onChange(''); setOpen(false); setQuery('') }}
              className="w-full text-left px-3 py-1.5 text-xs text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              — None —
            </button>
            {groupedFiltered.map((group) => (
              <div key={group.group}>
                <div className="px-3 pt-2 pb-0.5 sticky top-0 bg-white dark:bg-gray-800 z-10">
                  <span className="text-[9px] font-bold text-gray-400 uppercase tracking-wider">{group.group}</span>
                </div>
                {group.fields.map((o) => (
                  <button
                    key={o.key}
                    type="button"
                    onClick={() => { onChange(o.key); setOpen(false); setQuery('') }}
                    title={`${o.label || o.key} (${o.key})`}
                    className={`w-full text-left px-3 py-1.5 flex flex-col gap-0.5 hover:bg-purple-50 dark:hover:bg-purple-900/20 ${value === o.key ? 'bg-purple-100 dark:bg-purple-900/30' : ''}`}
                  >
                    <span className="text-xs text-gray-900 dark:text-gray-100 leading-snug break-words">
                      {o.label || o.key}{nsOptionScopeSuffix(o)}
                    </span>
                    <span className="text-[10px] text-gray-400 font-mono break-all">{o.key}</span>
                  </button>
                ))}
              </div>
            ))}
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-xs text-gray-400 italic text-center">No fields match your search.</p>
            )}
          </div>
        </div>,
        document.body
      )}
    </div>
  )
}

function AnalyzerStatusBadge({ template }) {
  const hasReadyAnalyzer = isAnalyzerReady(template)

  if (hasReadyAnalyzer) {
    return (
      <span className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700 ring-1 ring-inset ring-emerald-600/20 dark:bg-emerald-400/10 dark:text-emerald-300 dark:ring-emerald-400/20">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500 shadow-[0_0_0_3px_rgb(16_185_129/0.18)]" />
        Analyzer Ready
      </span>
    )
  }

  return (
    <span className="inline-flex flex-shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-300 dark:ring-amber-400/20">
      <Wrench className="h-3 w-3" />
      Build Needed
    </span>
  )
}

function isAnalyzerReady(template) {
  return !!(
    template?.analyzer?.is_active &&
    String(template?.analyzer?.azure_analyzer_id || '').trim()
  )
}

// formatDate imported from ../utils/dateUtils

function parseSourceDocs(raw) {
  if (!raw) return []
  try {
    const docs = JSON.parse(raw)
    return Array.isArray(docs) ? docs : []
  } catch { return [] }
}

function getMethodLabel(method) {
  const methods = {
    ai_suggest:          'AI Suggested',
    plugin_auto:         'Plugin (Auto)',
    json_import:         'JSON Import',
    manual:              'Created Manually',
    bc_purchase_order:   'Business Central PO',
    ns_purchase_order:   'NetSuite',
  }
  return methods[method] || method || 'Created Manually'
}

// Display-only sample descriptions for templates that don't have one saved yet.
// Keyed by lowercased template name. Falls back to the template's real description.
const DEMO_DESCRIPTIONS = {
  'purchase order': 'Extracts structured data from vendor Purchase Order documents — PO number, order and delivery dates, vendor and buyer details, and full line items (SKU, description, quantity, unit price and line totals), plus tax, shipping terms and currency. Use it to automate three-way matching, validate captured values against Business Central, and post approved orders straight into your ERP without any manual re-keying.',
}

function getTemplateDescription(template) {
  if (template?.description && template.description.trim()) return template.description
  const key = (template?.name || '').trim().toLowerCase()
  return DEMO_DESCRIPTIONS[key] || null
}

function CreateTemplateModal({ open, onClose, onCreate, creating, existingTemplates = [], isAdmin = false, fullPage = false, onOpenJsonImport = null }) {
  const features = useFeatures()
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [allowReprocessing, setAllowReprocessing] = useState(true)
  const [sourceTemplateId, setSourceTemplateId] = useState('')
  const [reuseSourceAnalyzer, setReuseSourceAnalyzer] = useState(true)
  const [sourceTemplateSearch, setSourceTemplateSearch] = useState('')
  const [sourceTemplateDropdownOpen, setSourceTemplateDropdownOpen] = useState(false)
  const sourceTemplatePickerRef = useRef(null)
  const connectorEventsPickerRef = useRef(null)
  const [wizardStep, setWizardStep] = useState(1)
  const [sampleInputMode, setSampleInputMode] = useState('ai_sample_docs') // ai_sample_docs | json_fields_import
  const [sampleDocuments, setSampleDocuments] = useState([])
  const [jsonImportFile, setJsonImportFile] = useState(null)
  const [accessTab, setAccessTab] = useState('users') // users | groups
  const [orgUsers, setOrgUsers] = useState([])
  const [orgGroups, setOrgGroups] = useState([])
  const [selectedAccessGroupIds, setSelectedAccessGroupIds] = useState(new Set())
  const [connectorMode, setConnectorMode] = useState('create_webhook') // later | select_existing | create_webhook
  const [connectorSearch, setConnectorSearch] = useState('')
  const [connectorName, setConnectorName] = useState('')
  const [connectorUrl, setConnectorUrl] = useState('')
  const [connectorEvents, setConnectorEvents] = useState('request.approved')
  const [connectorEventsOpen, setConnectorEventsOpen] = useState(false)
  const [connectorUpdateMode, setConnectorUpdateMode] = useState('append')
  const [connectorSecret, setConnectorSecret] = useState('')
  const [connectorShowSecret, setConnectorShowSecret] = useState(false)
  const [connectorBasicAuthUsername, setConnectorBasicAuthUsername] = useState('')
  const [connectorBasicAuthPassword, setConnectorBasicAuthPassword] = useState('')
  const [connectorShowBasicAuthPassword, setConnectorShowBasicAuthPassword] = useState(false)
  const [availableWebhooks, setAvailableWebhooks] = useState([])
  const [loadingWebhooks, setLoadingWebhooks] = useState(false)
  const [selectedConnectorWebhookId, setSelectedConnectorWebhookId] = useState('')
  const [connectorError, setConnectorError] = useState('')

  // Per-user role assignment: { [userId]: 'reviewer' | 'approver' | 'template_manager' }
  const [userRoleMap, setUserRoleMap] = useState({})
  // Role selected in the picker row before the user is added (per userId)
  const [pendingRoleMap, setPendingRoleMap] = useState({})
  const [usersLoading, setUsersLoading] = useState(false)
  const [accessUserSearch, setAccessUserSearch] = useState('')
  const [accessGroupSearch, setAccessGroupSearch] = useState('')
  const [accessUserDropdownOpen, setAccessUserDropdownOpen] = useState(false)
  const [accessGroupDropdownOpen, setAccessGroupDropdownOpen] = useState(false)
  const [accessSearchRoleMap, setAccessSearchRoleMap] = useState({})
  const [justAddedUserId, setJustAddedUserId] = useState(null)
  const [justAddedGroupId, setJustAddedGroupId] = useState(null)
  const accessUserPickerRef = useRef(null)
  const accessGroupPickerRef = useRef(null)
  // Simple approver select used only in the non-admin (single-page) creation form
  const [approverId, setApproverId] = useState('')
  // OpCo of the new template: OpCo admins in the All OpCos view pick it
  // ('' = shared); everyone else creates in their workspace (no picker).
  const showOpcoPicker = useTemplateOpCoPicker()
  const { opcos: opcoList } = useOpCo()
  const [createOpcoCode, setCreateOpcoCode] = useState('')
  const emitCreate = (payload) => {
    if (!showOpcoPicker) return onCreate(payload)
    const next = { ...payload, opco_code: createOpcoCode || null }
    if (next.source_template_id && next.reuse_source_analyzer) {
      // A copy for another OpCo builds its own analyzer (the backend refuses reuse).
      const source = existingTemplates.find(t => String(t.id) === String(next.source_template_id))
      if (!sameTemplateOpco(source, createOpcoCode, opcoList)) next.reuse_source_analyzer = false
    }
    return onCreate(next)
  }

  // BC creation method state
  const [creationMode, setCreationMode] = useState('blank') // 'blank' | 'from_template' | 'bc_purchase_order' | 'ns_purchase_order' | 'boomi_purchase_order'
  const [bcStatus, setBcStatus] = useState(null)   // { connected, company, environment } | null
  const [bcLoading, setBcLoading] = useState(false)
  // Separate from bcLoading: this is the live per-connection OAuth probe fired
  // when a specific connection is selected (see the effect below), not the
  // initial "is BC configured at all" check.
  const [bcTesting, setBcTesting] = useState(false)
  const [bcLoadError, setBcLoadError] = useState('')
  // True once the registered connections have loaded, so the connection check below
  // knows which connection to test. Before this the environment was probed first and
  // the chosen connection second, and whichever answered last decided what the user saw.
  const [bcConnsLoaded, setBcConnsLoaded] = useState(false)
  const [verificationPrompt, setVerificationPrompt] = useState('')
  // BC entity type + connection selection (multi-entity-type / multi-connection
  // support) — the wizard has no template yet, so these are chosen up front and
  // sent explicitly on both extract-from-document and template creation.
  const [bcEntityTypes, setBcEntityTypes] = useState([])
  const [selectedBcEntityType, setSelectedBcEntityType] = useState('')
  const [bcConnections, setBcConnections] = useState([])
  const [selectedBcConnectionId, setSelectedBcConnectionId] = useState('')
  // NS record types the template can target, and the chosen one. Prefetched
  // on modal open exactly like bcEntityTypes: the endpoint touches neither
  // NetSuite nor the DB, so the picker must not wait on the connection probe
  // — an admin can choose what they are building even mid-outage.
  //
  // Registering a record type that has never been used here before now
  // happens inside NsRecordTypePicker, in place, as part of picking it —
  // there is no separate "add a record type" mode for this screen to track.
  const [nsEntityTypes, setNsEntityTypes] = useState([])
  const [selectedNsEntityType, setSelectedNsEntityType] = useState('purchase_order')
  const [nsTypesReady, setNsTypesReady] = useState(false)
  const [nsTypesError, setNsTypesError] = useState('')
  // BC Field detection state
  const [bcDetecting, setBcDetecting] = useState(false)
  const [bcDetectResult, setBcDetectResult] = useState(null)
  const [bcCheckedFields, setBcCheckedFields] = useState({})
  // BC document-upload AI mapping state (mirrors NS upload flow)
  const [bcUploading, setBcUploading] = useState(false)
  const [bcUploadError, setBcUploadError] = useState('')
  const [bcMappingResult, setBcMappingResult] = useState(null) // result from extract-from-document
  // Editable BC field rows shown in the Configure Fields step — mirrors nsEditableFields.
  const [bcEditableFields, setBcEditableFields] = useState([])
  const [bcEditingRowKey, setBcEditingRowKey] = useState('')
  const [bcFieldSearch, setBcFieldSearch] = useState('')
  // NS connection status
  const [nsStatus, setNsStatus] = useState(null)
  const [nsLoading, setNsLoading] = useState(false)
  const [nsLoadError, setNsLoadError] = useState('')
  // Step 1 of NetSuite setup: which company (subsidiary) and environment this
  // template targets. Companies come from the live account; the environment is
  // fixed by the configured credentials (see nsScope.environment_locked).
  const [nsScope, setNsScope] = useState(null)
  const [nsCompanyId, setNsCompanyId] = useState('')
  const [nsEnvironmentId, setNsEnvironmentId] = useState('')
  const [nsDetecting, setNsDetecting] = useState(false)
  const [nsDetectResult, setNsDetectResult] = useState(null)
  const [nsCheckedFields, setNsCheckedFields] = useState({})
  const [nsFieldSearch, setNsFieldSearch] = useState('')
  // NS document-upload AI mapping state
  const [nsInputMode, setNsInputMode] = useState('checklist') // 'checklist' | 'upload'
  const [nsUploading, setNsUploading] = useState(false)
  const [nsUploadError, setNsUploadError] = useState('')
  const [nsMappingResult, setNsMappingResult] = useState(null) // result from extract-from-document
  // NS field-source picker on the Sample Documents step: upload a PDF for AI
  // mapping (default), import a NetSuite-shaped JSON file, or skip and build
  // fields manually on the next step. Deliberately separate from the generic
  // "blank" mode's sampleInputMode/jsonImportFile — NS JSON rows carry NS
  // mapping data (ns_field, mandatory lookups) that the blank-mode dict shape
  // has no room for, so this gets its own state and its own parser rather
  // than being folded into the generic one.
  const [nsFieldSourceMode, setNsFieldSourceMode] = useState('upload') // 'upload' | 'blank'
  const [nsShowReview, setNsShowReview] = useState(false)
  const [nsFinalFields, setNsFinalFields] = useState(null) // confirmed selected_fields
  // Editable NS field rows shown in the Configure Fields step — mirrors the layout of
  // the persisted Fields tab table in TemplateDetail.jsx (Display/Field Name/Type/
  // Required/Description/User Input/NS Mapping/Actions), but operates purely on local
  // state since no template exists yet in the wizard.
  const [nsEditableFields, setNsEditableFields] = useState([])
  const [nsEditingRowKey, setNsEditingRowKey] = useState('')
  // Which nsEditableFields rows are checked to actually be created with the
  // template. Defaults to "all selected" whenever the row set is (re)populated —
  // unchecking a row here does NOT delete it (the trash icon still does that),
  // it just excludes it from the template created at the end of the wizard.
  const [nsSelectedFieldKeys, setNsSelectedFieldKeys] = useState(new Set())
  // Blank mode extraction preview state
  const [blankDetecting, setBlankDetecting] = useState(false)
  const [blankDetectError, setBlankDetectError] = useState('')
  const [blankGuidancePrompt, setBlankGuidancePrompt] = useState('')
  const [blankRestrict, setBlankRestrict] = useState(false)
  const [blankDetailLevel, setBlankDetailLevel] = useState('standard')
  // The finished AI suggestion (its questions and job id), the optional
  // extras held back from the field list, and progress while it runs.
  const [blankSuggestion, setBlankSuggestion] = useState(null)
  const [blankOptionalFields, setBlankOptionalFields] = useState([])
  const [blankProgress, setBlankProgress] = useState(null)
  const [blankApplyingAnswers, setBlankApplyingAnswers] = useState(false)
  const [blankProcessedSignature, setBlankProcessedSignature] = useState('')
  const [blankFieldsDirty, setBlankFieldsDirty] = useState(false)
  const [blankFieldsPreview, setBlankFieldsPreview] = useState([])
  const [blankFieldsDict, setBlankFieldsDict] = useState({})
  const [blankCheckedFields, setBlankCheckedFields] = useState({})
  const [blankFieldSearch, setBlankFieldSearch] = useState('')
  const [blankPreviewSource, setBlankPreviewSource] = useState('')
  const [blankEditableFields, setBlankEditableFields] = useState([])
  const [blankSelectedFieldKeys, setBlankSelectedFieldKeys] = useState(new Set())
  const [blankFieldModalOpen, setBlankFieldModalOpen] = useState(false)
  const [blankFieldModalMode, setBlankFieldModalMode] = useState('add') // add | edit
  const [blankFieldOriginalKey, setBlankFieldOriginalKey] = useState('')
  const [blankFieldDraft, setBlankFieldDraft] = useState({
    label: '',
    key: '',
    type: 'text',
    description: '',
    required: false,
    user_input: false,
    display: true,
    section: 'None',
  })
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
  const [confirmDeleteTitle, setConfirmDeleteTitle] = useState('')
  const [confirmDeleteMessage, setConfirmDeleteMessage] = useState('')
  const [unsavedFieldsPromptOpen, setUnsavedFieldsPromptOpen] = useState(false)
  const [pendingStepTarget, setPendingStepTarget] = useState(null)
  const [pendingDeleteRecords, setPendingDeleteRecords] = useState([])
  const toast = useToast()

  const selectedSourceTemplateSummary = sourceTemplateId
    ? (existingTemplates.find(t => String(t.id) === String(sourceTemplateId)) || null)
    : null
  const [sourceTemplateDetail, setSourceTemplateDetail] = useState(null)
  const [loadingSourceTemplateFields, setLoadingSourceTemplateFields] = useState(false)
  const selectedSourceTemplate = useMemo(() => {
    if (!selectedSourceTemplateSummary) return null
    const detail = sourceTemplateDetail && String(sourceTemplateDetail.id) === String(sourceTemplateId)
      ? sourceTemplateDetail
      : null
    return detail ? { ...selectedSourceTemplateSummary, ...detail } : selectedSourceTemplateSummary
  }, [selectedSourceTemplateSummary, sourceTemplateDetail, sourceTemplateId])
  const selectedSourceHasAnalyzer = isAnalyzerReady(selectedSourceTemplate)

  // Templates list no longer carries full field data (see loadTemplates), so
  // fetch the one selected source template's fields on demand instead of
  // requiring every template in the org to be fetched with fields included.
  useEffect(() => {
    if (!sourceTemplateId) {
      setSourceTemplateDetail(null)
      setLoadingSourceTemplateFields(false)
      return
    }
    let cancelled = false
    setLoadingSourceTemplateFields(true)
    templatesApi.get(sourceTemplateId)
      .then((res) => {
        if (!cancelled) setSourceTemplateDetail(res.data)
      })
      .catch(() => {
        if (!cancelled) setSourceTemplateDetail(null)
      })
      .finally(() => {
        if (!cancelled) setLoadingSourceTemplateFields(false)
      })
    return () => { cancelled = true }
  }, [sourceTemplateId])

  // Reset all state when modal opens/closes
  useEffect(() => {
    if (open) {
      setWizardStep(1)
      setName('')
      setDescription('')
      setAllowReprocessing(true)
      setSourceTemplateId('')
      setReuseSourceAnalyzer(true)
      setSourceTemplateSearch('')
      setSourceTemplateDropdownOpen(false)
      setSampleInputMode('ai_sample_docs')
      setSampleDocuments([])
      setJsonImportFile(null)
      setAccessTab('users')
      setUserRoleMap({})
      setSelectedAccessGroupIds(new Set())
      setConnectorMode('create_webhook')
      setConnectorSearch('')
      setConnectorName('')
      setConnectorUrl('')
      setConnectorEvents('request.approved')
      setConnectorUpdateMode('append')
      setConnectorSecret('')
      setConnectorShowSecret(false)
      setConnectorBasicAuthUsername('')
      setConnectorBasicAuthPassword('')
      setConnectorShowBasicAuthPassword(false)
      setAvailableWebhooks([])
      setLoadingWebhooks(false)
      setSelectedConnectorWebhookId('')
      setConnectorError('')
      setCreationMode('blank')
      setBcStatus(null)
      setBcLoading(false)
      setBcTesting(false)
      setBcLoadError('')
      setVerificationPrompt('')
      setBcEntityTypes([])
      setSelectedBcEntityType('')
      setBcConnections([])
      setSelectedBcConnectionId('')
      setNsEntityTypes([])
      setSelectedNsEntityType('purchase_order')
      setNsTypesReady(false)
      setNsTypesError('')
      setBcDetecting(false)
      setBcDetectResult(null)
      setBcCheckedFields({})
      setBcUploading(false)
      setBcUploadError('')
      setBcMappingResult(null)
      setBcEditableFields([])
      setBcEditingRowKey('')
      setBcFieldSearch('')
      setNsStatus(null)
      setNsLoading(false)
      setNsLoadError('')
      setNsScope(null)
      setNsCompanyId('')
      setNsEnvironmentId('')
      setNsDetecting(false)
      setNsDetectResult(null)
      setNsCheckedFields({})
      setNsFieldSearch('')
      setNsInputMode('checklist')
      setNsUploading(false)
      setNsUploadError('')
      setNsMappingResult(null)
      setNsFieldSourceMode('upload')
      setNsShowReview(false)
      setNsFinalFields(null)
      setNsEditableFields([])
      setNsSelectedFieldKeys(new Set())
      setNsEditingRowKey('')
      setBlankDetecting(false)
      setBlankDetectError('')
      setBlankGuidancePrompt('')
      setBlankRestrict(false)
      setBlankDetailLevel('standard')
      setBlankSuggestion(null)
      setBlankOptionalFields([])
      setBlankProgress(null)
      setBlankApplyingAnswers(false)
      setBlankProcessedSignature('')
      setBlankFieldsDirty(false)
      setBlankFieldsPreview([])
      setBlankFieldsDict({})
      setBlankCheckedFields({})
      setBlankFieldSearch('')
      setBlankPreviewSource('')
      setBlankEditableFields([])
      setBlankSelectedFieldKeys(new Set())
      setBlankFieldModalOpen(false)
      setBlankFieldModalMode('add')
      setBlankFieldOriginalKey('')
      setBlankFieldDraft({
        label: '',
        key: '',
        type: 'text',
        description: '',
        required: false,
        user_input: false,
        display: true,
        section: 'None',
      })
      setConfirmDeleteOpen(false)
      setConfirmDeleteTitle('')
      setConfirmDeleteMessage('')
      setPendingDeleteRecords([])
      setUnsavedFieldsPromptOpen(false)
      setPendingStepTarget(null)
      setUserRoleMap({})
      setPendingRoleMap({})
      setAccessUserSearch('')
      setAccessGroupSearch('')
      setAccessUserDropdownOpen(false)
      setAccessGroupDropdownOpen(false)
      setAccessSearchRoleMap({})
      setJustAddedUserId(null)
      setJustAddedGroupId(null)
      setApproverId('')
      setUsersLoading(true)
      adminApi.listUsers()
        .then(res => setOrgUsers(res.data?.users || []))
        .catch(() => {})
        .finally(() => setUsersLoading(false))

      groupsApi.list()
        .then(res => setOrgGroups(res.data || []))
        .catch(() => setOrgGroups([]))
    }
  }, [open])

  useEffect(() => {
    if (sourceTemplateId && !selectedSourceHasAnalyzer) {
      setReuseSourceAnalyzer(false)
    }
  }, [sourceTemplateId, selectedSourceHasAnalyzer])

  // When a source template is selected for "Copy existing template", load its
  // fields into the same editable-fields state used by the "Start from blank"
  // flow so users can add/edit/delete/reorder before the new template is created.
  useEffect(() => {
    if (creationMode !== 'from_template') return
    if (!sourceTemplateId || !selectedSourceTemplate) {
      setBlankEditableFields([])
      setBlankFieldsDict({})
      setBlankCheckedFields({})
      setBlankSelectedFieldKeys(new Set())
      setBlankFieldsDirty(false)
      return
    }
    const sourceFields = selectedSourceTemplate.fields || []
    const editable = sourceFields.map((f, idx) => ({
      id: `${f.field_name || f.id || idx}-${idx}`,
      key: f.field_name || f.id || `field_${idx}`,
      label: f.display_name || f.field_name || `Field ${idx + 1}`,
      type: f.field_type || 'text',
      description: f.description || '',
      required: !!f.is_required,
      user_input: !!f.user_input,
      display: true,
      section: f.section || 'None',
    }))
    setBlankEditableFields(editable)
    const dict = {}
    editable.forEach((f) => {
      dict[f.key] = {
        type: f.type,
        description: f.description,
        label: f.label,
        required: f.required,
        user_input: f.user_input,
      }
    })
    setBlankFieldsDict(dict)
    const checked = {}
    editable.forEach((f) => { checked[f.key] = true })
    setBlankCheckedFields(checked)
    setBlankSelectedFieldKeys(new Set())
    setBlankFieldsDirty(false)
  }, [creationMode, sourceTemplateId, selectedSourceTemplate])

  useEffect(() => {
    if (!open || !isAdmin || !features.connectors) return
    let cancelled = false

    setLoadingWebhooks(true)
    setConnectorError('')
    api.get('/webhooks')
      .then((res) => {
        if (cancelled) return
        setAvailableWebhooks(res.data?.webhooks || [])
      })
      .catch((err) => {
        if (cancelled) return
        setAvailableWebhooks([])
        setConnectorError(err?.response?.data?.error || 'Could not load connectors.')
      })
      .finally(() => {
        if (!cancelled) setLoadingWebhooks(false)
      })

    return () => {
      cancelled = true
    }
  }, [open, isAdmin])

  // When BC mode is selected, probe connection then detect fields.
  // Also loads the entity-type catalog and every registered BC connection so
  // the wizard can offer the same Document Type / Connection pickers as
  // TemplateBCSettings.jsx (post-creation settings) — the two purposely stay
  // in sync so switching a template's entity type/connection later behaves
  // exactly like picking it here.
  useEffect(() => {
    if (creationMode !== 'bc_purchase_order') return
    let cancelled = false
    setBcConnsLoaded(false)

    Promise.all([bcApi.getEntityTypes(), bcApi.getConnections()])
      .then(([typesRes, connsRes]) => {
        if (cancelled) return
        const types = typesRes.data?.entity_types || []
        const conns = (connsRes.data?.connections || []).filter(c => c.is_active !== false)
        setBcEntityTypes(types)
        setBcConnections(conns)
        setSelectedBcEntityType(prev => prev || types.find(t => t.bc_entity_type_key === 'purchase_order')?.bc_entity_type_key || types[0]?.bc_entity_type_key || '')
        setSelectedBcConnectionId(prev => prev || (conns.find(c => c.is_default)?.bc_connection_id ?? conns[0]?.bc_connection_id ?? ''))
      })
      .catch(() => { /* non-fatal — dropdowns just show empty/defaults */ })
      .finally(() => { if (!cancelled) setBcConnsLoaded(true) })

    return () => { cancelled = true }
  }, [creationMode])

  // Prefetch NetSuite record types the moment the modal opens — same reason
  // as the BC effect above, and deliberately NOT gated on creationMode: the
  // picker is then already populated when someone selects "Create from
  // NetSuite", rather than appearing after a spinner.
  useEffect(() => {
    if (!open || !features.netsuite) return
    let cancelled = false
    setNsTypesError('')

    nsApi.getEntityTypes()
      .then(res => {
        if (cancelled) return
        const types = res.data?.entity_types || []
        setNsEntityTypes(types)
        if (types.length > 0) {
          setSelectedNsEntityType(prev =>
            types.find(t => t.ns_entity_type_key === prev) ? prev : types[0].ns_entity_type_key
          )
        }
        setNsTypesReady(true)
        return types
      })
      .catch(err => {
        if (cancelled) return
        setNsTypesError(err?.response?.data?.error || 'Could not load NetSuite record types.')
        setNsTypesReady(true) // Ready = the picker shows its error state instead of hanging.
      })

    return () => { cancelled = true }
  }, [open, features.netsuite])

  // Same fetch as above, callable again once NsRecordTypePicker registers a
  // record type that had never been used here before, so it becomes the
  // selected type without a full modal reopen. Returning the refreshed list
  // is part of the contract — the picker refuses to report success unless
  // the new type actually appears in it.
  const refreshNsEntityTypesAndSelect = useCallback((keyToSelect) => {
    return nsApi.getEntityTypes()
      .then(res => {
        const types = res.data?.entity_types || []
        setNsEntityTypes(types)
        if (keyToSelect && types.find(t => t.ns_entity_type_key === keyToSelect)) {
          setSelectedNsEntityType(keyToSelect)
        }
        return types
      })
      .catch(() => {})
  }, [])

  // NsRecordTypePicker calls this after it has registered a record type that
  // had never been used here before. The picker owns the create call and its
  // own error states; this only refreshes the list, selects the new type and
  // confirms, returning the refreshed list so the picker can verify it
  // actually landed before claiming success.
  const handleNsRecordTypeRegistered = useCallback((key) => {
    return refreshNsEntityTypesAndSelect(key).then(types => {
      const landed = (types || []).find(t => t.ns_entity_type_key === key)
      if (landed) toast.success(`${landed.display_name} is ready to use.`)
      return types
    })
  }, [refreshNsEntityTypesAndSelect, toast])

  // Check the connection the user picked (or the environment's, when the org has no
  // registered connections), then detect fields on THAT connection.
  //
  // One probe, driven only by the selected connection. It used to be two effects, the
  // environment check and a connection test, that both wrote the same status, so a
  // database-only org saw "not connected" whenever the environment check finished last.
  // A 403 (a template manager may not run the live test) means "not verified", not
  // "not connected", so it must not block the upload. A real failure shows its reason.
  useEffect(() => {
    if (!open || creationMode !== 'bc_purchase_order' || !bcConnsLoaded) return
    let cancelled = false
    const conn = bcConnections.find(c => String(c.bc_connection_id) === String(selectedBcConnectionId))
    setBcLoading(true)
    setBcLoadError('')
    setBcStatus(null)
    setBcDetecting(false)
    setBcDetectResult(null)
    setBcCheckedFields({})

    const probe = selectedBcConnectionId
      ? bcApi.testConnectionById(selectedBcConnectionId)
          .then(res => {
            const r = res.data || {}
            return r.success
              ? { connected: true, company: r.company?.name || conn?.company_name || '', environment: conn?.bc_environment_name || '' }
              : { connected: false, error: r.message || 'The connection test failed.' }
          })
          .catch(err => (
            err?.response?.status === 403
              ? { connected: true, unverified: true, company: conn?.company_name || '', environment: conn?.bc_environment_name || '' }
              : { connected: false, error: err?.response?.data?.message || err?.response?.data?.error || 'Could not test the Business Central connection.' }
          ))
      : bcApi.getConnectionInfo()
          .then(res => {
            const c = res.data || {}
            return {
              connected: Boolean(c.connected),
              company: c.company || c.company_name || '',
              environment: c.environment || '',
            }
          })
          .catch(err => ({ connected: false, error: err?.response?.data?.error || 'Could not check Business Central.' }))

    probe.then(status => {
      if (cancelled) return
      setBcStatus(status)
      if (!status.connected && status.error) setBcLoadError(status.error)
      setBcLoading(false)
      if (!status.connected) return undefined

      setBcDetecting(true)
      return bcApi.detectFields(undefined, selectedBcConnectionId || undefined)
        .then(detectRes => {
          if (cancelled) return
          const result = detectRes.data || {}
          setBcDetectResult(result)
          // Pre-check detected fields + always check mandatory fields
          const MANDATORY = new Set(['vendorNumber', 'orderDate'])
          const initial = {}
          ;(result.available_fields || []).forEach(f => {
            const isMandatory = MANDATORY.has(f.key)
            const isDetected = (result.detected_fields || []).includes(f.key)
            initial[f.key] = isMandatory || isDetected
          })
          setBcCheckedFields(initial)
        })
        .catch(err => {
          if (cancelled) return
          setBcDetectResult({ error: err?.response?.data?.error || 'Field detection failed.' })
        })
        .finally(() => { if (!cancelled) setBcDetecting(false) })
    })

    return () => { cancelled = true }
    // bcConnections is read for the connection's labels only; the check re-runs on the
    // picked connection, not on every list refresh.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, creationMode, selectedBcConnectionId, bcConnsLoaded])

  // Label for the BC document type currently selected, so the dialog's copy and
  // submit button follow the choice instead of always reading "Purchase Order".
  // Falls back to a neutral word rather than a wrong document type.
  const selectedBcEntityLabel =
    bcEntityTypes.find(t => t.bc_entity_type_key === selectedBcEntityType)?.display_name || 'Document'

  // Same for NetSuite, so the dialog's copy and submit button follow the chosen
  // document type instead of always reading "Purchase Order".
  const selectedNsEntityLabel =
    nsEntityTypes.find(t => t.ns_entity_type_key === selectedNsEntityType)?.display_name || 'Document'

  // When NS mode is selected, probe connection then detect fields
  // Phase 1, automatic and cheap: verify the credentials and populate the
  // Environment/Company pickers. Deliberately does NOT fetch fields/validations
  // here — that used to run unconditionally the instant NetSuite mode was
  // selected, before the user had picked a company, so the "fetched" data was
  // never actually scoped to the account it was fetched for. Field/validation
  // fetching is now the explicit fetchNsData() action below, fired from its
  // own step once a company is chosen.
  useEffect(() => {
    if (creationMode !== 'ns_purchase_order') return
    let cancelled = false
    setNsLoading(true)
    setNsLoadError('')
    setNsStatus(null)
    setNsDetecting(false)
    setNsDetectResult(null)
    setNsCheckedFields({})

    // One call for the whole step: verifies the credentials AND returns the
    // selectable companies and environment, so the dialog never has to make a
    // second round trip to populate its dropdowns.
    nsApi.getConnectionScope()
      .then(res => {
        if (cancelled) return
        const scope = res.data || {}
        setNsScope(scope)
        const status = {
          connected: Boolean(scope.connected),
          account_id: scope.account_id || '',
          environment: scope.environment || '',
        }
        setNsStatus(status)
        if (scope.error) setNsLoadError(scope.error)

        // Preselect the only environment there is, and the only company when
        // there is exactly one — a forced choice with a single option is noise.
        const envs = scope.environments || []
        setNsEnvironmentId(
          (envs.find(e => e.is_default) || envs[0] || {}).id || '',
        )
        const companies = scope.companies || []
        setNsCompanyId(companies.length === 1 ? String(companies[0].id) : '')
        setNsLoading(false)

        if (status.connected) {
          setNsDetecting(true)
          return nsApi.detectFields()
            .then(detectRes => {
              if (cancelled) return
              const result = detectRes.data || {}
              setNsDetectResult(result)
              const MANDATORY = new Set(['entity', 'tranDate', 'subsidiary'])
              const initial = {}
              ;(result.available_fields || []).forEach(f => {
                const isMandatory = MANDATORY.has(f.key)
                const isDetected = (result.detected_fields || []).includes(f.key)
                initial[f.key] = isMandatory || isDetected
              })
              setNsCheckedFields(initial)
            })
            .catch(err => {
              if (cancelled) return
              setNsDetectResult({ error: err?.response?.data?.error || 'Field detection failed.' })
            })
            .finally(() => { if (!cancelled) setNsDetecting(false) })
        }
      })
      .catch(err => {
        if (cancelled) return
        setNsLoadError(err?.response?.data?.error || 'Could not check NetSuite status.')
        setNsStatus({ connected: false, account_id: '', environment: '' })
        setNsScope(null)
        setNsLoading(false)
      })

    return () => { cancelled = true }
  }, [creationMode])

  // Phase 2, explicit: fetch this account's fields + validations, scoped to
  // the chosen company/subsidiary and document type. Triggered by the "Fetch
  // data & validations" button in the NetSuite setup step — never
  // automatically, so a multi-subsidiary account's detection genuinely
  // reflects the company the user picked rather than whatever happened to be
  // selected (or nothing) the instant NetSuite mode was chosen.
  const fetchNsData = () => {
    if (!nsStatus?.connected || nsDetecting) return
    setNsDetecting(true)
    setNsDetectResult(null)
    setNsCheckedFields({})
    // ns_entity_type must be explicit: the backend defaults to
    // 'purchase_order' when it's omitted, which would silently detect PO
    // fields (entity/tranDate/subsidiary) for a Vendor Bid template — the
    // same class of bug fixed elsewhere in this NS integration.
    nsApi.detectFields({ ns_entity_type: selectedNsEntityType, subsidiary_id: nsCompanyId || undefined })
      .then(detectRes => {
        const result = detectRes.data || {}
        setNsDetectResult(result)
        // mandatory_fields comes back from the same detection call, already
        // scoped to the chosen entity type (PO's entity/tranDate/subsidiary
        // vs Bid's own header+line keys) — reading it here instead of
        // hardcoding PO's keys is what makes this correct for a Vendor Bid
        // template too.
        const MANDATORY = new Set(result.mandatory_fields || [])
        const initial = {}
        ;(result.available_fields || []).forEach(f => {
          const isMandatory = MANDATORY.has(f.key)
          const isDetected = (result.detected_fields || []).includes(f.key)
          initial[f.key] = isMandatory || isDetected
        })
        setNsCheckedFields(initial)
      })
      .catch(err => {
        setNsDetectResult({ error: err?.response?.data?.error || 'Field detection failed.' })
      })
      .finally(() => setNsDetecting(false))
  }

  const clearBlankPreview = (errorMessage = '') => {
    setBlankFieldsPreview([])
    setBlankFieldsDict({})
    setBlankCheckedFields({})
    setBlankPreviewSource('')
    setBlankEditableFields([])
    setBlankSelectedFieldKeys(new Set())
    setBlankFieldModalOpen(false)
    setBlankFieldOriginalKey('')
    setBlankDetectError(errorMessage)
  }

  const setBlankPreviewFields = (fields, sourceLabel) => {
    setBlankFieldsPreview(fields)
    const dict = {}
    fields.forEach((f) => {
      dict[f.key] = f.raw && typeof f.raw === 'object' ? f.raw : {
        type: f.type || 'text',
        description: f.description || '',
      }
    })
    setBlankFieldsDict(dict)
    setBlankPreviewSource(sourceLabel)
    const initial = {}
    fields.forEach((f) => { initial[f.key] = true })
    setBlankCheckedFields(initial)
    setBlankEditableFields(fields.map((f, idx) => ({
      id: `${f.key}-${idx}`,
      key: f.key,
      label: f.label || f.key,
      type: f.type || 'text',
      description: f.description || '',
      required: false,
      user_input: false,
      display: true,
      section: f.section || 'None',
      columns: f.raw?.columns,
    })))
    setBlankSelectedFieldKeys(new Set())
    setBlankFieldModalOpen(false)
    setBlankFieldOriginalKey('')
    setBlankDetectError('')
    setBlankFieldsDirty(false)
  }

  const toPreviewFromFieldsDict = (fieldsDict) => {
    return Object.entries(fieldsDict || {}).map(([key, val]) => {
      const field = val && typeof val === 'object' ? val : {}
      return {
        key,
        label: field.label || key,
        type: field.type || field.field_type || 'text',
        description: field.description || '',
        raw: field,
      }
    })
  }

  const toFieldsDictFromJson = (jsonData) => {
    if (!jsonData) return {}

    if (
      jsonData.fieldSchema?.fields &&
      typeof jsonData.fieldSchema.fields === 'object' &&
      !Array.isArray(jsonData.fieldSchema.fields)
    ) {
      return jsonData.fieldSchema.fields
    }

    if (jsonData.fields && typeof jsonData.fields === 'object' && !Array.isArray(jsonData.fields)) {
      return jsonData.fields
    }

    if (
      typeof jsonData === 'object' &&
      !Array.isArray(jsonData) &&
      !jsonData.fieldSchema
    ) {
      const firstVal = Object.values(jsonData)[0]
      if (
        firstVal &&
        typeof firstVal === 'object' &&
        ('type' in firstVal || 'description' in firstVal)
      ) {
        return jsonData
      }
    }

    return {}
  }

  const getBlankProcessSignature = () => {
    if (sampleInputMode === 'json_fields_import') {
      if (!jsonImportFile) return 'json:none'
      return `json:${jsonImportFile.name}:${jsonImportFile.size}:${jsonImportFile.lastModified}`
    }

    const docsSig = sampleDocuments
      .map((doc) => `${doc.name}:${doc.size}:${doc.lastModified}`)
      .join('|')
    return `ai:${docsSig}:${blankGuidancePrompt.trim()}:${blankRestrict}:${blankDetailLevel}`
  }

  const suggestionToPreview = (item) => {
    const isTable = item.field_type === 'array_of_objects'
    const columns = isTable ? selectedColumns(item) : undefined
    return {
      key: item.field_name,
      label: item.display_name || item.field_name,
      type: item.field_type || 'text',
      description: item.description || '',
      section: item.category || 'None',
      raw: {
        label: item.display_name || item.field_name,
        type: item.field_type || 'text',
        description: item.description || '',
        category: item.category || undefined,
        ...(item.normalisation_instruction ? { normalisation_instruction: item.normalisation_instruction } : {}),
        ...(isTable ? { columns } : {}),
      },
    }
  }

  const applyBlankSuggestion = (result, signature, docCount) => {
    const items = (result.fields || []).filter((f) => f.placement?.action !== 'exists')
    if (!items.length) {
      clearBlankPreview('No extractable fields were found in the uploaded documents.')
      toast.error('Field suggestion failed', 'No extractable fields were found in the uploaded documents.')
      return
    }
    const main = items.filter((f) => !isOptionalSuggestion(f)).map(suggestionToPreview)
    const optional = items.filter(isOptionalSuggestion).map(suggestionToPreview)
    setBlankSuggestion(result)
    setBlankOptionalFields(optional)
    setBlankPreviewFields(main, `AI suggestion from ${docCount} sample document${docCount !== 1 ? 's' : ''}`)
    setBlankProcessedSignature(signature)
    const tables = main.filter((f) => f.type === 'array_of_objects').length
    toast.success(
      'Fields suggested',
      `${main.length} field${main.length !== 1 ? 's' : ''}${tables ? `, including ${tables} table${tables !== 1 ? 's' : ''}` : ''}, suggested from ${docCount} document${docCount !== 1 ? 's' : ''}.`,
    )
  }

  // Re-run with the answers to the AI's questions. The field list is
  // rebuilt from the new suggestion, so edits made to it are replaced.
  const applyBlankAnswers = async (answers) => {
    if (!blankSuggestion?.job_id) return
    setBlankApplyingAnswers(true)
    try {
      const res = await templatesApi.answerSuggestionQuestions(blankSuggestion.job_id, answers)
      const result = await waitForSuggestionJob(res.data.job_id, { onProgress: setBlankProgress })
      applyBlankSuggestion(result, getBlankProcessSignature(), sampleDocuments.length)
    } catch (e) {
      toast.error('Could not update the suggestions', e?.response?.data?.error || e?.message || 'Please try again.')
    } finally {
      setBlankApplyingAnswers(false)
      setBlankProgress(null)
    }
  }

  const addBlankOptionalFields = () => {
    if (!blankOptionalFields.length) return
    const taken = new Set(blankEditableFields.map((f) => f.key))
    const extra = blankOptionalFields.filter((f) => !taken.has(f.key))
    setBlankEditableFields((prev) => [
      ...prev,
      ...extra.map((f, idx) => ({
        id: `${f.key}-opt-${idx}`,
        key: f.key,
        label: f.label,
        type: f.type,
        description: f.description,
        required: false,
        user_input: false,
        display: true,
        section: f.section || 'None',
        columns: f.raw?.columns,
      })),
    ])
    setBlankFieldsDict((prev) => ({ ...prev, ...Object.fromEntries(extra.map((f) => [f.key, f.raw])) }))
    setBlankOptionalFields([])
    setBlankFieldsDirty(true)
  }

  const processBlankSampleInput = async () => {
    if (creationMode !== 'blank') return

    const signature = getBlankProcessSignature()
    setBlankDetecting(true)
    setBlankDetectError('')

    try {
      if (sampleInputMode === 'json_fields_import') {
        if (!jsonImportFile) {
          clearBlankPreview('Upload a JSON file to generate fields.')
          return
        }

        const text = await jsonImportFile.text()
        const parsed = JSON.parse(text)
        const fieldsDict = toFieldsDictFromJson(parsed)
        const preview = toPreviewFromFieldsDict(fieldsDict)

        if (!preview.length) {
          clearBlankPreview('No field definitions found in the JSON file.')
          toast.warning('No fields found', 'The JSON file did not contain extractable field definitions.')
          return
        }

        setBlankPreviewFields(preview, `JSON import: ${jsonImportFile.name}`)
        setBlankProcessedSignature(signature)
        toast.success('Fields generated', `${preview.length} field${preview.length !== 1 ? 's' : ''} loaded from JSON.`)
        return
      }

      if (!sampleDocuments.length) {
        clearBlankPreview('Upload sample documents to suggest fields.')
        return
      }

      const docs = sampleDocuments.filter((doc) => {
        const name = (doc?.name || '').toLowerCase()
        return doc?.type === 'application/pdf' || name.endsWith('.pdf')
      })

      if (!docs.length) {
        clearBlankPreview('AI extraction currently supports PDF files only. Upload at least one PDF sample.')
        toast.warning('PDF required', 'Please upload at least one PDF file for AI field suggestion.')
        return
      }

      // One background job designs the whole template from all the samples
      // (src/services/schema_designer.py): header fields, the product lines
      // as one table, typed and described.
      const res = await templatesApi.suggestFieldsDraft(docs, blankGuidancePrompt.trim(), {
        restrict: blankRestrict,
        detailLevel: blankDetailLevel,
        templateName: name.trim(),
      })
      const result = await waitForSuggestionJob(res.data.job_id, { onProgress: setBlankProgress })
      applyBlankSuggestion(result, signature, docs.length)
    } catch (e) {
      const details = e?.response?.data?.error || e?.message
      const suffix = details ? ` (${details})` : ''
      clearBlankPreview(`Could not process the provided sample input.${suffix}`)
      toast.error('Processing failed', details || 'Could not process sample input.')
    } finally {
      setBlankDetecting(false)
      setBlankProgress(null)
    }
  }

  const bcReady = bcStatus?.connected

  const WIZARD_STEPS = [
    {
      key: 'template_details',
      label: 'Template details',
      title: 'Define your template',
      description: 'Name this template and choose how you want to build its extraction fields.',
      icon: FileStack,
    },
    {
      key: 'ns_setup',
      label: 'NetSuite setup',
      title: `Set up ${selectedNsEntityLabel}`,
      description: 'Choose the company this template targets, then fetch its fields and validation rules from NetSuite.',
      icon: Globe,
    },
    {
      key: 'sample_documents',
      label: creationMode === 'ns_purchase_order' ? `Upload ${selectedNsEntityLabel} PDF` : creationMode === 'bc_purchase_order' ? 'Upload Business Central PDF' : 'Sample documents',
      title: creationMode === 'ns_purchase_order' ? `Upload NetSuite ${selectedNsEntityLabel} PDF` : creationMode === 'bc_purchase_order' ? 'Upload Business Central Purchase Order PDF' : 'Sample documents',
      description: creationMode === 'ns_purchase_order'
        ? `Upload a sample NetSuite ${selectedNsEntityLabel} PDF so AI can map fields to NetSuite keys automatically (required).`
        : creationMode === 'bc_purchase_order'
        ? 'Upload a sample Business Central Purchase Order PDF so AI can map fields to BC keys automatically (required).'
        : 'Upload representative documents or import JSON field definitions to bootstrap extraction fields.',
      icon: UploadCloud,
    },
    {
      key: 'configure_fields',
      label: 'Configure fields',
      title: 'Configure fields',
      description: 'Review and refine extracted fields in a structured view before continuing.',
      icon: Sparkles,
    },
    {
      key: 'access_ownership',
      label: 'Access & ownership',
      title: 'Assign access and ownership',
      description: 'Choose who can use this template and assign an approver for submitted requests.',
      icon: UsersRound,
    },
    {
      key: 'add_connectors',
      label: 'Add connectors',
      title: 'Add connectors',
      description: 'Connect an output destination now, or finish the template and configure it later.',
      icon: Link2,
    },
    {
      key: 'review_create',
      label: 'Review & create',
      title: 'Review and create',
      description: 'Confirm the workflow summary before creating your extraction template.',
      icon: CheckCircle2,
    },
  ]
  // The connectors wizard step is skipped for BC/NS/Boomi templates, and
  // entirely absent on deployments where connectors aren't switched on.
  const shouldSkipConnectors = !features.connectors || creationMode === 'bc_purchase_order' || creationMode === 'ns_purchase_order' || creationMode === 'boomi_purchase_order'
  const shouldSkipSampleDocuments = creationMode === 'from_template'
  const shouldSkipNsSetup = creationMode !== 'ns_purchase_order'
  const activeWizardSteps = isAdmin
    ? WIZARD_STEPS.filter((step) => (
        (shouldSkipConnectors ? step.key !== 'add_connectors' : true) &&
        (shouldSkipSampleDocuments ? step.key !== 'sample_documents' : true) &&
        (shouldSkipNsSetup ? step.key !== 'ns_setup' : true)
      ))
    : [WIZARD_STEPS[0]]
  const totalSteps = activeWizardSteps.length
  const isLastStep = wizardStep >= totalSteps
  const currentStepMeta = activeWizardSteps[Math.max(0, wizardStep - 1)]
  const currentStepKey = currentStepMeta?.key
  const CREATION_METHOD_ICONS = {
    blank: FileStack,
    from_template: Copy,
    connectors: Link2,
    netsuite: Globe,
    business_central: Building2,
    boomi: Layers,
  }
  // Top-level creation method options. NetSuite / Business Central are grouped
  // under a single "Connectors" entry with an inline sub-picker (rather than
  // being separate top-level cards) so the list stays to exactly 3 rows:
  // Start from blank / Copy existing template / Connectors.
  const connectorSubOptions = [
    {
      key: 'netsuite',
      title: 'NetSuite',
      description: 'Use the configured NetSuite document workflow.',
      enabled: !!features.netsuite,
      mapsTo: 'ns_purchase_order',
    },
    {
      key: 'business_central',
      title: 'Business Central',
      description: 'Use Business Central mapped fields.',
      enabled: !!features.business_central,
      mapsTo: 'bc_purchase_order',
    },
  ].filter((option) => option.enabled)

  const creationMethodOptions = [
    {
      key: 'blank',
      title: 'Start from blank',
      description: 'Generate fields from your sample documents.',
      enabled: true,
    },
    {
      key: 'from_template',
      title: 'Copy existing template',
      description: 'Reuse a template structure and refine it.',
      enabled: true,
    },
    {
      key: 'connectors',
      title: 'Connectors',
      description: 'Use NetSuite or Business Central mapped fields.',
      enabled: connectorSubOptions.length > 0,
    },
  ].filter((option) => option.enabled)

  const isConnectorMode = creationMode === 'ns_purchase_order' || creationMode === 'bc_purchase_order'
  const connectorModeToSubKey = { ns_purchase_order: 'netsuite', bc_purchase_order: 'business_central' }

  const canContinueStep = () => {
    if (!isAdmin) return !!name.trim()
    if (wizardStep === 1) return !!name.trim()
    if (currentStepKey === 'ns_setup') {
      return !!nsStatus?.connected && !!nsCompanyId && !!nsDetectResult && !nsDetectResult?.error
    }
    if (currentStepKey === 'sample_documents' && creationMode === 'blank') {
      const signatureMatches = blankProcessedSignature && blankProcessedSignature === getBlankProcessSignature()
      if (sampleInputMode === 'json_fields_import') return !!jsonImportFile && signatureMatches && blankEditableFields.length > 0
      return sampleDocuments.length > 0 && signatureMatches && blankEditableFields.length > 0
    }
    if (currentStepKey === 'sample_documents' && creationMode === 'ns_purchase_order') {
      return nsEditableFields.length > 0
    }
    if (currentStepKey === 'configure_fields' && creationMode === 'ns_purchase_order') {
      return nsEditableFields.some((f) => nsSelectedFieldKeys.has(f.key))
    }
    if (currentStepKey === 'sample_documents' && creationMode === 'bc_purchase_order') {
      return bcEditableFields.length > 0
    }
    if (currentStepKey === 'configure_fields' && creationMode === 'from_template') return !!sourceTemplateId
    if (currentStepKey === 'add_connectors') {
      if (connectorMode === 'select_existing') return !!selectedConnectorWebhookId
      if (connectorMode === 'create_webhook') {
        const url = connectorUrl.trim()
        if (!connectorName.trim() || !url || !url.startsWith('https://')) return false
        if (!connectorEvents.trim()) return false
        // Basic auth username/password must be provided together, mirrors backend validation
        if (!!connectorBasicAuthUsername.trim() !== !!connectorBasicAuthPassword.trim()) return false
        try {
          // Validate URL format before moving to review step.
          new URL(url)
        } catch {
          return false
        }
      }
    }
    return true
  }

  const goNextStep = () => {
    if (!canContinueStep()) return
    const target = Math.min(totalSteps, wizardStep + 1)
    if (currentStepKey === 'configure_fields' && blankFieldsDirty) {
      setPendingStepTarget(target)
      setUnsavedFieldsPromptOpen(true)
      return
    }
    setWizardStep(target)
  }

  const requestStepChange = (target) => {
    if (currentStepKey === 'configure_fields' && blankFieldsDirty && target !== wizardStep) {
      setPendingStepTarget(target)
      setUnsavedFieldsPromptOpen(true)
      return
    }
    setWizardStep(target)
  }

  const discardFieldChangesAndContinue = () => {
    setUnsavedFieldsPromptOpen(false)
    setBlankFieldsDirty(false)
    if (pendingStepTarget != null) setWizardStep(pendingStepTarget)
    setPendingStepTarget(null)
  }

  const saveFieldChangesAndContinue = () => {
    setUnsavedFieldsPromptOpen(false)
    saveBlankFieldChanges()
    if (pendingStepTarget != null) setWizardStep(pendingStepTarget)
    setPendingStepTarget(null)
  }

  const goPrevStep = () => {
    setWizardStep((prev) => Math.max(1, prev - 1))
  }

  useEffect(() => {
    setWizardStep((prev) => Math.min(prev, totalSteps))
  }, [totalSteps])

  // Close the user/group search dropdowns only when a click happens truly
  // outside the picker (not on the role <select> or Add button inside it),
  // so choosing a role from the dropdown no longer collapses it prematurely.
  useEffect(() => {
    const handlePointerDown = (event) => {
      if (accessUserPickerRef.current && !accessUserPickerRef.current.contains(event.target)) {
        setAccessUserDropdownOpen(false)
      }
      if (accessGroupPickerRef.current && !accessGroupPickerRef.current.contains(event.target)) {
        setAccessGroupDropdownOpen(false)
      }
      if (sourceTemplatePickerRef.current && !sourceTemplatePickerRef.current.contains(event.target)) {
        setSourceTemplateDropdownOpen(false)
      }
      if (connectorEventsPickerRef.current && !connectorEventsPickerRef.current.contains(event.target)) {
        setConnectorEventsOpen(false)
      }
    }
    document.addEventListener('mousedown', handlePointerDown)
    return () => document.removeEventListener('mousedown', handlePointerDown)
  }, [])

  const setUserRole = (userId, role) => {
    setUserRoleMap((prev) => {
      const next = { ...prev }
      if (!role) {
        delete next[userId]
        return next
      }
      // Approver and Template Manager are single-assignee roles
      if (role === 'approver' || role === 'template_manager') {
        Object.keys(next).forEach((id) => {
          if (next[id] === role) delete next[id]
        })
      }
      next[userId] = role
      return next
    })
  }

  const removeUserAccess = (userId) => {
    setUserRoleMap((prev) => {
      const next = { ...prev }
      delete next[userId]
      return next
    })
  }

  const toggleAccessGroup = (groupId) => {
    setSelectedAccessGroupIds((prev) => {
      const next = new Set(prev)
      if (next.has(groupId)) next.delete(groupId)
      else next.add(groupId)
      return next
    })
  }

  // Quick-assign from the search dropdown: add the user with a role, close the
  // dropdown, clear the query, and briefly flag the row so it can pulse/highlight
  // as it lands at the top of the "assigned" list.
  const quickAssignUser = (userId, role) => {
    setUserRole(userId, role || 'reviewer')
    setAccessUserSearch('')
    setAccessUserDropdownOpen(false)
    setJustAddedUserId(userId)
    window.setTimeout(() => setJustAddedUserId((cur) => (cur === userId ? null : cur)), 1200)
  }

  const quickAssignGroup = (groupId) => {
    setSelectedAccessGroupIds((prev) => {
      const next = new Set(prev)
      next.add(groupId)
      return next
    })
    setAccessGroupSearch('')
    setAccessGroupDropdownOpen(false)
    setJustAddedGroupId(groupId)
    window.setTimeout(() => setJustAddedGroupId((cur) => (cur === groupId ? null : cur)), 1200)
  }

  // Select a source template from the searchable "Copy existing template" picker
  const selectSourceTemplate = (templateId) => {
    setSourceTemplateId(String(templateId))
    setSourceTemplateSearch('')
    setSourceTemplateDropdownOpen(false)
  }

  const adminUsersForAccess = useMemo(
    () => orgUsers.filter((u) => u.role === 'admin' && u.is_active !== false),
    [orgUsers]
  )
  const nonAdminOrgUsers = useMemo(
    () => orgUsers.filter((u) => u.role !== 'admin' && u.is_active !== false),
    [orgUsers]
  )

  const ROLE_OPTIONS = [
    { value: 'reviewer', label: 'Reviewer' },
    { value: 'approver', label: 'Approver' },
    { value: 'template_manager', label: 'Template Manager' },
  ]

  const assignedRows = useMemo(() => {
    const adminRows = adminUsersForAccess.map((u) => ({ user: u, role: 'admin', isAdminRow: true }))
    const roleRows = Object.entries(userRoleMap)
      .map(([userId, role]) => {
        const u = orgUsers.find((ou) => String(ou.id) === String(userId))
        if (!u) return null
        return { user: u, role, isAdminRow: false }
      })
      .filter(Boolean)
    return [...adminRows, ...roleRows]
  }, [adminUsersForAccess, userRoleMap, orgUsers])

  // Kept below every hook in this component: returning before them changes
  // the hook count when `open` flips, and React throws ("Rendered more hooks
  // than during the previous render") — the modal stays mounted with open=false.
  if (!open) return null

  const handleSampleDocsSelected = (event) => {
    const files = Array.from(event.target.files || [])
    if (!files.length) return
    setSampleDocuments((prev) => [...prev, ...files].slice(0, 10))
    setBlankProcessedSignature('')
    event.target.value = ''
  }

  const removeSampleDocument = (indexToRemove) => {
    setBlankProcessedSignature('')
    setSampleDocuments((prev) => prev.filter((_, idx) => idx !== indexToRemove))
  }

  // ─── NetSuite PO field-row helpers (Configure Fields step for ns_purchase_order) ───
  // Builds local editable rows matching TemplateDetail.jsx's persisted Fields tab table
  // (Display / Field Name / Type / Required / Description / User Input / NS Mapping /
  // Actions), but pre-creation: everything lives in nsEditableFields until the template
  // is created, at which point handleSubmit turns it into prefill_fields for the existing
  // templatesApi.importFields / addField APIs (see handleCreateTemplate) — no backend changes.
  // Prefers the account's own live mandatory_fields (fetched in the NetSuite
  // setup step, already scoped to whichever entity type is selected) over the
  // hardcoded PO-only NS_MANDATORY_KEYS fallback — Bid's mandatory keys
  // (custrecord_jcs_sp_supplier, etc.) never match that PO list, so without
  // this every Bid field would default to "not required" regardless of what
  // the account actually needs.
  const isNsFieldMandatory = (key) => {
    if (!key) return false
    if (nsDetectResult?.mandatory_fields) return nsDetectResult.mandatory_fields.includes(key)
    return NS_MANDATORY_KEYS.includes(key)
  }

  const nsRowFromMapping = (m, idx) => {
    const nsKey = m.suggested_ns_key || ''
    const label = m.label || nsKey || `field_${idx + 1}`
    const fieldName = toInternalFieldName(label) || `field_${idx + 1}`
    return {
      id: `${fieldName}-${idx}-${Date.now()}`,
      key: fieldName,
      label,
      type: 'text',
      description: nsKey && NS_FIELD_HINTS[nsKey] ? NS_FIELD_HINTS[nsKey] : (m.sample_value ? `Sample value: ${m.sample_value}` : ''),
      required: isNsFieldMandatory(nsKey),
      user_input: false,
      ns_field: nsKey || null,
      confidence: m.confidence ?? null,
    }
  }

  // Special Pricing has a shape a flat field list cannot express: eight of the
  // document's columns belong to ONE line-item table, a handful of line keys
  // are printed once in the header, and some labels are deliberately not
  // fields at all. Mapping every row through nsRowFromMapping the way a PO
  // does turns the product table into eight bogus header fields and loses the
  // table entirely — so the bid shape is built by the shared helper this page
  // and the Fields tab both use (utils/nsBidFields).
  const nsBidRowsFromMappings = (mappings) =>
    bidFieldsFromMappings(mappings).map((f, idx) => ({
      id: `${f.field_name}-${idx}-${Date.now()}`,
      key: f.field_name,
      label: f.display_name || f.field_name,
      type: f.field_type || 'text',
      description: f.description || (f.sample_value ? `Sample value: ${f.sample_value}` : ''),
      required: isNsFieldMandatory(f.ns_key),
      user_input: false,
      ns_field: f.ns_key || null,
      confidence: f.confidence ?? null,
      mapping_source: f.mapping_source || null,
      ...(f.columns ? { columns: f.columns } : {}),
    }))

  const nsRowFromStandardField = (f) => ({
    id: `${f.field_name}-${Date.now()}`,
    key: f.field_name,
    label: f.display_name,
    type: f.field_type || 'text',
    description: f.description || '',
    required: isNsFieldMandatory(f.ns_key),
    user_input: false,
    ns_field: f.ns_key,
    confidence: null,
  })

  // Purchase Order's own convenience shortlist — the account's most commonly
  // mapped header fields, hand-picked for readability (tranDate/entity/etc.
  // read naturally; the full PO catalog is 250+ fields and not a useful
  // "quick add" list). Vendor Bid (or any future document type) has no such
  // hand-picked list — see addStandardNsFields below, which for anything
  // other than PO instead pulls the account's own mandatory fields straight
  // from the live fetch, so it never needs a hardcoded custrecord_jcs_sp_*
  // list here that would drift from what the account actually requires.
  const NS_STANDARD_PO_FIELDS = [
    { field_name: 'tranDate', display_name: 'Date', ns_key: 'tranDate' },
    { field_name: 'tranId', display_name: 'Order No', ns_key: 'tranId' },
    { field_name: 'entity', display_name: 'Supplier', ns_key: 'entity' },
    { field_name: 'location', display_name: 'Deliver To', ns_key: 'location' },
    { field_name: 'currency', display_name: 'Currency', ns_key: 'currency' },
    { field_name: 'subsidiary', display_name: 'Subsidiary', ns_key: 'subsidiary' },
  ].map((f) => ({ ...f, field_type: 'text', description: `${NS_FIELD_HINTS[f.ns_key] || ''}`.trim() }))

  const addStandardNsFields = () => {
    const standardFields = selectedNsEntityType === 'purchase_order'
      ? NS_STANDARD_PO_FIELDS
      : (() => {
          const availableByKey = new Map((nsDetectResult?.available_fields || []).map((f) => [f.key, f]))
          return (nsDetectResult?.mandatory_fields || []).map((key) => {
            const meta = availableByKey.get(key)
            return {
              field_name: key,
              display_name: meta?.label || key,
              ns_key: key,
              field_type: 'text',
              description: '',
            }
          })
        })()
    setNsEditableFields((prev) => {
      const existingKeys = new Set(prev.map((f) => f.key))
      const additions = standardFields.filter((f) => !existingKeys.has(f.field_name)).map(nsRowFromStandardField)
      if (additions.length) {
        setNsSelectedFieldKeys((prevSelected) => {
          const next = new Set(prevSelected)
          additions.forEach((f) => next.add(f.key))
          return next
        })
      }
      return [...prev, ...additions]
    })
  }

  const addBlankNsField = () => {
    let counter = nsEditableFields.length + 1
    const existing = new Set(nsEditableFields.map((f) => f.key))
    let candidate = `customField${counter}`
    while (existing.has(candidate)) {
      counter += 1
      candidate = `customField${counter}`
    }
    setNsEditableFields((prev) => ([
      ...prev,
      {
        id: `${candidate}-${Date.now()}`,
        key: candidate,
        label: candidate,
        type: 'text',
        description: '',
        required: false,
        user_input: false,
        ns_field: null,
        confidence: null,
      },
    ]))
    setNsSelectedFieldKeys((prev) => new Set(prev).add(candidate))
  }

  const updateNsField = (fieldKey, patch) => {
    setNsEditableFields((prev) => prev.map((f) => (f.key === fieldKey ? { ...f, ...patch } : f)))
  }

  // A line-items ('array_of_objects') row's own columns each need their own
  // NetSuite mapping — updateNsField only ever patches the row itself, so an
  // imported Special Pricing / Vendor Bid line table had no way to edit or
  // even see its column mappings from this screen.
  const updateNsFieldColumn = (fieldKey, columnKey, patch) => {
    setNsEditableFields((prev) =>
      prev.map((f) => {
        if (f.key !== fieldKey || !Array.isArray(f.columns)) return f
        return {
          ...f,
          columns: f.columns.map((c) => (c.key === columnKey ? { ...c, ...patch } : c)),
        }
      }),
    )
  }

  const deleteNsField = (fieldKey) => {
    setNsEditableFields((prev) => prev.filter((f) => f.key !== fieldKey))
    setNsSelectedFieldKeys((prev) => {
      if (!prev.has(fieldKey)) return prev
      const next = new Set(prev)
      next.delete(fieldKey)
      return next
    })
  }

  const toggleNsFieldSelection = (fieldKey) => {
    setNsSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      if (next.has(fieldKey)) next.delete(fieldKey)
      else next.add(fieldKey)
      return next
    })
  }

  const setNsFieldSelectionForKeys = (fieldKeys, shouldSelect) => {
    const keys = Array.isArray(fieldKeys) ? fieldKeys.filter(Boolean) : []
    if (!keys.length) return
    setNsSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      keys.forEach((key) => {
        if (shouldSelect) next.add(key)
        else next.delete(key)
      })
      return next
    })
  }

  // ─── Business Central PO field-row helpers (Configure Fields step for bc_purchase_order) ───
  // Mirrors the NS helpers above exactly — see nsRowFromMapping/nsRowFromStandardField/
  // NS_STANDARD_PO_FIELDS/addStandardNsFields/addBlankNsField/updateNsField/deleteNsField.
  //
  // Handles two mapping shapes returned by /business-central/extract-from-document:
  // - Legacy AI-guess mapping (Purchase Orders, no seeded field catalog): { label,
  //   suggested_bc_key, sample_value, confidence }.
  // - Deterministic field-plan mapping (any entity type with a seeded catalog, e.g.
  //   Completion Statement): { label, field_name, suggested_bc_key, sample_value,
  //   mandatory, value_found } — no confidence score, because the mapping is already
  //   fixed by the entity's field catalog rather than guessed.
  const bcRowFromMapping = (m, idx) => {
    const bcKey = m.suggested_bc_key || ''
    const label = m.label || bcKey || `field_${idx + 1}`
    const fieldName = m.field_name || toInternalFieldName(label) || `field_${idx + 1}`
    return {
      id: `${fieldName}-${idx}-${Date.now()}`,
      key: fieldName,
      label,
      type: m.field_type || 'text',
      // The description is the instruction the extractor reads on every document, so the
      // server's layout-aware hint wins over the generic one ("Sample value: X" taught it
      // nothing about where the value is or what it must not be confused with).
      description: m.extraction_hint
        || (bcKey && BC_FIELD_HINTS[bcKey])
        || (m.sample_value ? `Sample value: ${m.sample_value}` : ''),
      required: typeof m.mandatory === 'boolean' ? m.mandatory : BC_MANDATORY_KEYS.includes(bcKey),
      user_input: false,
      bc_field: bcKey || null,
      confidence: m.confidence ?? null,
      // The line-items table field: its columns carry their own extraction hints, and it
      // maps to the BC lines entity (shown as "<Type> Lines · Line").
      ...(Array.isArray(m.columns) ? { columns: m.columns } : {}),
      ...(m.scope === 'line' ? { bc_field_label: m.bc_field_label } : {}),
    }
  }

  const bcRowFromStandardField = (f) => ({
    id: `${f.field_name}-${Date.now()}`,
    key: f.field_name,
    label: f.display_name,
    type: f.field_type || 'text',
    description: f.description || '',
    required: BC_MANDATORY_KEYS.includes(f.bc_key),
    user_input: false,
    bc_field: f.bc_key,
    confidence: null,
  })

  const BC_STANDARD_FIELDS = [
    { field_name: 'orderDate', display_name: 'Order Date', bc_key: 'orderDate' },
    { field_name: 'yourReference', display_name: 'PO Number', bc_key: 'yourReference' },
    { field_name: 'vendorNumber', display_name: 'Vendor', bc_key: 'vendorNumber' },
    { field_name: 'shipToAddressLine1', display_name: 'Deliver To', bc_key: 'shipToAddressLine1' },
    { field_name: 'currencyCode', display_name: 'Currency', bc_key: 'currencyCode' },
    { field_name: 'paymentTermsCode', display_name: 'Payment Terms', bc_key: 'paymentTermsCode' },
  ].map((f) => ({ ...f, field_type: 'text', description: `${BC_FIELD_HINTS[f.bc_key] || ''}`.trim() }))

  const addStandardBcFields = () => {
    setBcEditableFields((prev) => {
      const existingKeys = new Set(prev.map((f) => f.key))
      const additions = BC_STANDARD_FIELDS.filter((f) => !existingKeys.has(f.field_name)).map(bcRowFromStandardField)
      return [...prev, ...additions]
    })
  }

  const addBlankBcField = () => {
    let counter = bcEditableFields.length + 1
    const existing = new Set(bcEditableFields.map((f) => f.key))
    let candidate = `customField${counter}`
    while (existing.has(candidate)) {
      counter += 1
      candidate = `customField${counter}`
    }
    setBcEditableFields((prev) => ([
      ...prev,
      {
        id: `${candidate}-${Date.now()}`,
        key: candidate,
        label: candidate,
        type: 'text',
        description: '',
        required: false,
        user_input: false,
        bc_field: null,
        confidence: null,
      },
    ]))
  }

  const updateBcField = (fieldKey, patch) => {
    setBcEditableFields((prev) => prev.map((f) => (f.key === fieldKey ? { ...f, ...patch } : f)))
  }

  const deleteBcField = (fieldKey) => {
    setBcEditableFields((prev) => prev.filter((f) => f.key !== fieldKey))
  }

  const toggleBlankFieldSelection = (fieldKey) => {
    setBlankSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      if (next.has(fieldKey)) next.delete(fieldKey)
      else next.add(fieldKey)
      return next
    })
  }

  const setBlankFieldSelectionForKeys = (fieldKeys, shouldSelect) => {
    const keys = Array.isArray(fieldKeys) ? fieldKeys.filter(Boolean) : []
    if (!keys.length) return
    setBlankSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      keys.forEach((key) => {
        if (shouldSelect) next.add(key)
        else next.delete(key)
      })
      return next
    })
  }

  const updateBlankField = (fieldKey, patch) => {
    setBlankEditableFields((prev) => prev.map((f) => (f.key === fieldKey ? { ...f, ...patch } : f)))
    if (Object.prototype.hasOwnProperty.call(patch, 'display')) {
      setBlankCheckedFields((prev) => ({ ...prev, [fieldKey]: !!patch.display }))
    }
    setBlankFieldsDirty(true)
  }

  const addBlankField = () => {
    const base = 'customField'
    let counter = blankEditableFields.length + 1
    let candidate = `${base}${counter}`
    const existing = new Set(blankEditableFields.map((f) => f.key))
    while (existing.has(candidate)) {
      counter += 1
      candidate = `${base}${counter}`
    }

    setBlankFieldModalMode('add')
    setBlankFieldOriginalKey('')
    setBlankFieldDraft({
      label: candidate,
      key: candidate,
      type: 'text',
      description: '',
      required: false,
      user_input: false,
      display: true,
      section: 'None',
    })
    setBlankFieldModalOpen(true)
  }

  const restoreDeletedBlankFields = (records) => {
    if (!Array.isArray(records) || records.length === 0) return

    const sorted = [...records].sort((a, b) => a.index - b.index)

    setBlankEditableFields((prev) => {
      const next = [...prev]
      sorted.forEach(({ index, field }) => {
        if (!field?.key) return
        if (next.some((f) => f.key === field.key)) return
        const safeIndex = Math.max(0, Math.min(index, next.length))
        next.splice(safeIndex, 0, field)
      })
      return next
    })

    setBlankFieldsDict((prev) => {
      const next = { ...prev }
      sorted.forEach(({ field, dictEntry }) => {
        if (!field?.key) return
        next[field.key] = dictEntry || {
          type: field.type || 'text',
          description: field.description || '',
          label: field.label || field.key,
        }
      })
      return next
    })

    setBlankCheckedFields((prev) => {
      const next = { ...prev }
      sorted.forEach(({ field, checked }) => {
        if (!field?.key) return
        next[field.key] = checked ?? !!field.display
      })
      return next
    })
  }

  const deleteBlankFieldsWithUndo = (records, toastTitle) => {
    if (!Array.isArray(records) || records.length === 0) return

    setBlankFieldsDirty(true)

    const keys = new Set(records.map((r) => r.field.key))
    setBlankEditableFields((prev) => prev.filter((f) => !keys.has(f.key)))

    setBlankFieldsDict((prev) => {
      const next = { ...prev }
      records.forEach(({ field }) => { delete next[field.key] })
      return next
    })

    setBlankCheckedFields((prev) => {
      const next = { ...prev }
      records.forEach(({ field }) => { delete next[field.key] })
      return next
    })

    setBlankSelectedFieldKeys((prev) => {
      const next = new Set(prev)
      records.forEach(({ field }) => next.delete(field.key))
      return next
    })

    toast.addToast({
      type: 'warning',
      title: toastTitle,
      message: `${records.length} field${records.length !== 1 ? 's' : ''} removed.`,
      duration: 8000,
      action: {
        label: 'Undo',
        onClick: () => {
          restoreDeletedBlankFields(records)
          setBlankFieldsDirty(true)
          toast.success('Undo complete', `${records.length} field${records.length !== 1 ? 's' : ''} restored.`)
        },
      },
    })
  }

  const promptDeleteBlankFields = (records, title, message) => {
    if (!Array.isArray(records) || records.length === 0) return
    setPendingDeleteRecords(records)
    setConfirmDeleteTitle(title)
    setConfirmDeleteMessage(message)
    setConfirmDeleteOpen(true)
  }

  const closeDeleteConfirmModal = () => {
    setConfirmDeleteOpen(false)
    setConfirmDeleteTitle('')
    setConfirmDeleteMessage('')
    setPendingDeleteRecords([])
  }

  const confirmDeleteBlankFields = () => {
    if (!pendingDeleteRecords.length) {
      closeDeleteConfirmModal()
      return
    }
    const toastTitle = pendingDeleteRecords.length === 1 ? 'Field deleted' : 'Fields deleted'
    deleteBlankFieldsWithUndo(pendingDeleteRecords, toastTitle)
    closeDeleteConfirmModal()
  }

  const deleteBlankField = (fieldKey) => {
    const target = blankEditableFields.find((f) => f.key === fieldKey)
    if (!target) return

    const index = blankEditableFields.findIndex((f) => f.key === fieldKey)
    const record = {
      index,
      field: target,
      dictEntry: blankFieldsDict[fieldKey] || null,
      checked: blankCheckedFields[fieldKey] ?? !!target.display,
    }
    promptDeleteBlankFields(
      [record],
      'Delete field',
      `Are you sure you want to delete "${target.label || target.key}"? This action can be undone from the notification.`
    )
  }

  const deleteSelectedBlankFields = () => {
    const selected = new Set(blankSelectedFieldKeys)
    if (selected.size === 0) return

    const records = blankEditableFields
      .map((field, index) => ({ field, index }))
      .filter(({ field }) => selected.has(field.key))
      .map(({ field, index }) => ({
        index,
        field,
        dictEntry: blankFieldsDict[field.key] || null,
        checked: blankCheckedFields[field.key] ?? !!field.display,
      }))

    promptDeleteBlankFields(
      records,
      'Delete selected fields',
      `Are you sure you want to delete ${selected.size} selected field${selected.size !== 1 ? 's' : ''}? This action can be undone from the notification.`
    )
  }

  const saveBlankFieldChanges = () => {
    if (!blankFieldsDirty) return
    setBlankFieldsDirty(false)
    toast.success('Changes saved', `${blankEditableFields.length} field${blankEditableFields.length !== 1 ? 's' : ''} ready for template creation.`)
  }

  const openEditBlankFieldModal = (field) => {
    setBlankFieldModalMode('edit')
    setBlankFieldOriginalKey(field.key)
    setBlankFieldDraft({
      label: field.label || field.key,
      key: field.key,
      type: field.type || 'text',
      description: field.description || '',
      required: !!field.required,
      user_input: !!field.user_input,
      display: !!field.display,
      section: field.section || 'None',
    })
    setBlankFieldModalOpen(true)
  }

  const closeBlankFieldModal = () => {
    setBlankFieldModalOpen(false)
  }

  const toInternalFieldName = (value) => {
    return String(value || '')
      .trim()
      .replace(/[^a-zA-Z0-9_\s-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/^_+|_+$/g, '')
      .toLowerCase()
  }

  const applyBlankFieldModal = () => {
    const label = (blankFieldDraft.label || '').trim()
    const key = (blankFieldDraft.key || '').trim()
    if (!label || !key) return

    const keyChanged = blankFieldModalMode === 'edit' && blankFieldOriginalKey !== key
    const duplicate = blankEditableFields.some((f) => f.key === key && f.key !== blankFieldOriginalKey)
    if (duplicate) return

    if (blankFieldModalMode === 'add') {
      const nextField = {
        id: `${key}-${Date.now()}`,
        key,
        label,
        type: blankFieldDraft.type || 'text',
        description: blankFieldDraft.description || '',
        required: !!blankFieldDraft.required,
        user_input: !!blankFieldDraft.user_input,
        display: !!blankFieldDraft.display,
        section: blankFieldDraft.section || 'None',
      }
      setBlankEditableFields((prev) => [...prev, nextField])
      setBlankFieldsDict((prev) => ({
        ...prev,
        [key]: {
          ...(prev[key] || {}),
          type: blankFieldDraft.type || 'text',
          description: blankFieldDraft.description || '',
          user_input: !!blankFieldDraft.user_input,
          required: !!blankFieldDraft.required,
          label,
        },
      }))
      setBlankCheckedFields((prev) => ({ ...prev, [key]: !!blankFieldDraft.display }))
    } else {
      setBlankEditableFields((prev) => prev.map((f) => (
        f.key === blankFieldOriginalKey
          ? {
              ...f,
              key,
              label,
              type: blankFieldDraft.type || 'text',
              description: blankFieldDraft.description || '',
              required: !!blankFieldDraft.required,
              user_input: !!blankFieldDraft.user_input,
              display: !!blankFieldDraft.display,
              section: blankFieldDraft.section || 'None',
            }
          : f
      )))

      setBlankFieldsDict((prev) => {
        const next = { ...prev }
        const source = next[blankFieldOriginalKey] || {}
        if (keyChanged) delete next[blankFieldOriginalKey]
        next[key] = {
          ...source,
          type: blankFieldDraft.type || 'text',
          description: blankFieldDraft.description || '',
          user_input: !!blankFieldDraft.user_input,
          required: !!blankFieldDraft.required,
          label,
        }
        return next
      })

      setBlankCheckedFields((prev) => {
        const next = { ...prev }
        if (keyChanged) delete next[blankFieldOriginalKey]
        next[key] = !!blankFieldDraft.display
        return next
      })

      if (keyChanged) {
        setBlankSelectedFieldKeys((prev) => {
          const next = new Set(prev)
          if (next.has(blankFieldOriginalKey)) {
            next.delete(blankFieldOriginalKey)
            next.add(key)
          }
          return next
        })
      }
    }

    setBlankFieldsDirty(true)
    setBlankFieldModalOpen(false)
  }

  const handleSubmit = () => {
    if (!name.trim()) return
    // Derive approver / template manager / general access users from the unified role map
    const derivedApproverId = isAdmin
      ? (Object.entries(userRoleMap).find(([, role]) => role === 'approver')?.[0] || undefined)
      : (approverId || undefined)
    const derivedManagerId = Object.entries(userRoleMap).find(([, role]) => role === 'template_manager')?.[0] || undefined
    const derivedAccessUserIds = Object.keys(userRoleMap)
    // Shared connector fields used whenever the wizard is set to create a brand-new webhook
    const connectorFields = connectorMode === 'create_webhook'
      ? {
          connector_events: connectorEvents,
          connector_update_mode: connectorUpdateMode,
          connector_secret: connectorSecret.trim() || undefined,
          connector_basic_auth_username: connectorBasicAuthUsername.trim() || undefined,
          connector_basic_auth_password: connectorBasicAuthPassword || undefined,
        }
      : {}
    if (creationMode === 'bc_purchase_order') {
      // Build prefill_fields from the editable BC field table (mirrors the ns_purchase_order
      // flow below) so handleCreateTemplate can import them via the existing
      // templatesApi.importFields API (which now supports bc_field per-field) — no
      // backend changes required beyond the bc_field plumbing already added.
      const bcPrefillFields = {}
      bcEditableFields.forEach((f) => {
        bcPrefillFields[f.key] = {
          type: f.type || 'text',
          description: f.description || '',
          label: f.label || f.key,
          required: !!f.required,
          user_input: !!f.user_input,
          bc_field: f.bc_field || null,
          ...(Array.isArray(f.columns) ? { columns: f.columns } : {}),
        }
      })
      emitCreate({
        name: name.trim(),
        description: description.trim() || null,
        allow_reprocessing: allowReprocessing,
        creation_method: 'bc_purchase_order',
        verification_prompt: verificationPrompt.trim() || null,
        prefill_fields: Object.keys(bcPrefillFields).length ? bcPrefillFields : undefined,
        // Explicit document type + connection chosen in this step, mirroring
        // the same fields TemplateBCSettings.jsx lets you change post-creation.
        bc_entity_type: selectedBcEntityType || 'purchase_order',
        bc_connection_id: selectedBcConnectionId || undefined,
        approver_id: derivedApproverId,
        manager_id: derivedManagerId,
        access_user_ids: derivedAccessUserIds,
        access_group_ids: Array.from(selectedAccessGroupIds),
        connector_mode: connectorMode,
        connector_webhook_id: connectorMode === 'select_existing' ? selectedConnectorWebhookId : undefined,
        connector_name: connectorMode === 'create_webhook' ? connectorName.trim() : undefined,
        connector_url: connectorMode === 'create_webhook' ? connectorUrl.trim() : undefined,
        ...connectorFields,
      })
    } else if (creationMode === 'boomi_purchase_order') {
      emitCreate({
        name: name.trim(),
        description: description.trim() || null,
        allow_reprocessing: allowReprocessing,
        creation_method: 'boomi_purchase_order',
        verification_prompt: verificationPrompt.trim() || null,
        approver_id: approverId || undefined,
      })
    } else if (creationMode === 'ns_purchase_order') {
      // Build prefill_fields from the editable NS field table (mirrors the "from_template"
      // reconciliation flow below) so handleCreateTemplate can import them via the existing
      // templatesApi.importFields API (which already supports ns_field per-field) — no
      // backend changes required.
      const nsPrefillFields = {}
      // Only the fields the user left checked in the Configure Fields step —
      // unchecking a field there excludes it from the created template without
      // deleting the row (the trash icon is still the only way to do that).
      nsEditableFields.filter((f) => nsSelectedFieldKeys.has(f.key)).forEach((f) => {
        nsPrefillFields[f.key] = {
          type: f.type || 'text',
          description: f.description || '',
          label: f.label || f.key,
          required: !!f.required,
          user_input: !!f.user_input,
          ns_field: f.ns_field || null,
          // A line-items table has to travel as a CU array schema, or
          // import-fields creates it as a plain text box and the Special
          // Pricing product table is never extracted. See
          // templates_api.import_template_fields, which reads its columns
          // back out of items.properties.
          ...(f.type === 'array_of_objects' && f.columns?.length
            ? {
                type: 'array',
                items: {
                  type: 'object',
                  properties: Object.fromEntries(
                    f.columns.map((c) => [
                      c.name || c.key,
                      {
                        type: c.type === 'currency' ? 'number' : (c.type || 'string'),
                        description: c.description || '',
                        // Extra keys beyond type/description aren't standard
                        // JSON Schema, but import_template_fields on the
                        // backend reads them back out explicitly (see
                        // templates_api.py) — without this, a line-items
                        // table built through this wizard would create the
                        // right columns but with none of their NetSuite
                        // mappings, which is the whole point of building a
                        // Vendor Bid / Special Pricing template this way.
                        ns_field: c.ns_field || null,
                        required: c.required !== false,
                        posted: c.posted !== false,
                        inherited_from_header: c.inherited_from_header || null,
                      },
                    ]),
                  ),
                },
              }
            : {}),
        }
      })
      emitCreate({
        name: name.trim(),
        description: description.trim() || null,
        allow_reprocessing: allowReprocessing,
        creation_method: 'ns_purchase_order',
        // Which NetSuite record this template creates. Drives field detection,
        // the request-review screen and the posting mechanics.
        ns_entity_type: selectedNsEntityType,
        // Display label for that record type, carried along purely so the
        // parent's success toast can name the document type. Stripped out
        // before the create call so it never reaches the backend.
        ns_entity_label: selectedNsEntityLabel,
        // The chosen company scopes the template: a PO belongs to one NetSuite
        // subsidiary, and field detection samples that subsidiary's own POs.
        ns_default_subsidiary_id: nsCompanyId || undefined,
        verification_prompt: verificationPrompt.trim() || null,
        prefill_fields: Object.keys(nsPrefillFields).length ? nsPrefillFields : undefined,
        approver_id: derivedApproverId,
        manager_id: derivedManagerId,
        access_user_ids: derivedAccessUserIds,
        access_group_ids: Array.from(selectedAccessGroupIds),
        connector_mode: connectorMode,
        connector_webhook_id: connectorMode === 'select_existing' ? selectedConnectorWebhookId : undefined,
        connector_name: connectorMode === 'create_webhook' ? connectorName.trim() : undefined,
        connector_url: connectorMode === 'create_webhook' ? connectorUrl.trim() : undefined,
        ...connectorFields,
      })
    } else if (creationMode === 'from_template') {
      // Build field overrides from any add/edit/delete changes made in the
      // "Configure fields" step so the copy reflects the user's edits, not
      // just a verbatim clone of the source template.
      const copiedFieldOverrides = {}
      blankEditableFields.forEach((f) => {
        const base = blankFieldsDict[f.key] || {}
        copiedFieldOverrides[f.key] = {
          ...base,
          type: f.type || base.type || 'text',
          description: f.description || base.description || '',
          user_input: !!f.user_input,
          required: !!f.required,
          label: f.label || f.key,
        }
      })
      emitCreate({
        name: name.trim(),
        description: description.trim() || null,
        allow_reprocessing: allowReprocessing,
        source_template_id: sourceTemplateId || undefined,
        reuse_source_analyzer: sourceTemplateId ? reuseSourceAnalyzer : false,
        prefill_fields: Object.keys(copiedFieldOverrides).length ? copiedFieldOverrides : undefined,
        approver_id: derivedApproverId,
        manager_id: derivedManagerId,
        access_user_ids: derivedAccessUserIds,
        access_group_ids: Array.from(selectedAccessGroupIds),
        connector_mode: connectorMode,
        connector_webhook_id: connectorMode === 'select_existing' ? selectedConnectorWebhookId : undefined,
        connector_name: connectorMode === 'create_webhook' ? connectorName.trim() : undefined,
        connector_url: connectorMode === 'create_webhook' ? connectorUrl.trim() : undefined,
        ...connectorFields,
      })
    } else {
      const selectedPreviewFields = {}
      blankEditableFields.forEach((f) => {
        if (!f.display) return
        const base = blankFieldsDict[f.key] || {}
        selectedPreviewFields[f.key] = {
          ...base,
          type: f.type || base.type || 'text',
          description: f.description || base.description || '',
          user_input: !!f.user_input,
          required: !!f.required,
          label: f.label || f.key,
          // The section shown in this step is the field's category.
          ...(f.section && f.section !== 'None' ? { category: f.section } : {}),
        }
      })
      emitCreate({
        name: name.trim(),
        description: description.trim() || null,
        allow_reprocessing: allowReprocessing,
        creation_method: 'manual',
        // How the sample documents write dates, as the AI suggestion read them;
        // the template's Dates on Documents setting (default day first).
        date_order: ['day_first', 'month_first', 'year_first'].includes(blankSuggestion?.date_order)
          ? blankSuggestion.date_order
          : undefined,
        prefill_fields: Object.keys(selectedPreviewFields).length ? selectedPreviewFields : undefined,
        approver_id: derivedApproverId,
        manager_id: derivedManagerId,
        access_user_ids: derivedAccessUserIds,
        access_group_ids: Array.from(selectedAccessGroupIds),
        connector_mode: connectorMode,
        connector_webhook_id: connectorMode === 'select_existing' ? selectedConnectorWebhookId : undefined,
        connector_name: connectorMode === 'create_webhook' ? connectorName.trim() : undefined,
        connector_url: connectorMode === 'create_webhook' ? connectorUrl.trim() : undefined,
        ...connectorFields,
      })
    }
  }

  const panel = (
      <div className={`relative w-full bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 flex flex-col overflow-hidden ${fullPage ? 'create-surface rounded-2xl h-[calc(100vh-8rem)]' : 'max-w-lg rounded-2xl shadow-2xl max-h-[90vh]'}`}>
        {/* Header */}
        <div className="flex items-center gap-4 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-[#1f2d45] text-white shadow-sm`}>
            <FileStack className="h-5 w-5" />
          </div>
          <div className="flex-1 min-w-0" data-create-stagger>
            <h3 className={`${fullPage ? 'text-[1.3rem]' : 'text-[1.2rem]'} font-semibold tracking-tight text-gray-900 dark:text-gray-100 leading-tight`}>Create a template</h3>
            <p className="text-[13px] md:text-[14px] text-gray-500 dark:text-gray-400 mt-1 leading-relaxed">
              Set up an extraction workflow in a few guided steps — your progress is saved as you go.
            </p>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="flex-shrink-0 inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Stepper (fixed, does not scroll with step content) */}
        {isAdmin && (
          <div className="border-b border-gray-100 dark:border-gray-800 px-5 pb-3 pt-3 overflow-x-auto bg-white dark:bg-gray-900">
            <WizardStepper
              ariaLabel="Template creation steps"
              current={wizardStep}
              steps={activeWizardSteps.map((item, idx) => {
                const step = idx + 1
                // Allow jumping straight to the very next step if the current step's
                // requirements are already satisfied (mirrors the Continue button),
                // so e.g. after processing sample docs the user can click "Configure fields"
                // directly instead of being forced to press Continue first.
                const nextReachable = step === wizardStep + 1 && canContinueStep()
                return {
                  key: item.key || item.label,
                  label: item.label,
                  icon: item.icon,
                  disabled: wizardStep < step && !nextReachable,
                  onSelect: () => requestStepChange(step),
                }
              })}
            />
          </div>
        )}

        {/* Body (only this middle section scrolls) */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
          {isAdmin && (
              <div key={wizardStep} className="pt-0.5 pb-3 animate-[fadeIn_0.3s_ease-out]">
                <p className="text-[12px] font-semibold uppercase tracking-[0.11em] text-gray-500 dark:text-gray-400">Step {wizardStep} of {totalSteps}</p>
                <h4 className="mt-1 text-[1.3rem] md:text-[1.4rem] font-semibold tracking-tight text-gray-900 dark:text-gray-100">{currentStepMeta?.title}</h4>
                <p className="mt-0.5 text-[13px] md:text-[14px] text-gray-500 dark:text-gray-400 max-w-3xl leading-relaxed">{currentStepMeta?.description}</p>
            </div>
          )}

          {(!isAdmin || currentStepKey === 'template_details') && (
            <div key="template_details" className="contents animate-[fadeIn_0.3s_ease-out]">
              <div>
                <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">Template name <span className="text-red-500">*</span></label>
                <input
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Supplier invoices"
                  autoFocus
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-[14px] transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                />
              </div>

              {showOpcoPicker && (
                <div className="mt-2">
                  <TemplateOpCoPicker
                    value={createOpcoCode}
                    onChange={setCreateOpcoCode}
                    hint="Unassigned templates show only in the All OpCos view. A copy for another OpCo builds its own analyzer."
                  />
                </div>
              )}

              <div className="mt-2">
                <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-1.5">Description</label>
                <textarea
                  rows={3}
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="What type of documents will this template process?"
                  className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-[14px] resize-none"
                />
              </div>

              <div>
                <label className="block text-[14px] font-medium text-gray-700 dark:text-gray-300 mb-2">Creation method</label>
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800 bg-white dark:bg-gray-900">
                  {creationMethodOptions.map((option, optionIdx) => {
                    const isConnectorsRow = option.key === 'connectors'
                    const mode = option.mapsTo || option.key
                    const selected = isConnectorsRow ? isConnectorMode : creationMode === mode
                    const OptionIcon = CREATION_METHOD_ICONS[option.key] || FileStack
                    const expanded = isConnectorsRow ? isConnectorMode : creationMode === 'from_template' && option.key === 'from_template'
                    const isFirst = optionIdx === 0
                    const isLast = optionIdx === creationMethodOptions.length - 1

                    return (
                      <div key={option.key} className={isLast && !expanded ? 'rounded-b-xl overflow-hidden' : undefined} data-create-stagger>
                        <button
                          type="button"
                          onClick={() => setCreationMode(isConnectorsRow ? (connectorSubOptions[0]?.mapsTo || mode) : mode)}
                          aria-pressed={selected}
                          className={`w-full flex items-center gap-3 text-left px-4 py-3 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 focus-visible:ring-inset ${isFirst ? 'rounded-t-xl' : ''} ${isLast && !expanded ? 'rounded-b-xl' : ''} ${selected ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                        >
                          <span className={`flex-shrink-0 w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${selected ? 'bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                            <OptionIcon className="w-4.5 h-4.5" strokeWidth={2} />
                          </span>
                          <span className="min-w-0 flex-1">
                            <span className={`block text-[14px] font-semibold ${selected ? 'text-navy-800 dark:text-navy-200' : 'text-gray-900 dark:text-gray-100'}`}>{option.title}</span>
                            <span className="block text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{option.description}</span>
                          </span>
                          <span className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors ${selected ? 'border-navy-500 bg-navy-500' : 'border-gray-300 dark:border-gray-600'}`}>
                            {selected && <Check className="w-3 h-3 text-white" strokeWidth={3} />}
                          </span>
                        </button>

                        {/* Inline sub-content rendered directly below its own row, not after the whole list */}
                        {isConnectorsRow && expanded && (
                          <div className="px-4 pb-3.5 pt-0.5 bg-navy-50/50 dark:bg-navy-900/10 space-y-2.5 animate-[fadeIn_0.15s_ease-out]">
                            <div className="rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800 overflow-hidden bg-white dark:bg-gray-900">
                              {connectorSubOptions.map((sub) => {
                                const subSelected = creationMode === sub.mapsTo
                                const SubIcon = CREATION_METHOD_ICONS[sub.key] || Link2
                                return (
                                  <button
                                    key={sub.key}
                                    type="button"
                                    onClick={() => setCreationMode(sub.mapsTo)}
                                    aria-pressed={subSelected}
                                    className={`w-full flex items-center gap-3 text-left px-3 py-2.5 transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 focus-visible:ring-inset ${subSelected ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                                  >
                                    <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center transition-colors ${subSelected ? 'bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                                      <SubIcon className="w-4 h-4" strokeWidth={2} />
                                    </span>
                                    <span className="min-w-0 flex-1">
                                      <span className={`block text-[13px] font-semibold ${subSelected ? 'text-navy-800 dark:text-navy-200' : 'text-gray-900 dark:text-gray-100'}`}>{sub.title}</span>
                                      <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 truncate">{sub.description}</span>
                                    </span>
                                    <span className={`flex-shrink-0 w-4.5 h-4.5 rounded-full border-2 flex items-center justify-center transition-colors ${subSelected ? 'border-navy-500 bg-navy-500' : 'border-gray-300 dark:border-gray-600'}`}>
                                      {subSelected && <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />}
                                    </span>
                                  </button>
                                )
                              })}
                            </div>

                            {creationMode === 'bc_purchase_order' && (
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-white dark:bg-gray-900">
                                {bcLoading && <p className="text-[12px] text-gray-500 dark:text-gray-400">Checking Business Central connection...</p>}
                                {!bcLoading && bcStatus?.connected && <p className="text-[12px] font-medium text-navy-700 dark:text-navy-300">Business Central connected</p>}
                                {!bcLoading && !bcStatus?.connected && !bcLoadError && <p className="text-[12px] text-gray-500 dark:text-gray-400">Business Central not connected.</p>}
                                {!bcLoading && bcLoadError && <p className="text-[12px] text-gray-500 dark:text-gray-400">{bcLoadError}</p>}
                              </div>
                            )}

                            {creationMode === 'ns_purchase_order' && (
                              <div className="space-y-2.5">
                                {/* Record Type — the primary choice, and rendered first
                                    because everything downstream (which fields are
                                    detected, which review screen the request gets)
                                    follows from it. One search field covers both the
                                    record types already set up here and everything else
                                    in the account: whether a type happens to be
                                    registered yet is our own bookkeeping, not a
                                    decision to put in front of the user. */}
                                <NsRecordTypePicker
                                  entityTypes={nsEntityTypes}
                                  loading={!nsTypesReady}
                                  loadError={nsTypesError || 'No NetSuite record types are available.'}
                                  value={selectedNsEntityType}
                                  onChange={(key) => { setSelectedNsEntityType(key); setNsFieldSourceMode('upload') }}
                                  onRegistered={handleNsRecordTypeRegistered}
                                  jsonOption={onOpenJsonImport ? {
                                    label: 'Import a template JSON file',
                                    active: false,
                                    onSelect: () => { onClose(); onOpenJsonImport() },
                                  } : undefined}
                                />

                                <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2.5 bg-white dark:bg-gray-900">
                                  {nsLoading && <p className="text-[12px] text-gray-500 dark:text-gray-400">Checking NetSuite connection...</p>}
                                  {!nsLoading && nsStatus?.connected && <p className="text-[12px] font-medium text-navy-700 dark:text-navy-300">NetSuite connected</p>}
                                  {!nsLoading && !nsStatus?.connected && !nsLoadError && <p className="text-[12px] text-gray-500 dark:text-gray-400">NetSuite not connected.</p>}
                                  {!nsLoading && nsLoadError && <p className="text-[12px] text-gray-500 dark:text-gray-400">{nsLoadError}</p>}
                                </div>
                              </div>
                            )}
                          </div>
                        )}

                        {option.key === 'from_template' && expanded && (
                          <div className="px-4 pb-3.5 pt-0.5 bg-navy-50/50 dark:bg-navy-900/10 space-y-3 animate-[fadeIn_0.15s_ease-out]">
                            <div className="relative" ref={sourceTemplatePickerRef}>
                              <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1.5">Select existing template</label>
                              <div className={`relative rounded-lg border bg-white dark:bg-gray-800 transition-all duration-200 ${sourceTemplateDropdownOpen ? 'border-navy-500 ring-4 ring-navy-500/10 shadow-md' : 'border-gray-300 dark:border-gray-700'}`}>
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                                <input
                                  type="text"
                                  value={sourceTemplateDropdownOpen ? sourceTemplateSearch : (selectedSourceTemplate ? `${selectedSourceTemplate.name}${isAnalyzerReady(selectedSourceTemplate) ? ' (Analyzer Ready)' : ''}` : '')}
                                  onChange={(e) => { setSourceTemplateSearch(e.target.value); setSourceTemplateDropdownOpen(true) }}
                                  onFocus={() => { setSourceTemplateSearch(''); setSourceTemplateDropdownOpen(true) }}
                                  placeholder="Search templates by name..."
                                  className="w-full pl-9 pr-8 py-2.5 rounded-lg bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
                                />
                                {sourceTemplateId && !sourceTemplateDropdownOpen && (
                                  <button
                                    type="button"
                                    onClick={() => selectSourceTemplate('')}
                                    className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                                    aria-label="Clear selected template"
                                  >
                                    <X className="w-4 h-4" />
                                  </button>
                                )}
                              </div>

                              {sourceTemplateDropdownOpen && (
                                <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                                  {(() => {
                                    const q = sourceTemplateSearch.trim().toLowerCase()
                                    const results = existingTemplates
                                      .filter(t => !q || (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
                                      .slice(0, 8)
                                    if (results.length === 0) {
                                      return (
                                        <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                          {q ? `No templates match "${sourceTemplateSearch}"` : 'No templates available'}
                                        </div>
                                      )
                                    }
                                    return (
                                      <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                                        {results.map((t) => {
                                          const ready = isAnalyzerReady(t)
                                          const isSelected = String(sourceTemplateId) === String(t.id)
                                          return (
                                          <button
                                            key={t.id}
                                            type="button"
                                            onMouseDown={(e) => { e.preventDefault(); selectSourceTemplate(t.id) }}
                                            className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-colors duration-150 ${isSelected ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}
                                          >
                                            <span className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${isSelected ? 'bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300' : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'}`}>
                                              <FileStack className="w-4 h-4" strokeWidth={2} />
                                            </span>
                                            <div className="min-w-0 flex-1">
                                              <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t.name}</p>
                                              {t.description && <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 truncate">{t.description}</p>}
                                            </div>
                                            {ready ? (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-400 flex-shrink-0">
                                                <CheckCircle2 className="w-3 h-3" /> Analyzer Ready
                                              </span>
                                            ) : (
                                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 flex-shrink-0">
                                                <Wrench className="w-3 h-3" /> Build Needed
                                              </span>
                                            )}
                                          </button>
                                          )
                                        })}
                                      </div>
                                    )
                                  })()}
                                </div>
                              )}
                            </div>

                            {sourceTemplateId && (
                              <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 bg-white dark:bg-gray-900">
                                <div className="flex items-start gap-3">
                                  <Toggle checked={reuseSourceAnalyzer} onChange={() => setReuseSourceAnalyzer(prev => !prev)} disabled={!selectedSourceHasAnalyzer} />
                                  <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Reuse source analyzer</p>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Use the selected template's analyzer immediately without rebuilding.</p>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>

              <div className="mt-4 rounded-xl border border-gray-200 dark:border-gray-700 px-4 py-3">
                <div className="flex items-start gap-3">
                  <Toggle checked={allowReprocessing} onChange={() => setAllowReprocessing(prev => !prev)} />
                  <div>
                    <p className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">Allow reprocessing duplicates</p>
                    <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5">Allow the same document to be processed again when needed.</p>
                  </div>
                </div>
              </div>

              {!isAdmin && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Assign approver (optional)</label>
                  <select value={approverId} onChange={e => setApproverId(e.target.value)} disabled={usersLoading} className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm">
                    <option value="">{usersLoading ? 'Loading users…' : 'Select an approver'}</option>
                    {orgUsers
                      .filter(u => ['reviewer', 'template_manager', 'admin'].includes(u.role) && u.is_active !== false)
                      .map(u => <option key={u.id} value={u.id}>{u.username || u.email}</option>)}
                  </select>
                </div>
              )}
            </div>
          )}

          {isAdmin && currentStepKey === 'ns_setup' && (
            <div className="space-y-4 animate-[fadeIn_0.3s_ease-out]">
              <div className="rounded-xl border border-[var(--brand-accent)]/25 bg-[var(--brand-accent)]/10 px-4 py-3 text-navy-800 dark:text-navy-100 text-sm flex items-start gap-2">
                <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--brand-accent)]" />
                <p>Pick the NetSuite company this template targets, then fetch its fields and validation rules. Everything downstream — which fields are mandatory, which vendors/items/locations are valid — is scoped to this company.</p>
              </div>

              {nsLoading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 dark:text-gray-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking NetSuite connection…
                </div>
              )}
              {!nsLoading && nsLoadError && (
                <p className="text-sm text-gray-600 dark:text-gray-300">{nsLoadError}</p>
              )}
              {!nsLoading && !nsStatus?.connected && !nsLoadError && (
                <p className="text-sm text-gray-500 dark:text-gray-400">NetSuite not connected.</p>
              )}

              {!nsLoading && nsStatus?.connected && (
                <>
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0" />
                    <p className="text-[12px] font-medium text-gray-700 dark:text-gray-300">
                      NetSuite connected — {(nsStatus.environment || 'Sandbox')}{nsStatus.account_id ? ` • ${nsStatus.account_id}` : ''}
                    </p>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Environment
                        {nsScope?.environment_count > 0 && (
                          <span className="ml-1 font-normal text-gray-400">({nsScope.environment_count})</span>
                        )}
                      </label>
                      <select
                        value={nsEnvironmentId}
                        onChange={e => setNsEnvironmentId(e.target.value)}
                        disabled={nsScope?.environment_locked}
                        className="w-full px-2.5 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-navy-400 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {(nsScope?.environments || []).map(env => (
                          <option key={env.id} value={env.id}>
                            {env.name}{env.account_id ? ` — ${env.account_id}` : ''}
                          </option>
                        ))}
                      </select>
                      {nsScope?.environment_locked && (
                        <p className="text-[11px] text-gray-400 mt-1">Set by the configured NetSuite credentials.</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
                        Company <span className="text-red-500">*</span>
                        {nsScope?.company_count > 0 && (
                          <span className="ml-1 font-normal text-gray-400">({nsScope.company_count})</span>
                        )}
                      </label>
                      <select
                        value={nsCompanyId}
                        onChange={e => { setNsCompanyId(e.target.value); setNsDetectResult(null) }}
                        disabled={!(nsScope?.companies || []).length}
                        className="w-full px-2.5 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-1 focus:ring-navy-400 disabled:opacity-70"
                      >
                        <option value="">Select a company…</option>
                        {(nsScope?.companies || []).map(c => (
                          <option key={c.id} value={String(c.id)}>{c.name}</option>
                        ))}
                      </select>
                      {nsScope?.companies_error && (
                        <p className="text-[11px] text-amber-600 dark:text-amber-400 mt-1">{nsScope.companies_error}</p>
                      )}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={fetchNsData}
                    disabled={!nsCompanyId || nsDetecting}
                    className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-navy-800 text-white text-sm font-semibold shadow-sm hover:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {nsDetecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                    {nsDetecting ? 'Fetching…' : 'Fetch data & validations'}
                  </button>
                  {!nsCompanyId && (
                    <p className="text-xs text-gray-400 -mt-2">Select a company to enable fetching.</p>
                  )}

                  {nsDetectResult?.error && (
                    <p className="text-sm text-red-600 dark:text-red-400">{nsDetectResult.error}</p>
                  )}

                  {nsDetectResult && !nsDetectResult.error && (
                    <div className="rounded-lg border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/10 px-3 py-2.5 flex items-start gap-2">
                      <CheckCircle2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400 mt-0.5 flex-shrink-0" />
                      <p className="text-[12px] text-emerald-800 dark:text-emerald-300">
                        Fetched {(nsDetectResult.available_fields || []).length} available field{(nsDetectResult.available_fields || []).length !== 1 ? 's' : ''}
                        {' '}({(nsDetectResult.mandatory_fields || []).length} mandatory) for {selectedNsEntityLabel}.
                        {' '}Continue to upload a sample document.
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {isAdmin && currentStepKey === 'sample_documents' && (
            <div className="space-y-4 animate-[fadeIn_0.3s_ease-out]">
              {creationMode === 'blank' ? (
                <>
                  <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 p-1 bg-white dark:bg-gray-800">
                    <button
                      type="button"
                      onClick={() => {
                        setSampleInputMode('ai_sample_docs')
                        setBlankProcessedSignature('')
                      }}
                      className={`px-3 py-1.5 text-xs rounded-md ${sampleInputMode === 'ai_sample_docs' ? 'bg-navy-800 text-white' : 'text-gray-600 dark:text-gray-300'}`}
                    >
                      Sample documents
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setSampleInputMode('json_fields_import')
                        setBlankProcessedSignature('')
                      }}
                      className={`px-3 py-1.5 text-xs rounded-md ${sampleInputMode === 'json_fields_import' ? 'bg-navy-800 text-white' : 'text-gray-600 dark:text-gray-300'}`}
                    >
                      Import JSON fields
                    </button>
                  </div>

                  {sampleInputMode === 'ai_sample_docs' ? (
                    <div className="rounded-2xl border border-navy-200 dark:border-navy-800 bg-gradient-to-br from-navy-50 to-white dark:from-navy-900/20 dark:to-gray-900 p-5 space-y-4 animate-[fadeIn_0.25s_ease-out]">
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 h-9 w-9 rounded-lg bg-navy-800 shadow-sm flex items-center justify-center transition-transform duration-300 hover:scale-105">
                          <Sparkles className="h-4.5 w-4.5 text-white" />
                        </div>
                        <div>
                          <p className="text-base font-semibold text-gray-900 dark:text-gray-100">AI Field Suggestion</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400">Upload sample PDFs and let AI generate template fields</p>
                        </div>
                      </div>

                      <div className="rounded-xl border border-[var(--brand-accent)]/25 bg-[var(--brand-accent)]/10 px-4 py-3 text-navy-800 dark:text-navy-100 text-sm flex items-start gap-2">
                        <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--brand-accent)]" />
                        <p>
                          Upload up to <strong>10 documents of the same type</strong>. The AI will analyze them together to discover the most comprehensive set of extractable fields.
                        </p>
                      </div>

                      <label className="group block rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 p-8 text-center cursor-pointer bg-white/70 dark:bg-gray-900/40 hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                        <UploadCloud className="h-9 w-9 mx-auto text-gray-400 group-hover:text-[var(--brand-accent)] group-hover:-translate-y-0.5 transition-all duration-200" />
                        <p className="mt-3 text-base font-medium text-gray-700 dark:text-gray-200">Drop sample PDFs here or <span className="text-[var(--brand-accent)] font-semibold">browse</span></p>
                        <p className="text-xs text-gray-400 mt-1.5">Up to 10 PDFs · the AI will analyze them and suggest extractable fields</p>
                        <input type="file" multiple className="hidden" accept=".pdf" onChange={handleSampleDocsSelected} />
                      </label>

                      <div>
                        <label className="block text-sm font-medium text-gray-600 dark:text-gray-300 mb-2">Optional: Tell the AI what fields you're looking for</label>
                        <textarea
                          rows={2}
                          value={blankGuidancePrompt}
                          onChange={(e) => {
                            setBlankGuidancePrompt(e.target.value)
                            setBlankProcessedSignature('')
                          }}
                          placeholder="e.g. order number, order date, delivery address and the product lines"
                          className="w-full px-3.5 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-[14px] resize-none focus:outline-none focus:ring-2 focus:ring-navy-500 focus:border-navy-500"
                        />
                        <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
                          <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300 cursor-pointer">
                            <input
                              type="checkbox"
                              checked={blankRestrict}
                              onChange={(e) => {
                                setBlankRestrict(e.target.checked)
                                setBlankProcessedSignature('')
                              }}
                              className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500"
                            />
                            Only the fields I list
                          </label>
                          <label className="inline-flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
                            Detail
                            <select
                              value={blankDetailLevel}
                              disabled={blankRestrict}
                              onChange={(e) => {
                                setBlankDetailLevel(e.target.value)
                                setBlankProcessedSignature('')
                              }}
                              className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm disabled:opacity-50"
                            >
                              <option value="essentials">Essentials</option>
                              <option value="standard">Standard</option>
                              <option value="everything">Everything</option>
                            </select>
                          </label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <label className="group block rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 p-8 text-center cursor-pointer hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/5 hover:shadow-md hover:-translate-y-0.5 transition-all duration-200">
                      <FileText className="h-8 w-8 mx-auto text-gray-400 group-hover:text-[var(--brand-accent)] group-hover:-translate-y-0.5 transition-all duration-200" />
                      <p className="mt-3 text-sm font-semibold text-gray-900 dark:text-gray-100">Upload JSON file to generate fields</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Accepts structured .json and auto-creates extraction fields</p>
                      <input
                        type="file"
                        className="hidden"
                        accept=".json,application/json"
                        onChange={(e) => {
                          setJsonImportFile((e.target.files || [])[0] || null)
                          setBlankProcessedSignature('')
                        }}
                      />
                    </label>
                  )}

                  {sampleDocuments.length > 0 && sampleInputMode === 'ai_sample_docs' && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 animate-[fadeIn_0.2s_ease-out]">
                      <div className="px-3 py-2 text-xs font-semibold text-gray-700 dark:text-gray-300">{sampleDocuments.length} of 10 documents ready</div>
                      <div className="divide-y divide-gray-100 dark:divide-gray-800">
                        {sampleDocuments.map((file, idx) => (
                          <div key={`${file.name}-${idx}`} className="px-3 py-2 flex items-center gap-3 transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/40">
                            <span className="text-sm text-gray-700 dark:text-gray-200 truncate flex-1">{file.name}</span>
                            <button
                              type="button"
                              onClick={() => removeSampleDocument(idx)}
                              className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-all duration-150 hover:scale-110"
                              aria-label={`Remove ${file.name}`}
                              title="Remove file"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {jsonImportFile && sampleInputMode === 'json_fields_import' && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 flex items-center gap-3">
                      <p className="text-xs text-gray-600 dark:text-gray-300 flex-1 truncate">Selected JSON: <strong>{jsonImportFile.name}</strong></p>
                      <button
                        type="button"
                        onClick={() => {
                          setJsonImportFile(null)
                          setBlankProcessedSignature('')
                        }}
                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 dark:hover:text-gray-200 transition-colors"
                        aria-label="Remove selected JSON file"
                        title="Remove JSON file"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}

                  {blankDetectError && (
                    <p className="text-sm text-red-600 dark:text-red-400 animate-[fadeIn_0.2s_ease-out]">{blankDetectError}</p>
                  )}

                  {blankPreviewSource && (
                    <p className="text-xs font-medium text-navy-700 dark:text-navy-400 flex items-center gap-1.5 animate-[fadeIn_0.25s_ease-out]">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      Processed successfully: {blankPreviewSource}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={processBlankSampleInput}
                    disabled={blankDetecting || (sampleInputMode === 'ai_sample_docs' ? sampleDocuments.length === 0 : !jsonImportFile)}
                    className="group w-full h-12 rounded-xl bg-navy-800 text-white font-semibold text-sm shadow-sm hover:bg-[var(--brand-accent)] hover:shadow-lg hover:shadow-[var(--brand-accent)]/30 hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-sm disabled:hover:translate-y-0 disabled:active:scale-100 inline-flex items-center justify-center gap-2 transition-all duration-200"
                  >
                    {blankDetecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12 group-hover:scale-110" />}
                    {blankDetecting ? 'Processing...' : (sampleInputMode === 'ai_sample_docs' ? 'Suggest Fields' : 'Process Fields')}
                  </button>
                </>
              ) : creationMode === 'ns_purchase_order' ? (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[var(--brand-accent)]/25 bg-[var(--brand-accent)]/10 px-4 py-3 text-navy-800 dark:text-navy-100 text-sm flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--brand-accent)]" />
                    <p>
                      <>Upload a <strong>NetSuite {selectedNsEntityLabel} PDF</strong> (or similar document) — AI will extract fields and automatically map them to NetSuite field keys. Mapped fields will be pre-selected for you to review on the next step.</>
                    </p>
                  </div>

                  {nsFieldSourceMode === 'upload' && !nsStatus?.connected && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">NetSuite not connected — go back to the NetSuite setup step.</p>
                  )}
                  {nsFieldSourceMode === 'upload' && nsStatus?.connected && !nsDetectResult && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Go back to the NetSuite setup step and fetch data & validations first.</p>
                  )}

                  {nsFieldSourceMode === 'upload' && nsStatus?.connected && nsDetectResult && !nsDetectResult.error && (
                    <label className={`group block rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200 ${nsUploading ? 'border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-70' : 'border-gray-300 dark:border-gray-700 cursor-pointer hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/5 hover:shadow-md hover:-translate-y-0.5'}`}>
                      {nsUploading ? (
                        <Loader2 className="h-9 w-9 mx-auto text-[var(--brand-accent)] animate-spin" />
                      ) : (
                        <UploadCloud className="h-9 w-9 mx-auto text-gray-400 group-hover:text-[var(--brand-accent)] group-hover:-translate-y-0.5 transition-all duration-200" />
                      )}
                      <p className="mt-3 text-base font-medium text-gray-700 dark:text-gray-200">
                        {nsUploading ? 'Mapping NetSuite fields…' : (<>Drop your <span className="text-[var(--brand-accent)] font-semibold">NetSuite PDF</span> here or <span className="text-[var(--brand-accent)] font-semibold">browse</span></>)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1.5">Upload a NetSuite {selectedNsEntityLabel} PDF — AI will map fields to NS keys automatically</p>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        disabled={nsUploading}
                        onChange={async (e) => {
                          const file = (e.target.files || [])[0]
                          e.target.value = ''
                          if (!file) return
                          setNsUploading(true)
                          setNsUploadError('')
                          setNsMappingResult(null)
                          try {
                            const formData = new FormData()
                            formData.append('file', file)
                            // Without this the backend's _resolve_ns_entity_type()
                            // has neither a template (none exists yet, mid-creation)
                            // nor an explicit type to go on, so it defaults to
                            // 'purchase_order' — mapping a Vendor Bid sample PDF
                            // against PO's field catalog (entity/tranDate/subsidiary)
                            // instead of Bid's own custrecord_jcs_sp_* keys, which
                            // never overlap and always produces "No fields mapped".
                            formData.append('ns_entity_type', selectedNsEntityType)
                            const res = await nsApi.extractFromDocument(formData)
                            const data = res.data || {}
                            const mappings = data.mappings || []
                            const isBid = selectedNsEntityType === 'vendor_bid'
                            const availableFields = nsDetectResult?.available_fields || []
                            const availableKeys = new Set(availableFields.map((f) => f.key))
                            // A Special Pricing bid builds its rows from the shared
                            // bid helper (line columns collapse into one line-items
                            // table, not-a-field labels are dropped); anything else
                            // is one flat row per mapping as before.
                            const mappedRows = isBid
                              ? nsBidRowsFromMappings(mappings)
                              : mappings.map((m, idx) => nsRowFromMapping(m, idx))
                            const mappedKeys = []
                            mappedRows.forEach((row) => {
                              const key = row.ns_field
                              // The bid's own catalog is authoritative for its keys:
                              // when NetSuite's live field list is unavailable the
                              // static layout stands in, and filtering against an
                              // empty availableKeys would silently discard every
                              // correctly-mapped field.
                              if (key && (availableKeys.has(key) || isBid)) mappedKeys.push(key)
                            })
                            if (mappedKeys.length > 0) {
                              setNsCheckedFields((prev) => {
                                const next = { ...prev }
                                mappedKeys.forEach((k) => { next[k] = true })
                                return next
                              })
                            }
                            setNsMappingResult({
                              mappings,
                              mappedKeys,
                              fileName: file.name,
                              excludedLabels: isBid ? excludedBidLabels(mappings) : [],
                              catalogSource: data.catalog_source || null,
                              aiError: data.ai_error || null,
                            })
                            setNsEditableFields(mappedRows)
                            setNsSelectedFieldKeys(new Set(mappedRows.map((f) => f.key)))
                            if (data.ai_error && mappedKeys.length > 0) {
                              toast.warning(
                                'Mapped from the known Samsung layout',
                                `The AI step was unavailable (${data.ai_error}) — ${mappedKeys.length} field(s) were mapped from the known Special Pricing layout. Review them on the next step.`,
                              )
                            } else if (mappedKeys.length > 0) {
                              toast.success('Fields mapped', `Mapped ${mappedKeys.length} field(s) from ${file.name}. Review them on the next step.`)
                            } else {
                              toast.warning('No fields mapped', 'The AI could not confidently map any fields from this document. Use "Start with the standard fields instead" to continue.')
                            }
                          } catch (err) {
                            setNsUploadError(err?.response?.data?.error || 'Failed to map fields from the uploaded document.')
                          } finally {
                            setNsUploading(false)
                          }
                        }}
                      />
                    </label>
                  )}

                  {nsFieldSourceMode === 'upload' && nsUploadError && (
                    <p className="text-sm text-red-600 dark:text-red-400 animate-[fadeIn_0.2s_ease-out]">{nsUploadError}</p>
                  )}

                  {nsFieldSourceMode === 'upload' && nsMappingResult && (
                    <div className="space-y-1.5 animate-[fadeIn_0.2s_ease-out]">
                      <p className="text-xs font-medium text-navy-700 dark:text-navy-400 flex items-center gap-1.5">
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        {nsMappingResult.mappedKeys?.length > 0
                          ? `Mapped ${nsMappingResult.mappedKeys.length} field(s) from ${nsMappingResult.fileName}`
                          : `No fields could be mapped from ${nsMappingResult.fileName}`}
                      </p>
                      {/* Say which source produced the mapping rather than
                          implying the AI did it when it was unavailable. */}
                      {nsMappingResult.aiError && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                          <span>Mapped from the known Special Pricing layout — the AI step was unavailable ({nsMappingResult.aiError})</span>
                        </p>
                      )}
                      {nsMappingResult.catalogSource === 'known_layout' && (
                        <p className="text-xs text-amber-700 dark:text-amber-400 flex items-start gap-1.5">
                          <AlertTriangle className="h-3.5 w-3.5 mt-px flex-shrink-0" />
                          <span>NetSuite's own field list could not be read, so the known Special Pricing layout was used. Account-specific custom fields are not listed.</span>
                        </p>
                      )}
                      {/* Shown, not silently dropped: the user can see the
                          Distributor / Competitor rows WERE read and skipped
                          on purpose, rather than wondering if they were missed. */}
                      {nsMappingResult.excludedLabels?.length > 0 && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          Read but not mapped (not Special Pricing fields):{' '}
                          {nsMappingResult.excludedLabels.map((e) => e.label).filter(Boolean).join(', ')}
                        </p>
                      )}
                    </div>
                  )}

                  {nsFieldSourceMode === 'upload' && nsEditableFields.length === 0 && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                      <AlertTriangle className="h-3.5 w-3.5" />
                      This step is required — upload a NetSuite {selectedNsEntityLabel} PDF to continue.
                    </p>
                  )}

                </div>
              ) : creationMode === 'bc_purchase_order' ? (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[var(--brand-accent)]/25 bg-[var(--brand-accent)]/10 px-4 py-3 text-navy-800 dark:text-navy-100 text-sm flex items-start gap-2">
                    <Info className="h-4 w-4 mt-0.5 flex-shrink-0 text-[var(--brand-accent)]" />
                    <p>Upload a <strong>Business Central Purchase Order PDF</strong> (or similar PO document) — this step is required. AI will extract fields and automatically map them to BC PO field keys (e.g. vendorNumber, orderDate). Mapped fields will be pre-selected for you to review on the next step.</p>
                  </div>

                  {(bcEntityTypes.length > 0 || bcConnections.length > 0) && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {bcEntityTypes.length > 0 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Document Type</label>
                          <select
                            value={selectedBcEntityType}
                            onChange={(e) => {
                              setSelectedBcEntityType(e.target.value)
                              // A different entity type has a different field set entirely —
                              // any previously-mapped fields belong to the old type.
                              setBcMappingResult(null)
                              setBcEditableFields([])
                            }}
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 transition-colors"
                          >
                            {bcEntityTypes.map((t) => (
                              <option key={t.bc_entity_type_key} value={t.bc_entity_type_key}>{t.display_name}</option>
                            ))}
                          </select>
                        </div>
                      )}
                      {bcConnections.length > 1 && (
                        <div>
                          <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1.5">Connection</label>
                          <select
                            value={selectedBcConnectionId}
                            onChange={(e) => setSelectedBcConnectionId(e.target.value)}
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 transition-colors"
                          >
                            {bcConnections.map((c) => (
                              <option key={c.bc_connection_id} value={String(c.bc_connection_id)}>
                                {c.connection_label} — {c.bc_environment_name} · {c.company_name}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  )}

                  {bcLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Checking Business Central connection...</p>}
                  {!bcLoading && bcLoadError && (
                    <p className="text-sm text-gray-600 dark:text-gray-300">{bcLoadError}</p>
                  )}
                  {!bcLoading && !bcStatus?.connected && !bcLoadError && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">Business Central not connected.</p>
                  )}

                  {!bcLoading && bcStatus?.connected && (
                    <label className={`group block rounded-xl border-2 border-dashed p-8 text-center transition-all duration-200 ${bcUploading ? 'border-gray-200 dark:border-gray-700 cursor-not-allowed opacity-70' : 'border-gray-300 dark:border-gray-700 cursor-pointer hover:border-[var(--brand-accent)] hover:bg-[var(--brand-accent)]/5 hover:shadow-md hover:-translate-y-0.5'}`}>
                      {bcUploading ? (
                        <Loader2 className="h-9 w-9 mx-auto text-[var(--brand-accent)] animate-spin" />
                      ) : (
                        <UploadCloud className="h-9 w-9 mx-auto text-gray-400 group-hover:text-[var(--brand-accent)] group-hover:-translate-y-0.5 transition-all duration-200" />
                      )}
                      <p className="mt-3 text-base font-medium text-gray-700 dark:text-gray-200">
                        {bcUploading ? 'Mapping Business Central fields…' : (<>Drop your <span className="text-[var(--brand-accent)] font-semibold">Business Central PDF</span> here or <span className="text-[var(--brand-accent)] font-semibold">browse</span></>)}
                      </p>
                      <p className="text-xs text-gray-400 mt-1.5">Upload a Business Central {selectedBcEntityLabel} PDF — AI will map fields to BC keys automatically</p>
                      <input
                        type="file"
                        accept=".pdf"
                        className="hidden"
                        disabled={bcUploading}
                        onChange={async (e) => {
                          const file = (e.target.files || [])[0]
                          e.target.value = ''
                          if (!file) return
                          setBcUploading(true)
                          setBcUploadError('')
                          setBcMappingResult(null)
                          try {
                            const formData = new FormData()
                            formData.append('file', file)
                            if (selectedBcEntityType) formData.append('entity_type', selectedBcEntityType)
                            if (selectedBcConnectionId) formData.append('connection_id', selectedBcConnectionId)
                            const res = await bcApi.extractFromDocument(formData)
                            const data = res.data || {}
                            const mappings = data.mappings || []
                            const availableFields = data.available_fields || bcDetectResult?.available_fields || []
                            const availableKeys = new Set(availableFields.map((f) => f.key))
                            const mappedKeys = []
                            mappings.forEach((m) => {
                              // Deterministic field-plan mode reports value_found instead of a
                              // suggested_bc_key lookup (the mapping is already fixed by the
                              // entity's field catalog) — count either signal as "mapped".
                              const key = m.suggested_bc_key
                              if (key && availableKeys.has(key)) mappedKeys.push(key)
                              else if (data.deterministic_mapping && m.value_found) mappedKeys.push(m.field_name || key)
                            })
                            if (mappedKeys.length > 0) {
                              setBcCheckedFields((prev) => {
                                const next = { ...prev }
                                mappedKeys.forEach((k) => { next[k] = true })
                                return next
                              })
                            }
                            setBcMappingResult({ mappings, mappedKeys, fileName: file.name })
                            setBcEditableFields(mappings.map((m, idx) => bcRowFromMapping(m, idx)))
                            if (mappedKeys.length > 0) {
                              toast.success('Fields mapped', `Mapped ${mappedKeys.length} field(s) from ${file.name}. Review them on the next step.`)
                            } else {
                              toast.warning('No fields mapped', 'The AI could not confidently map any fields from this document. You can still select fields manually on the next step.')
                            }
                          } catch (err) {
                            setBcUploadError(err?.response?.data?.error || 'Failed to map fields from the uploaded document.')
                          } finally {
                            setBcUploading(false)
                          }
                        }}
                      />
                    </label>
                  )}

                  {bcUploadError && (
                    <p className="text-sm text-red-600 dark:text-red-400 animate-[fadeIn_0.2s_ease-out]">{bcUploadError}</p>
                  )}

                  {bcMappingResult && (
                    <p className="text-xs font-medium text-navy-700 dark:text-navy-400 flex items-center gap-1.5 animate-[fadeIn_0.2s_ease-out]">
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {bcMappingResult.mappedKeys?.length > 0
                        ? `Mapped ${bcMappingResult.mappedKeys.length} field(s) from ${bcMappingResult.fileName}`
                        : `No fields could be mapped from ${bcMappingResult.fileName}`}
                    </p>
                  )}

                  {bcEditableFields.length === 0 && (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                        <AlertTriangle className="h-3.5 w-3.5" />
                        Upload a Business Central {selectedBcEntityLabel} PDF to continue, or start from the standard fields.
                      </p>
                      <button
                        type="button"
                        onClick={addStandardBcFields}
                        className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                      >
                        <Sparkles className="h-3.5 w-3.5" />
                        Start with the standard fields instead
                      </button>
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
                  This creation method does not require sample input in this step.
                </div>
              )}
            </div>
          )}

          {((isAdmin && currentStepKey === 'configure_fields') || !isAdmin) && (
            <div className="space-y-4 animate-[fadeIn_0.3s_ease-out]">
              {creationMode === 'from_template' && (
                <>
                  <div className="relative" ref={sourceTemplatePickerRef}>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Source template</label>
                    <div className={`relative rounded-lg border bg-white dark:bg-gray-800 transition-all duration-200 ${sourceTemplateDropdownOpen ? 'border-navy-500 ring-4 ring-navy-500/10 shadow-md' : 'border-gray-300 dark:border-gray-700'}`}>
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={sourceTemplateDropdownOpen ? sourceTemplateSearch : (selectedSourceTemplate ? `${selectedSourceTemplate.name}${isAnalyzerReady(selectedSourceTemplate) ? ' (Analyzer Ready)' : ''}` : '')}
                        onChange={(e) => { setSourceTemplateSearch(e.target.value); setSourceTemplateDropdownOpen(true) }}
                        onFocus={() => { setSourceTemplateSearch(''); setSourceTemplateDropdownOpen(true) }}
                        placeholder="Search templates by name..."
                        className="w-full pl-9 pr-8 py-2.5 rounded-lg bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
                      />
                      {sourceTemplateId && !sourceTemplateDropdownOpen && (
                        <button
                          type="button"
                          onClick={() => selectSourceTemplate('')}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                          aria-label="Clear selected template"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    {sourceTemplateDropdownOpen && (
                      <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                        {(() => {
                          const q = sourceTemplateSearch.trim().toLowerCase()
                          const results = existingTemplates
                            .filter(t => !q || (t.name || '').toLowerCase().includes(q) || (t.description || '').toLowerCase().includes(q))
                            .slice(0, 8)
                          if (results.length === 0) {
                            return (
                              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                {q ? `No templates match "${sourceTemplateSearch}"` : 'No templates available'}
                              </div>
                            )
                          }
                          return (
                            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                              {results.map((t) => (
                                <button
                                  key={t.id}
                                  type="button"
                                  onMouseDown={(e) => { e.preventDefault(); selectSourceTemplate(t.id) }}
                                  className={`w-full flex items-center justify-between gap-3 px-4 py-2.5 text-left hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-150 ${String(sourceTemplateId) === String(t.id) ? 'bg-navy-50 dark:bg-navy-900/20' : ''}`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{t.name}</p>
                                    {t.description && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{t.description}</p>}
                                  </div>
                                  {isAnalyzerReady(t) && (
                                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-lg text-[11px] font-medium bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-400 flex-shrink-0">
                                      <CheckCircle2 className="w-3 h-3" /> Analyzer Ready
                                    </span>
                                  )}
                                </button>
                              ))}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>
                  {sourceTemplateId && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                      <div className="flex items-start gap-3">
                        <Toggle checked={reuseSourceAnalyzer} onChange={() => setReuseSourceAnalyzer(prev => !prev)} disabled={!selectedSourceHasAnalyzer} />
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">Reuse source analyzer</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Use the selected template's analyzer immediately without rebuilding.</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {sourceTemplateId && loadingSourceTemplateFields && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      Loading fields…
                    </div>
                  )}
                  {sourceTemplateId && !loadingSourceTemplateFields && !selectedSourceTemplate?.fields?.length && (
                    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/30 px-4 py-3 text-sm text-gray-500 dark:text-gray-400">
                      This template has no configured fields yet.
                    </div>
                  )}

                  {sourceTemplateId && !!selectedSourceTemplate?.fields?.length && (() => {
                    const query = blankFieldSearch.trim().toLowerCase()
                    const filteredFields = query
                      ? blankEditableFields.filter((f) =>
                          (f.label || f.key).toLowerCase().includes(query) ||
                          f.key.toLowerCase().includes(query) ||
                          (f.description || '').toLowerCase().includes(query)
                        )
                      : blankEditableFields

                    const filteredFieldKeys = filteredFields.map((f) => f.key)
                    const selectedVisibleCount = filteredFieldKeys.filter((key) => blankSelectedFieldKeys.has(key)).length
                    const allVisibleSelected = filteredFieldKeys.length > 0 && selectedVisibleCount === filteredFieldKeys.length

                    return (
                      <div className="space-y-2">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300">
                          Fields from "{selectedSourceTemplate?.name}" ({blankEditableFields.length})
                        </p>

                        <div className="flex items-center justify-between gap-3">
                          <div className="relative flex-1 max-w-[420px]">
                            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={blankFieldSearch}
                              onChange={(e) => setBlankFieldSearch(e.target.value)}
                              placeholder="Search fields"
                              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-8 pr-3 py-2 text-[14px] text-gray-700 dark:text-gray-200"
                            />
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {blankSelectedFieldKeys.size > 0 && (
                              <button
                                type="button"
                                onClick={deleteSelectedBlankFields}
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-red-200 dark:border-red-800 text-[14px] font-medium leading-none text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="text-[14px]">Delete ({blankSelectedFieldKeys.size})</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={addBlankField}
                              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-gray-200 dark:border-gray-700 text-[14px] font-medium leading-none text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <Plus className="h-4 w-4" />
                              <span className="text-[14px]">Add field</span>
                            </button>
                            <button
                              type="button"
                              onClick={saveBlankFieldChanges}
                              disabled={!blankFieldsDirty}
                              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-navy-800 text-white text-[14px] font-semibold hover:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-[14px]">Save Changes ({blankEditableFields.length})</span>
                            </button>
                          </div>
                        </div>

                        {filteredFields.length > 0 ? (
                          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                            <div className="grid grid-cols-[44px_1.2fr_1.2fr_0.7fr_90px_2.2fr_86px] gap-4 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                              <span className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={allVisibleSelected}
                                  onChange={(e) => setBlankFieldSelectionForKeys(filteredFieldKeys, e.target.checked)}
                                  className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500"
                                  aria-label="Select all fields"
                                />
                              </span>
                              <span>Display</span>
                              <span>Field Name</span>
                              <span>Type</span>
                              <span>Required</span>
                              <span>Description</span>
                              <span className="text-right">Actions</span>
                            </div>
                            <div className="max-h-[calc(100vh-28rem)] min-h-[12rem] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-700">
                              {filteredFields.map((f) => {
                                const isRowSelected = blankSelectedFieldKeys.has(f.key)
                                return (
                                  <div key={f.key} className="group grid grid-cols-[44px_1.2fr_1.2fr_0.7fr_90px_2.2fr_86px] gap-4 px-4 py-3 text-[14px] hover:bg-gray-50 dark:hover:bg-gray-700/60">
                                    <span className="flex items-center">
                                      <input
                                        type="checkbox"
                                        checked={isRowSelected}
                                        onChange={() => toggleBlankFieldSelection(f.key)}
                                        className={`h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500 transition-opacity ${isRowSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                                      />
                                    </span>
                                    <span className="text-gray-800 dark:text-gray-200 flex items-center truncate whitespace-nowrap">
                                      {f.label || f.key}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300 font-mono flex items-center truncate whitespace-nowrap">
                                      {f.key}
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center">
                                      {f.type || 'text'}
                                    </span>
                                    <span className="flex items-center">
                                      <Toggle checked={!!f.required} onChange={() => updateBlankField(f.key, { required: !f.required })} />
                                    </span>
                                    <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center">
                                      {f.description || '-'}
                                    </span>
                                    <span className="flex items-center gap-1 justify-end">
                                      <button
                                        type="button"
                                        onClick={() => openEditBlankFieldModal(f)}
                                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        title="Edit field"
                                      >
                                        <Pencil className="h-3.5 w-3.5" />
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() => deleteBlankField(f.key)}
                                        className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                        title="Delete field"
                                      >
                                        <Trash2 className="h-3.5 w-3.5" />
                                      </button>
                                    </span>
                                  </div>
                                )
                              })}
                            </div>
                          </div>
                        ) : (
                          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                            <div className="px-3 py-6 text-center text-[14px] text-gray-500 dark:text-gray-400">No fields match your search.</div>
                          </div>
                        )}

                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          These fields will be copied to your new template. Add, edit, or remove fields now, or continue and refine them later.
                        </p>
                      </div>
                    )
                  })()}
                </>
              )}

              {creationMode === 'bc_purchase_order' && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[#7da5bf] dark:border-navy-700 bg-[#edf7fc] dark:bg-navy-900/20 p-4">
                    <div className="flex items-start gap-3">
                      <div className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#3e6170] text-[#3e6170] dark:border-navy-300 dark:text-navy-300 mt-0.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#3e6170] dark:bg-navy-300" />
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">BC Create from Business Central Purchase Order</p>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 max-w-2xl">Review the fields AI mapped from your uploaded PDF. Ready for review after creation.</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#b6d7e8] dark:border-navy-800 bg-[#f4fbff] dark:bg-navy-900/10 p-4 space-y-3">
                    {bcLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Checking Business Central connection...</p>}

                    {!bcLoading && bcLoadError && (
                      <p className="text-sm text-gray-600 dark:text-gray-300">{bcLoadError}</p>
                    )}

                    {!bcLoading && bcStatus?.connected && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4.5 w-4.5 text-[#3c7185] dark:text-navy-300" />
                          <p className="text-[12px] font-semibold text-[#2f5f73] dark:text-navy-300">Business Central connected</p>
                        </div>
                        <p className="pl-6 text-[12px] text-gray-500 dark:text-gray-400">
                          {(bcStatus.environment || 'Sandbox')}
                          {bcStatus.company ? ` • ${bcStatus.company}` : ''}
                        </p>
                      </div>
                    )}

                    {!bcLoading && !bcStatus?.connected && !bcLoadError && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">Business Central not connected.</p>
                    )}

                    {!bcLoading && bcStatus?.connected && (() => {
                      const availableFields = bcDetectResult?.available_fields || []
                      const query = bcFieldSearch.trim().toLowerCase()
                      const filteredRows = query
                        ? bcEditableFields.filter((f) =>
                            (f.label || '').toLowerCase().includes(query) ||
                            (f.key || '').toLowerCase().includes(query) ||
                            (f.description || '').toLowerCase().includes(query)
                          )
                        : bcEditableFields

                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative flex-1 max-w-[360px]">
                              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                              <input
                                type="text"
                                value={bcFieldSearch}
                                onChange={(e) => setBcFieldSearch(e.target.value)}
                                placeholder="Search fields"
                                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-8 pr-3 py-2 text-xs text-gray-700 dark:text-gray-200"
                              />
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                              <button
                                type="button"
                                onClick={addStandardBcFields}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                Add Standard BC PO Fields
                              </button>
                              <button
                                type="button"
                                onClick={addBlankBcField}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Add field
                              </button>
                            </div>
                          </div>

                          {bcEditableFields.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-[#bfd8e4] dark:border-navy-700 bg-[#ebf6fb] dark:bg-navy-900/20 p-5 text-center">
                              <Sparkles className="h-6 w-6 mx-auto text-[#4e7e93] dark:text-navy-300 mb-1.5" />
                              <p className="text-[13px] font-semibold text-[#2f5f73] dark:text-navy-300">No Business Central PO fields yet</p>
                              <p className="text-[12px] text-[#3f6576] dark:text-navy-200/90 mt-0.5 max-w-md mx-auto">
                                Go back to upload a Business Central Purchase Order PDF so AI can map fields automatically, or add the standard BC PO fields to get started.
                              </p>
                              <button
                                type="button"
                                onClick={addStandardBcFields}
                                className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--brand-accent)] text-white text-xs font-semibold hover:opacity-90"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                Add Standard BC PO Fields
                              </button>
                            </div>
                          ) : (
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                              <div className="grid grid-cols-[1.2fr_1.2fr_0.7fr_90px_1.8fr_90px_1.6fr_86px] gap-3 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                <span>Display</span>
                                <span>Field Name</span>
                                <span>Type</span>
                                <span className="text-center">Required</span>
                                <span>Description</span>
                                <span className="text-center">User Input</span>
                                <span className="text-navy-600 dark:text-navy-400">BC Mapping</span>
                                <span className="text-right">Actions</span>
                              </div>
                              {/* No inner max-h/overflow here — the field list scrolls
                                  with the modal body (its own overflow-y-auto), not in
                                  a separate boxed-in scroll area that only shows a
                                  handful of rows at a time. */}
                              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filteredRows.map((f) => {
                                  const isEditing = bcEditingRowKey === f.key
                                  const bcMeta = availableFields.find((af) => af.key === f.bc_field)
                                  const isBcMandatory = !!bcMeta?.mandatory || BC_MANDATORY_KEYS.includes(f.bc_field)
                                  return (
                                    <div key={f.key} className="group grid grid-cols-[1.2fr_1.2fr_0.7fr_90px_1.8fr_90px_1.6fr_86px] gap-3 px-4 py-3 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-700/60">
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={f.label}
                                          onChange={(e) => updateBcField(f.key, { label: e.target.value })}
                                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200"
                                        />
                                      ) : (
                                        <span className="text-gray-800 dark:text-gray-200 flex items-center truncate whitespace-nowrap">{f.label}</span>
                                      )}
                                      <span className="text-gray-600 dark:text-gray-300 font-mono flex items-center truncate whitespace-nowrap">{f.key}</span>
                                      <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center">{f.type || 'text'}</span>
                                      <span className="flex items-center justify-center">
                                        <Toggle checked={!!f.required} onChange={() => updateBcField(f.key, { required: !f.required })} />
                                      </span>
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={f.description}
                                          onChange={(e) => updateBcField(f.key, { description: e.target.value })}
                                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200"
                                        />
                                      ) : (
                                        <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center" title={f.description}>{f.description || '-'}</span>
                                      )}
                                      <span className="flex items-center justify-center">
                                        <Toggle checked={!!f.user_input} onChange={() => updateBcField(f.key, { user_input: !f.user_input })} />
                                      </span>
                                      <span className="flex items-center">
                                        <MappingFieldSelect
                                          value={f.bc_field || ''}
                                          // A table holds lines, so its only target is the BC lines
                                          // entity; header fields would never receive its rows.
                                          options={f.type === 'array_of_objects'
                                            ? [{
                                                key: f.bc_field || 'purchaseOrderLines',
                                                label: f.bc_field_label || `${selectedBcEntityLabel || 'Purchase Order'} Lines`,
                                                scope: 'line',
                                              }]
                                            : availableFields}
                                          onChange={(key) => updateBcField(f.key, { bc_field: key || null })}
                                        />
                                        {isBcMandatory && (
                                          <span className="ml-1 inline-flex px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-[10px] font-medium whitespace-nowrap">required</span>
                                        )}
                                      </span>
                                      <span className="flex items-center gap-1 justify-end">
                                        <button
                                          type="button"
                                          onClick={() => setBcEditingRowKey(isEditing ? '' : f.key)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                          title={isEditing ? 'Done editing' : 'Edit field'}
                                        >
                                          {isEditing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteBcField(f.key)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          title="Delete field"
                                        >
                                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                        </button>
                                      </span>
                                    </div>
                                  )
                                })}
                                {filteredRows.length === 0 && (
                                  <div className="px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400">No fields match your search.</div>
                                )}
                              </div>
                            </div>
                          )}

                          <p className="text-xs text-gray-500 dark:text-gray-400">{bcEditableFields.length} field{bcEditableFields.length !== 1 ? 's' : ''} will be created for this template.</p>
                        </div>
                      )
                    })()}
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Approver hint (optional)</label>
                    <textarea rows={2} maxLength={500} value={verificationPrompt} onChange={e => setVerificationPrompt(e.target.value)} placeholder="Add any validation hints for approvers" className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-xs resize-none" />
                  </div>
                </div>
              )}

              {creationMode === 'ns_purchase_order' && (
                <div className="space-y-3">
                  <div className="rounded-xl border border-[#7da5bf] dark:border-navy-700 bg-[#edf7fc] dark:bg-navy-900/20 p-4">
                    <div className="flex items-start gap-3">
                      <div className="inline-flex h-5 w-5 items-center justify-center rounded-full border border-[#3e6170] text-[#3e6170] dark:border-navy-300 dark:text-navy-300 mt-0.5">
                        <span className="h-2.5 w-2.5 rounded-full bg-[#3e6170] dark:bg-navy-300" />
                      </div>
                      <div>
                        <p className="text-[14px] font-semibold text-gray-900 dark:text-gray-100">Create from NetSuite {selectedNsEntityLabel}</p>
                        <p className="text-[12px] text-gray-500 dark:text-gray-400 mt-0.5 max-w-2xl">Auto-imported NetSuite {selectedNsEntityLabel} fields. Review and refine them below.</p>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-[#b6d7e8] dark:border-navy-800 bg-[#f4fbff] dark:bg-navy-900/10 p-4 space-y-3">
                    {nsLoading && <p className="text-sm text-gray-500 dark:text-gray-400">Checking NetSuite connection...</p>}

                    {!nsLoading && nsLoadError && (
                      <p className="text-sm text-gray-600 dark:text-gray-300">{nsLoadError}</p>
                    )}

                    {!nsLoading && nsStatus?.connected && (
                      <div className="space-y-1">
                        <div className="flex items-center gap-2">
                          <CheckCircle2 className="h-4.5 w-4.5 text-[#3c7185] dark:text-navy-300" />
                          <p className="text-[12px] font-semibold text-[#2f5f73] dark:text-navy-300">NetSuite connected</p>
                        </div>
                        <p className="pl-6 text-[12px] text-gray-500 dark:text-gray-400">
                          {(nsStatus.environment || 'Sandbox')}
                          {nsStatus.account_id ? ` • ${nsStatus.account_id}` : ''}
                        </p>
                      </div>
                    )}

                    {!nsLoading && !nsStatus?.connected && !nsLoadError && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">NetSuite not connected.</p>
                    )}

                    {!nsLoading && nsStatus?.connected && (() => {
                      const availableFields = nsDetectResult?.available_fields || []
                      const query = nsFieldSearch.trim().toLowerCase()
                      const filteredRows = query
                        ? nsEditableFields.filter((f) =>
                            (f.label || '').toLowerCase().includes(query) ||
                            (f.key || '').toLowerCase().includes(query) ||
                            (f.description || '').toLowerCase().includes(query)
                          )
                        : nsEditableFields
                      const filteredFieldKeys = filteredRows.map((f) => f.key)
                      const selectedVisibleCount = filteredFieldKeys.filter((key) => nsSelectedFieldKeys.has(key)).length
                      const allVisibleSelected = filteredFieldKeys.length > 0 && selectedVisibleCount === filteredFieldKeys.length
                      const selectedTotalCount = nsEditableFields.filter((f) => nsSelectedFieldKeys.has(f.key)).length
                      const addStandardFieldsLabel = selectedNsEntityType === 'purchase_order'
                        ? 'Add Standard NetSuite PO Fields'
                        : 'Add Mandatory NetSuite Fields'

                      return (
                        <div className="space-y-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <div className="relative flex-1 max-w-[360px]">
                              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                              <input
                                type="text"
                                value={nsFieldSearch}
                                onChange={(e) => setNsFieldSearch(e.target.value)}
                                placeholder="Search fields"
                                className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-8 pr-3 py-2 text-xs text-gray-700 dark:text-gray-200"
                              />
                            </div>
                            <div className="ml-auto flex items-center gap-2">
                              <button
                                type="button"
                                onClick={addStandardNsFields}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {addStandardFieldsLabel}
                              </button>
                              <button
                                type="button"
                                onClick={addBlankNsField}
                                className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-gray-200 dark:border-gray-700 text-xs font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                              >
                                <Plus className="h-3.5 w-3.5" />
                                Add field
                              </button>
                            </div>
                          </div>

                          {nsEditableFields.length === 0 ? (
                            <div className="rounded-xl border border-dashed border-[#bfd8e4] dark:border-navy-700 bg-[#ebf6fb] dark:bg-navy-900/20 p-5 text-center">
                              <Sparkles className="h-6 w-6 mx-auto text-[#4e7e93] dark:text-navy-300 mb-1.5" />
                              <p className="text-[13px] font-semibold text-[#2f5f73] dark:text-navy-300">No NetSuite {selectedNsEntityLabel} fields yet</p>
                              <p className="text-[12px] text-[#3f6576] dark:text-navy-200/90 mt-0.5 max-w-md mx-auto">
                                Go back to upload a NetSuite {selectedNsEntityLabel} PDF so AI can map fields automatically, or add fields to get started.
                              </p>
                              <button
                                type="button"
                                onClick={addStandardNsFields}
                                className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-lg bg-[var(--brand-accent)] text-white text-xs font-semibold hover:opacity-90"
                              >
                                <Sparkles className="h-3.5 w-3.5" />
                                {addStandardFieldsLabel}
                              </button>
                            </div>
                          ) : (
                            <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                              <div className="grid grid-cols-[36px_1.2fr_1.2fr_0.7fr_90px_1.8fr_90px_1.6fr_86px] gap-3 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                <span className="flex items-center">
                                  <input
                                    type="checkbox"
                                    checked={allVisibleSelected}
                                    onChange={(e) => setNsFieldSelectionForKeys(filteredFieldKeys, e.target.checked)}
                                    className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500"
                                    aria-label="Select all fields"
                                  />
                                </span>
                                <span>Display</span>
                                <span>Field Name</span>
                                <span>Type</span>
                                <span className="text-center">Required</span>
                                <span>Description</span>
                                <span className="text-center">User Input</span>
                                <span className="text-navy-600 dark:text-navy-400">NS Mapping</span>
                                <span className="text-right">Actions</span>
                              </div>
                              {/* No inner max-h/overflow here — the field list scrolls
                                  with the modal body (its own overflow-y-auto), not in
                                  a separate boxed-in scroll area that only shows a
                                  handful of rows at a time. */}
                              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {filteredRows.map((f) => {
                                  const isEditing = nsEditingRowKey === f.key
                                  const nsMeta = availableFields.find((af) => af.key === f.ns_field)
                                  const isNsMandatory = !!nsMeta?.mandatory || NS_MANDATORY_KEYS.includes(f.ns_field)
                                  const isRowSelected = nsSelectedFieldKeys.has(f.key)
                                  const isArrayField = f.type === 'array_of_objects'
                                  const columns = isArrayField && Array.isArray(f.columns) ? f.columns : []
                                  const mappedColumnCount = columns.filter((c) => c.ns_field).length
                                  return (
                                  <React.Fragment key={f.key}>
                                    <div className={`group grid grid-cols-[36px_1.2fr_1.2fr_0.7fr_90px_1.8fr_90px_1.6fr_86px] gap-3 px-4 py-3 text-[13px] hover:bg-gray-50 dark:hover:bg-gray-700/60 ${isRowSelected ? '' : 'opacity-50'}`}>
                                      <span className="flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={isRowSelected}
                                          onChange={() => toggleNsFieldSelection(f.key)}
                                          className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500"
                                          aria-label={`Include ${f.label || f.key} in template`}
                                        />
                                      </span>
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={f.label}
                                          onChange={(e) => updateNsField(f.key, { label: e.target.value })}
                                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200"
                                        />
                                      ) : (
                                        <span className="text-gray-800 dark:text-gray-200 flex items-center truncate whitespace-nowrap">{f.label}</span>
                                      )}
                                      <span className="text-gray-600 dark:text-gray-300 font-mono flex items-center truncate whitespace-nowrap">{f.key}</span>
                                      <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center">
                                        {isArrayField ? `line items (${columns.length} col${columns.length !== 1 ? 's' : ''})` : (f.type || 'text')}
                                      </span>
                                      <span className="flex items-center justify-center">
                                        <Toggle checked={!!f.required} onChange={() => updateNsField(f.key, { required: !f.required })} />
                                      </span>
                                      {isEditing ? (
                                        <input
                                          type="text"
                                          value={f.description}
                                          onChange={(e) => updateNsField(f.key, { description: e.target.value })}
                                          className="rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1 text-[13px] text-gray-800 dark:text-gray-200"
                                        />
                                      ) : (
                                        <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center" title={f.description}>{f.description || '-'}</span>
                                      )}
                                      <span className="flex items-center justify-center">
                                        <Toggle checked={!!f.user_input} onChange={() => updateNsField(f.key, { user_input: !f.user_input })} />
                                      </span>
                                      <span className="flex items-center">
                                        {isArrayField ? (
                                          <span className="text-[12px] text-gray-500 dark:text-gray-400 italic">
                                            {mappedColumnCount}/{columns.length} column{columns.length !== 1 ? 's' : ''} mapped below
                                          </span>
                                        ) : (
                                          <>
                                            <MappingFieldSelect
                                              value={f.ns_field || ''}
                                              options={availableFields}
                                              onChange={(key) => updateNsField(f.key, { ns_field: key || null })}
                                            />
                                            {isNsMandatory && (
                                              <span className="ml-1 inline-flex px-1.5 py-0.5 rounded bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 text-[10px] font-medium whitespace-nowrap">required</span>
                                            )}
                                          </>
                                        )}
                                      </span>
                                      <span className="flex items-center gap-1 justify-end">
                                        <button
                                          type="button"
                                          onClick={() => setNsEditingRowKey(isEditing ? '' : f.key)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                          title={isEditing ? 'Done editing' : 'Edit field'}
                                        >
                                          {isEditing ? <Check className="h-3.5 w-3.5" /> : <Pencil className="h-3.5 w-3.5" />}
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteNsField(f.key)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-red-50 dark:hover:bg-red-900/20"
                                          title="Delete field"
                                        >
                                          <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                        </button>
                                      </span>
                                    </div>
                                    {isArrayField && columns.length > 0 && (
                                      <div className="px-4 pb-3 pt-1 bg-gray-50/60 dark:bg-gray-900/40">
                                        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                                          <div className="grid grid-cols-[1.2fr_0.9fr_70px_1fr] gap-3 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500 border-b border-gray-100 dark:border-gray-800">
                                            <span>Column</span>
                                            <span>Type</span>
                                            <span className="text-center">Req.</span>
                                            <span>NS Mapping</span>
                                          </div>
                                          <div className="divide-y divide-gray-100 dark:divide-gray-800">
                                            {columns.map((c) => (
                                              <div key={c.key} className="grid grid-cols-[1.2fr_0.9fr_70px_1fr] gap-3 px-3 py-1.5 items-center text-[12px]">
                                                <span className="text-gray-700 dark:text-gray-300 truncate" title={c.description}>
                                                  {c.label || c.key}
                                                  {c.inherited_from_header && (
                                                    <span className="ml-1.5 text-[10px] text-blue-500 dark:text-blue-400">↳ from header</span>
                                                  )}
                                                </span>
                                                <span className="text-gray-500 dark:text-gray-400">{c.type || 'text'}</span>
                                                <span className="flex items-center justify-center">
                                                  <Toggle
                                                    checked={c.required !== false}
                                                    onChange={() => updateNsFieldColumn(f.key, c.key, { required: c.required === false })}
                                                  />
                                                </span>
                                                {c.posted === false ? (
                                                  <span className="italic text-gray-400 dark:text-gray-500">not posted — extraction helper only</span>
                                                ) : (
                                                  <MappingFieldSelect
                                                    value={c.ns_field || ''}
                                                    options={availableFields}
                                                    onChange={(key) => updateNsFieldColumn(f.key, c.key, { ns_field: key || null })}
                                                  />
                                                )}
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </React.Fragment>
                                  )
                                })}
                                {filteredRows.length === 0 && (
                                  <div className="px-3 py-6 text-center text-xs text-gray-500 dark:text-gray-400">No fields match your search.</div>
                                )}
                              </div>
                            </div>
                          )}

                          <p className="text-xs text-gray-500 dark:text-gray-400">
                            {selectedTotalCount} of {nsEditableFields.length} field{nsEditableFields.length !== 1 ? 's' : ''} selected — only checked fields will be created for this template.
                          </p>
                        </div>
                      )
                    })()}
                  </div>
                </div>
              )}


              {creationMode === 'blank' && (
                <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-4 bg-white dark:bg-gray-900">
                  {blankDetecting && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {blankProgress?.message || 'Reading the documents and designing the template...'}
                      {blankProgress?.percent ? ` (${blankProgress.percent}%)` : ''}
                    </p>
                  )}

                  {!blankDetecting && blankSuggestion?.questions?.length > 0 && (
                    <SuggestionQuestions
                      questions={blankSuggestion.questions}
                      onApply={applyBlankAnswers}
                      applying={blankApplyingAnswers}
                    />
                  )}

                  {!blankDetecting && blankOptionalFields.length > 0 && (
                    <button
                      type="button"
                      onClick={addBlankOptionalFields}
                      className="text-sm text-[var(--brand-accent)] hover:underline font-medium"
                      title={blankOptionalFields.map((f) => f.label).join(', ')}
                    >
                      Add {blankOptionalFields.length} optional suggestion{blankOptionalFields.length !== 1 ? 's' : ''} (footer details, secondary tables)
                    </button>
                  )}

                  {!blankDetecting && blankDetectError && (
                    <p className="text-sm text-gray-500 dark:text-gray-400">{blankDetectError}</p>
                  )}

                  {!blankDetecting && !blankDetectError && blankPreviewSource && (
                    <p className="text-[14px] text-gray-500 dark:text-gray-400">{blankPreviewSource}</p>
                  )}

                  {!blankDetecting && blankEditableFields.length > 0 && (() => {
                    const query = blankFieldSearch.trim().toLowerCase()
                    const filteredFields = query
                      ? blankEditableFields.filter((f) =>
                          (f.label || f.key).toLowerCase().includes(query) ||
                          f.key.toLowerCase().includes(query) ||
                          (f.description || '').toLowerCase().includes(query)
                        )
                      : blankEditableFields

                    const filteredFieldKeys = filteredFields.map((f) => f.key)
                    const selectedVisibleCount = filteredFieldKeys.filter((key) => blankSelectedFieldKeys.has(key)).length
                    const allVisibleSelected = filteredFieldKeys.length > 0 && selectedVisibleCount === filteredFieldKeys.length

                    const groupedFields = filteredFields.reduce((acc, field) => {
                      const section = (field.section || 'None').trim() || 'None'
                      if (!acc[section]) acc[section] = []
                      acc[section].push(field)
                      return acc
                    }, {})
                    const sectionEntries = Object.entries(groupedFields)

                    return (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between gap-3">
                          <div className="relative flex-1 max-w-[420px]">
                            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
                            <input
                              type="text"
                              value={blankFieldSearch}
                              onChange={(e) => setBlankFieldSearch(e.target.value)}
                              placeholder="Search fields"
                              className="w-full rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 pl-8 pr-3 py-2 text-[14px] text-gray-700 dark:text-gray-200"
                            />
                          </div>
                          <div className="ml-auto flex items-center gap-2">
                            {blankSelectedFieldKeys.size > 0 && (
                              <button
                                type="button"
                                onClick={deleteSelectedBlankFields}
                                className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-red-200 dark:border-red-800 text-[14px] font-medium leading-none text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20"
                              >
                                <Trash2 className="h-4 w-4" />
                                <span className="text-[14px]">Delete ({blankSelectedFieldKeys.size})</span>
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={addBlankField}
                              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl border border-gray-200 dark:border-gray-700 text-[14px] font-medium leading-none text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
                            >
                              <Plus className="h-4 w-4" />
                              <span className="text-[14px]">Add field</span>
                            </button>
                            <button
                              type="button"
                              onClick={saveBlankFieldChanges}
                              disabled={!blankFieldsDirty}
                              className="inline-flex items-center gap-1.5 h-10 px-4 rounded-xl bg-navy-800 text-white text-[14px] font-semibold hover:bg-navy-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              <CheckCircle2 className="h-4 w-4" />
                              <span className="text-[14px]">Save Changes ({blankEditableFields.length})</span>
                            </button>
                          </div>
                        </div>

                        {sectionEntries.length > 0 ? (
                          <div className="space-y-2 max-h-[calc(100vh-28rem)] min-h-[12rem] overflow-y-auto">
                            {sectionEntries.map(([sectionName, sectionFields]) => (
                              <div key={sectionName} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                                <div className="px-4 py-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30">
                                  {sectionName}
                                </div>
                                <div className="grid grid-cols-[44px_1.2fr_1.2fr_0.7fr_90px_2.4fr_120px_86px] gap-4 px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.05em] text-gray-500 dark:text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                  <span className="flex items-center">
                                    <input
                                      type="checkbox"
                                      checked={allVisibleSelected}
                                      onChange={(e) => setBlankFieldSelectionForKeys(filteredFieldKeys, e.target.checked)}
                                      className="h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500"
                                      aria-label="Select all fields"
                                    />
                                  </span>
                                  <span>Display</span>
                                  <span>Field Name</span>
                                  <span>Type</span>
                                  <span>Required</span>
                                  <span>Description</span>
                                  <span>User Input</span>
                                  <span className="text-right">Actions</span>
                                </div>
                                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                  {sectionFields.map((f) => {
                                    const isRowSelected = blankSelectedFieldKeys.has(f.key)
                                    return (
                                    <div key={f.key} className="group grid grid-cols-[44px_1.2fr_1.2fr_0.7fr_90px_2.4fr_120px_86px] gap-4 px-4 py-3 text-[14px] hover:bg-gray-50 dark:hover:bg-gray-700/60">
                                      <span className="flex items-center">
                                        <input
                                          type="checkbox"
                                          checked={isRowSelected}
                                          onChange={() => toggleBlankFieldSelection(f.key)}
                                          className={`h-4 w-4 rounded border-gray-300 text-navy-700 focus:ring-2 focus:ring-navy-500 transition-opacity ${isRowSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 focus:opacity-100'}`}
                                        />
                                      </span>
                                      <span className="text-gray-800 dark:text-gray-200 flex items-center truncate whitespace-nowrap">
                                        {f.label || f.key}
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 font-mono flex items-center truncate whitespace-nowrap">
                                        {f.key}
                                      </span>
                                      <span
                                        className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center"
                                        title={f.type === 'array_of_objects' && f.columns?.length ? `Columns: ${f.columns.map((c) => c.label || c.key).join(', ')}` : undefined}
                                      >
                                        {f.type === 'array_of_objects'
                                          ? `table · ${f.columns?.length || 0} columns`
                                          : f.type || 'text'}
                                      </span>
                                      <span className="flex items-center">
                                        <Toggle checked={!!f.required} onChange={() => updateBlankField(f.key, { required: !f.required })} />
                                      </span>
                                      <span className="text-gray-600 dark:text-gray-300 truncate whitespace-nowrap flex items-center">
                                        {f.description || '-'}
                                      </span>
                                      <span className="flex items-center">
                                        <Toggle checked={!!f.user_input} onChange={() => updateBlankField(f.key, { user_input: !f.user_input })} />
                                      </span>
                                      <span className="flex items-center gap-1 justify-end">
                                        <button
                                          type="button"
                                          onClick={() => openEditBlankFieldModal(f)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                          title="Edit field"
                                        >
                                          <Pencil className="h-3.5 w-3.5" />
                                        </button>
                                        <button
                                          type="button"
                                          onClick={() => deleteBlankField(f.key)}
                                          className="inline-flex items-center justify-center h-7 w-7 rounded-md text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
                                          title="Delete field"
                                        >
                                          <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                      </span>
                                    </div>
                                    )
                                  })}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden">
                            <div className="px-3 py-6 text-center text-[14px] text-gray-500 dark:text-gray-400">No fields match your search.</div>
                          </div>
                        )}

                        <div className="flex items-center justify-between">
                          <p className="text-[14px] text-gray-500 dark:text-gray-400">{blankEditableFields.length} field(s)</p>
                          <p className="text-[13px] text-gray-500 dark:text-gray-400">{selectedVisibleCount} selected</p>
                        </div>
                      </div>
                    )
                  })()}
                </div>
              )}
            </div>
          )}

          {isAdmin && currentStepKey === 'access_ownership' && (
            <div className="space-y-5 animate-[fadeIn_0.3s_ease-out]">
              {/* Tab switch */}
              <div className="inline-flex items-center gap-1 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-1 shadow-sm">
                <button
                  type="button"
                  onClick={() => setAccessTab('users')}
                  className={`relative inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-sm font-semibold transition-all duration-200 ${
                    accessTab === 'users'
                      ? 'bg-navy-800 text-white shadow-sm scale-[1.02]'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                  }`}
                >
                  <UsersRound className="h-4 w-4" />
                  Users
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-bold transition-colors duration-200 ${accessTab === 'users' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
                    {Object.keys(userRoleMap).length}
                  </span>
                </button>
                <button
                  type="button"
                  onClick={() => setAccessTab('groups')}
                  className={`relative inline-flex h-9 items-center gap-1.5 rounded-md px-4 text-sm font-semibold transition-all duration-200 ${
                    accessTab === 'groups'
                      ? 'bg-navy-800 text-white shadow-sm scale-[1.02]'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/60'
                  }`}
                >
                  <UsersRound className="h-4 w-4" />
                  Groups
                  <span className={`inline-flex items-center justify-center min-w-[20px] h-[20px] px-1.5 rounded-full text-[11px] font-bold transition-colors duration-200 ${accessTab === 'groups' ? 'bg-white/20' : 'bg-gray-200 dark:bg-gray-700'}`}>
                    {selectedAccessGroupIds.size}
                  </span>
                </button>
              </div>

              {accessTab === 'users' ? (
                <>
                  {/* Search-as-you-type user picker with inline role + add */}
                  <div className="relative" ref={accessUserPickerRef}>
                    <div className={`relative rounded-xl border bg-white dark:bg-gray-900 transition-all duration-200 ${accessUserDropdownOpen ? 'border-navy-500 ring-4 ring-navy-500/10 shadow-md' : 'border-gray-200 dark:border-gray-700 shadow-sm'}`}>
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={accessUserSearch}
                        onChange={(e) => { setAccessUserSearch(e.target.value); setAccessUserDropdownOpen(true) }}
                        onFocus={() => setAccessUserDropdownOpen(true)}
                        placeholder="Search users by name or email to assign a role..."
                        className="w-full pl-11 pr-4 py-3 rounded-xl bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
                      />
                    </div>

                    {accessUserDropdownOpen && accessUserSearch.trim() && (
                      <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                        {(() => {
                          const q = accessUserSearch.trim().toLowerCase()
                          const results = nonAdminOrgUsers.filter((u) =>
                            (u.username || '').toLowerCase().includes(q) || (u.email || '').toLowerCase().includes(q)
                          ).slice(0, 8)
                          if (results.length === 0) {
                            return (
                              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                No users match "{accessUserSearch}"
                              </div>
                            )
                          }
                          return (
                            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                              {results.map((u) => {
                                const isAssigned = !!userRoleMap[u.id]
                                const pendingRole = accessSearchRoleMap[u.id] || 'reviewer'
                                return (
                                  <div
                                    key={u.id}
                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-150"
                                  >
                                    <UserAvatar user={u} size="sm" />
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{u.username || u.email}</p>
                                      {u.email && u.username && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>}
                                    </div>
                                    {isAssigned ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-400 flex-shrink-0">
                                        <CheckCircle2 className="w-3 h-3" /> Assigned
                                      </span>
                                    ) : (
                                      <>
                                        <select
                                          value={pendingRole}
                                          onMouseDown={(e) => e.stopPropagation()}
                                          onChange={(e) => setAccessSearchRoleMap((prev) => ({ ...prev, [u.id]: e.target.value }))}
                                          className="text-xs font-medium px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 flex-shrink-0 focus:outline-none focus:ring-2 focus:ring-navy-500/30"
                                        >
                                          {ROLE_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                                          ))}
                                        </select>
                                        <button
                                          type="button"
                                          onMouseDown={(e) => { e.preventDefault(); quickAssignUser(u.id, pendingRole) }}
                                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-navy-800 text-white hover:bg-navy-900 flex-shrink-0 transition-all duration-150 active:scale-95 shadow-sm"
                                        >
                                          <Plus className="w-3 h-3" /> Add
                                        </button>
                                      </>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Assigned users — newest additions float to top */}
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Assigned users
                      </p>
                      <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{Object.keys(userRoleMap).length} assigned</span>
                    </div>

                    {Object.keys(userRoleMap).length === 0 ? (
                      <div className="text-center py-10 px-4">
                        <UsersRound className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No users assigned yet.</p>
                        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">Search above to find someone and assign a role.</p>
                      </div>
                    ) : (
                      <div className="max-h-[22rem] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                        {Object.entries(userRoleMap)
                          .map(([userId, role]) => ({ user: nonAdminOrgUsers.find((u) => String(u.id) === String(userId)), role, userId }))
                          .filter((row) => row.user)
                          .sort((a, b) => (a.userId === justAddedUserId ? -1 : b.userId === justAddedUserId ? 1 : 0))
                          .map(({ user: u, role, userId }) => (
                            <div
                              key={userId}
                              className={`flex items-center gap-3 px-4 py-2.5 transition-all duration-300 animate-[fadeIn_0.25s_ease-out] ${String(justAddedUserId) === String(userId) ? 'bg-navy-50/70 dark:bg-navy-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}
                            >
                              <UserAvatar user={u} size="sm" />
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{u.username || u.email}</p>
                                {u.email && u.username && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{u.email}</p>}
                              </div>
                              <select
                                value={role}
                                onChange={(e) => setUserRole(userId, e.target.value)}
                                className="text-xs font-medium px-2 py-1.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 flex-shrink-0 transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30"
                              >
                                {ROLE_OPTIONS.map((opt) => (
                                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                              </select>
                              <button
                                type="button"
                                onClick={() => removeUserAccess(userId)}
                                className="group inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0 transition-all duration-150 active:scale-90"
                                aria-label="Remove"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <>
                  {/* Search-as-you-type group picker */}
                  <div className="relative" ref={accessGroupPickerRef}>
                    <div className={`relative rounded-xl border bg-white dark:bg-gray-900 transition-all duration-200 ${accessGroupDropdownOpen ? 'border-navy-500 ring-4 ring-navy-500/10 shadow-md' : 'border-gray-200 dark:border-gray-700 shadow-sm'}`}>
                      <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <input
                        type="text"
                        value={accessGroupSearch}
                        onChange={(e) => { setAccessGroupSearch(e.target.value); setAccessGroupDropdownOpen(true) }}
                        onFocus={() => setAccessGroupDropdownOpen(true)}
                        placeholder="Search groups by name to assign..."
                        className="w-full pl-11 pr-4 py-3 rounded-xl bg-transparent text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:outline-none"
                      />
                    </div>

                    {accessGroupDropdownOpen && accessGroupSearch.trim() && (
                      <div className="absolute z-20 mt-2 w-full rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                        {(() => {
                          const q = accessGroupSearch.trim().toLowerCase()
                          const results = orgGroups.filter((g) =>
                            (g.name || '').toLowerCase().includes(q) || (g.description || '').toLowerCase().includes(q)
                          ).slice(0, 8)
                          if (results.length === 0) {
                            return (
                              <div className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                                No groups match "{accessGroupSearch}"
                              </div>
                            )
                          }
                          return (
                            <div className="max-h-72 overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                              {results.map((g) => {
                                const checked = selectedAccessGroupIds.has(g.id)
                                const isEntra = g.type === 'entra'
                                return (
                                  <div
                                    key={g.id}
                                    className="flex items-center gap-3 px-4 py-2.5 hover:bg-gray-50 dark:hover:bg-gray-800/60 transition-colors duration-150"
                                  >
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isEntra ? 'bg-navy-100 dark:bg-navy-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                      {isEntra
                                        ? <Globe className="w-4 h-4 text-navy-700 dark:text-navy-400" />
                                        : <UsersRound className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{g.name}</p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{g.description || (isEntra ? 'Entra ID group' : 'Manual group')}</p>
                                    </div>
                                    {checked ? (
                                      <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium bg-navy-50 dark:bg-navy-900/20 text-navy-700 dark:text-navy-400 flex-shrink-0">
                                        <CheckCircle2 className="w-3 h-3" /> Assigned
                                      </span>
                                    ) : (
                                      <button
                                        type="button"
                                        onMouseDown={(e) => { e.preventDefault(); quickAssignGroup(g.id) }}
                                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold bg-navy-800 text-white hover:bg-navy-900 flex-shrink-0 transition-all duration-150 active:scale-95 shadow-sm"
                                      >
                                        <Plus className="w-3 h-3" /> Add
                                      </button>
                                    )}
                                  </div>
                                )
                              })}
                            </div>
                          )
                        })()}
                      </div>
                    )}
                  </div>

                  {/* Assigned groups — newest additions float to top */}
                  <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-sm">
                    <div className="flex items-center justify-between px-4 py-2.5 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
                      <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                        Assigned groups
                      </p>
                      <span className="text-xs font-medium text-gray-400 dark:text-gray-500">{selectedAccessGroupIds.size} assigned</span>
                    </div>

                    {selectedAccessGroupIds.size === 0 ? (
                      <div className="text-center py-10 px-4">
                        <UsersRound className="w-8 h-8 mx-auto text-gray-300 dark:text-gray-600" />
                        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">No groups assigned yet.</p>
                        <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">Search above to find a group and assign it.</p>
                      </div>
                    ) : (
                      <div className="max-h-[22rem] overflow-y-auto divide-y divide-gray-100 dark:divide-gray-800">
                        {orgGroups
                          .filter((g) => selectedAccessGroupIds.has(g.id))
                          .sort((a, b) => (a.id === justAddedGroupId ? -1 : b.id === justAddedGroupId ? 1 : 0))
                          .map((g) => {
                            const isEntra = g.type === 'entra'
                            return (
                              <div
                                key={g.id}
                                className={`flex items-center gap-3 px-4 py-2.5 transition-all duration-300 animate-[fadeIn_0.25s_ease-out] ${g.id === justAddedGroupId ? 'bg-navy-50/70 dark:bg-navy-900/10' : 'hover:bg-gray-50 dark:hover:bg-gray-800/40'}`}
                              >
                                <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isEntra ? 'bg-navy-100 dark:bg-navy-900/30' : 'bg-gray-100 dark:bg-gray-800'}`}>
                                  {isEntra
                                    ? <Globe className="w-4 h-4 text-navy-700 dark:text-navy-400" />
                                    : <UsersRound className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{g.name}</p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{g.description || (isEntra ? 'Entra ID group' : 'Manual group')}</p>
                                </div>
                                <span className="inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 flex-shrink-0">
                                  <UsersRound className="w-3 h-3 text-gray-400" />
                                  {g.member_count || 0}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => toggleAccessGroup(g.id)}
                                  className="group inline-flex items-center justify-center h-8 w-8 rounded-lg text-gray-400 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-600 dark:hover:text-red-400 flex-shrink-0 transition-all duration-150 active:scale-90"
                                  aria-label="Remove"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            )
                          })}
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {isAdmin && currentStepKey === 'add_connectors' && (
            <div className="space-y-3 animate-[fadeIn_0.3s_ease-out]">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
                <button type="button" onClick={() => setConnectorMode('create_webhook')} className={`text-left rounded-lg border p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${connectorMode === 'create_webhook' ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/20 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Create connector</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Add a new HTTP destination</p>
                </button>
                <button type="button" onClick={() => setConnectorMode('select_existing')} className={`text-left rounded-lg border p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${connectorMode === 'select_existing' ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/20 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Select existing</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Use a connected connector</p>
                </button>
                <button type="button" onClick={() => setConnectorMode('later')} className={`text-left rounded-lg border p-3 transition-all duration-200 hover:-translate-y-0.5 hover:shadow-sm ${connectorMode === 'later' ? 'border-navy-500 bg-navy-50 dark:bg-navy-900/20 shadow-sm' : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'}`}>
                  <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">Later</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">Configure after creation</p>
                </button>
              </div>

              {connectorMode === 'select_existing' && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                  <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Search existing integration</label>
                  <input value={connectorSearch} onChange={(e) => setConnectorSearch(e.target.value)} placeholder="Search integrations" className="mt-2 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm" />

                  {loadingWebhooks && <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">Loading existing connectors...</p>}
                  {!loadingWebhooks && connectorError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{connectorError}</p>}

                  {!loadingWebhooks && !connectorError && (
                    <div className="mt-2 max-h-44 overflow-y-auto rounded-lg border border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
                      {availableWebhooks
                        .filter((wh) => {
                          const q = connectorSearch.trim().toLowerCase()
                          if (!q) return true
                          return (
                            (wh.name || '').toLowerCase().includes(q) ||
                            (wh.url || '').toLowerCase().includes(q)
                          )
                        })
                        .map((wh) => (
                          <label key={wh.id} className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800">
                            <input
                              type="radio"
                              name="selectedWebhook"
                              value={String(wh.id)}
                              checked={String(selectedConnectorWebhookId) === String(wh.id)}
                              onChange={() => setSelectedConnectorWebhookId(String(wh.id))}
                              className="mt-0.5 accent-navy-600"
                            />
                            <span className="min-w-0">
                              <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{wh.name || `Webhook ${wh.id}`}</span>
                              <span className="block text-xs text-gray-500 dark:text-gray-400 truncate">{wh.url}</span>
                            </span>
                          </label>
                        ))}
                      {availableWebhooks.filter((wh) => {
                        const q = connectorSearch.trim().toLowerCase()
                        if (!q) return true
                        return (
                          (wh.name || '').toLowerCase().includes(q) ||
                          (wh.url || '').toLowerCase().includes(q)
                        )
                      }).length === 0 && (
                        <p className="px-3 py-3 text-xs text-gray-500 dark:text-gray-400">No matching connectors found.</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {connectorMode === 'create_webhook' && (
                <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden animate-[fadeIn_0.2s_ease-out]">
                  {/* Section 1: Connection details */}
                  <div className="p-4 space-y-4">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-100 dark:border-gray-800">
                      <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300 flex items-center justify-center">
                        <Link2 className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                      <div>
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-200">Connection details</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Where deliveries are sent and what triggers them</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Name</label>
                        <input
                          value={connectorName}
                          onChange={(e) => setConnectorName(e.target.value)}
                          placeholder="e.g. Purchase Order Export"
                          maxLength={100}
                          className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                        />
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Endpoint URL</label>
                        <input
                          value={connectorUrl}
                          onChange={(e) => setConnectorUrl(e.target.value)}
                          placeholder="https://your-function.azurewebsites.net/api/webhook"
                          className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                        />
                        {connectorUrl && !connectorUrl.startsWith('https://') && (
                          <p className="mt-1 text-xs text-red-600 dark:text-red-400">URL must use HTTPS.</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {(() => {
                        const EVENT_OPTIONS = [
                          { value: 'request.approved', label: 'Request Approved', description: "Fires when a request is approved/published (auto when the template's auto-approve toggle is on, otherwise on manual Publish)" },
                          { value: 'request.updated', label: 'Request Updated', description: "Fires when a request's fields are saved/edited (toggle controls upsert/append)" },
                        ]
                        const selectedEvents = connectorEvents.split(',').map((e) => e.trim()).filter(Boolean)
                        const toggleEvent = (value) => {
                          const set = new Set(selectedEvents)
                          if (set.has(value)) set.delete(value)
                          else set.add(value)
                          if (set.size === 0) set.add('request.approved')
                          setConnectorEvents([...set].join(','))
                        }
                        const summaryLabel = selectedEvents.length === EVENT_OPTIONS.length
                          ? 'All events'
                          : EVENT_OPTIONS.filter((o) => selectedEvents.includes(o.value)).map((o) => o.label).join(', ') || 'Select events'
                        return (
                          <div className="relative" ref={connectorEventsPickerRef}>
                            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Events</label>
                            <button
                              type="button"
                              onClick={() => setConnectorEventsOpen((v) => !v)}
                              className="mt-1.5 w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-left transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 hover:border-gray-400 dark:hover:border-gray-600"
                            >
                              <span className="flex items-center gap-2 min-w-0">
                                <Zap className="h-3.5 w-3.5 flex-shrink-0 text-navy-600 dark:text-navy-400" />
                                <span className="truncate text-gray-800 dark:text-gray-200">{summaryLabel}</span>
                              </span>
                              <ChevronRight className={`h-3.5 w-3.5 flex-shrink-0 text-gray-400 transition-transform duration-150 ${connectorEventsOpen ? 'rotate-90' : ''}`} />
                            </button>
                            {connectorEventsOpen && (
                              <div className="absolute z-20 mt-1.5 w-full min-w-[280px] rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-lg overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                                {EVENT_OPTIONS.map((evt) => {
                                  const active = selectedEvents.includes(evt.value)
                                  return (
                                    <button
                                      key={evt.value}
                                      type="button"
                                      onClick={() => toggleEvent(evt.value)}
                                      className={`w-full text-left px-3 py-2.5 flex items-start gap-2.5 border-b border-gray-100 dark:border-gray-700/60 last:border-b-0 transition-colors ${
                                        active ? 'bg-navy-50 dark:bg-navy-900/20' : 'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                                      }`}
                                    >
                                      <span className={`mt-0.5 flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center ${
                                        active ? 'bg-navy-600 border-navy-600' : 'border-gray-300 dark:border-gray-600'
                                      }`}>
                                        {active && <Check className="h-3 w-3 text-white" strokeWidth={3} />}
                                      </span>
                                      <span className="min-w-0">
                                        <span className={`block text-sm font-medium ${active ? 'text-navy-800 dark:text-navy-200' : 'text-gray-800 dark:text-gray-200'}`}>{evt.label}</span>
                                        <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">{evt.description}</span>
                                      </span>
                                    </button>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })()}

                      <div>
                        <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Update Mode</label>
                        <select
                          value={connectorUpdateMode}
                          onChange={(e) => setConnectorUpdateMode(e.target.value)}
                          className="mt-1.5 w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                        >
                          <option value="append">Append - insert new row</option>
                          <option value="upsert">Upsert by Request ID</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Section 2: Security */}
                  <div className="p-4 space-y-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/30">
                    <div className="flex items-center gap-2 pb-3 border-b border-gray-200/70 dark:border-gray-700/70">
                      <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-100 dark:bg-navy-900/40 text-navy-700 dark:text-navy-300 flex items-center justify-center">
                        <ShieldCheck className="w-3.5 h-3.5" strokeWidth={2} />
                      </span>
                      <div>
                        <p className="text-[13px] font-semibold uppercase tracking-wide text-gray-800 dark:text-gray-200">Security</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Verify and authenticate deliveries to your endpoint</p>
                      </div>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Signing Secret</label>
                      <div className="mt-1.5 flex items-center gap-2">
                        <div className="relative flex-1">
                          <input
                            type={connectorShowSecret ? 'text' : 'password'}
                            value={connectorSecret}
                            onChange={(e) => setConnectorSecret(e.target.value)}
                            placeholder="Auto-generated if left empty"
                            className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                          />
                          <button
                            type="button"
                            onClick={() => setConnectorShowSecret((v) => !v)}
                            aria-label={connectorShowSecret ? 'Hide secret' : 'Show secret'}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                          >
                            {connectorShowSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        {connectorSecret && (
                          <button
                            type="button"
                            onClick={() => navigator.clipboard.writeText(connectorSecret)}
                            title="Copy secret"
                            className="p-2.5 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-400 hover:text-gray-600 transition-colors active:scale-95"
                          >
                            <Copy className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <p className="mt-1.5 text-[11px] text-gray-400 dark:text-gray-500">
                        Used to sign payloads with HMAC-SHA256. Sent in the <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono">X-Xtract-Signature</code> header.
                      </p>
                    </div>

                    <div>
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">Basic Authentication</label>
                      <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">
                        Sends an <code className="text-[10px] bg-gray-100 dark:bg-gray-800 px-1 rounded font-mono">Authorization: Basic</code> header on every delivery. Password is encrypted before storage.
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <label className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 block">Username</label>
                          <input
                            value={connectorBasicAuthUsername}
                            onChange={(e) => setConnectorBasicAuthUsername(e.target.value)}
                            placeholder="username"
                            autoComplete="off"
                            className="w-full px-3 py-2.5 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                          />
                        </div>
                        <div>
                          <label className="text-[11px] text-gray-500 dark:text-gray-400 mb-1 block">Password</label>
                          <div className="relative">
                            <input
                              type={connectorShowBasicAuthPassword ? 'text' : 'password'}
                              value={connectorBasicAuthPassword}
                              onChange={(e) => setConnectorBasicAuthPassword(e.target.value)}
                              placeholder="password"
                              autoComplete="new-password"
                              className="w-full px-3 py-2.5 pr-10 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm font-mono transition-shadow focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500"
                            />
                            <button
                              type="button"
                              onClick={() => setConnectorShowBasicAuthPassword((v) => !v)}
                              aria-label={connectorShowBasicAuthPassword ? 'Hide password' : 'Show password'}
                              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                            >
                              {connectorShowBasicAuthPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </button>
                          </div>
                        </div>
                      </div>
                      {(!!connectorBasicAuthUsername.trim() !== !!connectorBasicAuthPassword.trim()) && (
                        <p className="mt-1.5 text-xs text-red-600 dark:text-red-400">Both username and password are required together.</p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {isAdmin && currentStepKey === 'review_create' && (
            <div className="space-y-3 animate-[fadeIn_0.3s_ease-out]">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"><p className="text-xs text-gray-500">Template</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{name || 'Untitled'}</p></div>
                {creationMode === 'from_template' ? (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5">
                    <p className="text-xs text-gray-500">Copied from</p>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1 flex items-center gap-1.5">
                      <Copy className="h-3.5 w-3.5 text-navy-600 dark:text-navy-400 flex-shrink-0" />
                      {selectedSourceTemplate?.name || 'Existing template'}
                    </p>
                  </div>
                ) : (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"><p className="text-xs text-gray-500">Documents</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{sampleDocuments.length} sample documents</p></div>
                )}
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"><p className="text-xs text-gray-500">Access</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{Object.keys(userRoleMap).length} users · {selectedAccessGroupIds.size} groups</p></div>
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3 transition-all duration-200 hover:shadow-sm hover:-translate-y-0.5"><p className="text-xs text-gray-500">Integration</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{connectorMode === 'later' ? 'Configure later' : connectorMode === 'select_existing' ? 'Select existing webhook' : 'Create connector'}</p></div>
                {connectorMode === 'select_existing' && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"><p className="text-xs text-gray-500">Selected connector</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{availableWebhooks.find((wh) => String(wh.id) === String(selectedConnectorWebhookId))?.name || 'Not selected'}</p></div>
                )}
                {connectorMode === 'create_webhook' && (
                  <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-3"><p className="text-xs text-gray-500">New connector</p><p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1">{connectorName || 'Unnamed webhook'}</p><p className="text-xs text-gray-500 dark:text-gray-400 mt-1 break-all">{connectorUrl || '-'}</p></div>
                )}
              </div>

              {creationMode === 'ns_purchase_order' && (
                <div className="flex items-start gap-2 rounded-lg bg-navy-100/60 dark:bg-navy-900/20 border border-navy-200 dark:border-navy-700 px-3 py-2.5">
                  <Sparkles className="h-4 w-4 text-navy-500 dark:text-navy-400 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-xs font-semibold text-navy-700 dark:text-navy-300">Next: two steps in the Fields tab</p>
                    <p className="text-xs text-navy-600 dark:text-navy-400 mt-0.5">
                      <strong>1.</strong> Fetch this account's data and validations.{' '}
                      <strong>2.</strong> Upload a <strong>NetSuite {selectedNsEntityLabel} PDF</strong> so AI can map its fields.
                    </p>
                  </div>
                </div>
              )}
              <div className="rounded-lg border border-navy-200 dark:border-navy-800 bg-navy-50 dark:bg-navy-900/20 p-3 text-sm text-navy-700 dark:text-navy-300">
                Your template can be created now. You can continue refining fields, access, and integrations from the template detail page.
              </div>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between gap-2.5 px-5 py-3.5 border-t border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div>
            {isAdmin && wizardStep > 1 ? (
              <button onClick={goPrevStep} className="inline-flex h-10 items-center gap-2 px-4 rounded-lg border border-gray-200 dark:border-gray-700 text-[14px] font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.97] transition-all duration-150"><ArrowLeft className="h-4 w-4" />Back</button>
            ) : (
              <button onClick={onClose} className="inline-flex h-10 items-center gap-2 px-4 rounded-lg border border-gray-200 dark:border-gray-700 text-[14px] font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"><ArrowLeft className="h-4 w-4" />{isAdmin ? 'Back to Templates' : 'Cancel'}</button>
            )}
          </div>

          <div className="ml-auto flex items-center gap-4">
            {isAdmin && <span className="text-[13px] text-gray-500 dark:text-gray-400">Step {wizardStep} of {totalSteps}</span>}
            {isAdmin && !isLastStep ? (
              <button
                onClick={goNextStep}
                disabled={!canContinueStep()}
                className="group inline-flex h-10 items-center gap-2 px-5 rounded-lg bg-navy-800 text-white text-[14px] font-semibold shadow-sm hover:bg-navy-900 hover:shadow-md active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 transition-all duration-150"
              >
                Continue <ChevronRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={
                  !name.trim() || creating ||
                  (creationMode === 'from_template' && !sourceTemplateId) ||
                  // NetSuite: don't let a template be created against an account we
                  // can't reach, or without a company — everything downstream
                  // (valid vendors/items/locations, mandatory fields) is scoped by
                  // it, and discovering that at post time wastes a PO attempt.
                  // Only gated once the probe has finished, so a slow NetSuite
                  // doesn't leave the button permanently dead.
                  (creationMode === 'ns_purchase_order' && !nsLoading && (
                    !nsStatus?.connected ||
                    ((nsScope?.companies || []).length > 0 && !nsCompanyId)
                  ))
                }
                className="inline-flex h-10 items-center gap-2 px-5 rounded-lg bg-navy-800 text-white text-[14px] font-semibold shadow-sm hover:bg-navy-900 hover:shadow-md active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 transition-all duration-150"
              >
                {creating && <Loader2 className="h-4 w-4 animate-spin" />}
                {isAdmin
                  ? 'Create template'
                  : creationMode === 'bc_purchase_order'
                  ? `Create & Import BC ${selectedBcEntityLabel} Fields`
                  : creationMode === 'ns_purchase_order'
                  ? `Create & Import NS ${selectedNsEntityLabel} Fields`
                  : 'Create and Continue'}
              </button>
            )}
          </div>
        </div>
      </div>
  )

  const blankFieldEditorModal = blankFieldModalOpen ? createPortal(
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={closeBlankFieldModal} />
      <div role="dialog" aria-modal="true" aria-labelledby="field-editor-title" className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <h3 id="field-editor-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">
          {blankFieldModalMode === 'add' ? 'Add Field' : 'Edit Field'}
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Keep descriptions clear and specific - they guide the analyzer to extract accurately.
        </p>

        <div className="mt-5 space-y-4">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Display name</label>
                <input type="text" value={blankFieldDraft.label} onChange={(e) => setBlankFieldDraft((prev) => ({ ...prev, label: e.target.value }))} placeholder="Coupon Rate" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Field name (internal)</label>
                <div className="flex gap-2">
                  <input type="text" value={blankFieldDraft.key} onChange={(e) => setBlankFieldDraft((prev) => ({ ...prev, key: e.target.value }))} placeholder="coupon_rate" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 font-mono text-sm text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100" />
                  <button type="button" onClick={() => setBlankFieldDraft((prev) => ({ ...prev, key: toInternalFieldName(prev.label) || prev.key }))} className="rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700">Auto</button>
                </div>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Data type</label>
                <select value={blankFieldDraft.type} onChange={(e) => setBlankFieldDraft((prev) => ({ ...prev, type: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800">
                  <option value="text">Text</option><option value="number">Number</option><option value="currency">Currency</option><option value="percentage">Percentage</option><option value="date">Date</option><option value="boolean">Yes / No</option>
                  {blankFieldDraft.type === 'array_of_objects' && <option value="array_of_objects">Table (line items)</option>}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Section</label>
                <select value={blankFieldDraft.section || 'None'} onChange={(e) => setBlankFieldDraft((prev) => ({ ...prev, section: e.target.value }))} className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800">
                  <option value="None">None</option><option value="General">General</option><option value="Order Details">Order Details</option><option value="Invoice Details">Invoice Details</option>
                  {!['None', 'General', 'Order Details', 'Invoice Details'].includes(blankFieldDraft.section || 'None') && (
                    <option value={blankFieldDraft.section}>{blankFieldDraft.section}</option>
                  )}
                </select>
              </div>
            </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300">Description</label>
            <textarea rows={4} value={blankFieldDraft.description} onChange={(e) => setBlankFieldDraft((prev) => ({ ...prev, description: e.target.value }))} placeholder="Example: Annual coupon rate shown in percentage near financial terms section" className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm dark:border-gray-700 dark:bg-gray-800" />
          </div>

          <div className="flex items-center gap-3">
            <Toggle checked={!!blankFieldDraft.user_input} onChange={() => setBlankFieldDraft((prev) => ({ ...prev, user_input: !prev.user_input }))} />
            <div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300">Require User Input</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">When enabled, users must fill in this field in the plugin before submitting</p>
            </div>
          </div>
        </div>

        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={closeBlankFieldModal}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={applyBlankFieldModal}
            disabled={!blankFieldDraft.label.trim() || !blankFieldDraft.key.trim()}
            className="inline-flex items-center gap-2 rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            {blankFieldModalMode === 'add' ? <Plus className="h-4 w-4" /> : <CheckCircle2 className="h-4 w-4" />}
            {blankFieldModalMode === 'add' ? 'Add Field' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null

  const deleteConfirmModal = confirmDeleteOpen ? createPortal(
    <div className="fixed inset-0 z-[90] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={closeDeleteConfirmModal} />
      <div role="alertdialog" aria-modal="true" aria-labelledby="delete-confirmation-title" className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <h3 id="delete-confirmation-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">{confirmDeleteTitle || 'Delete Field'}</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">{confirmDeleteMessage || 'Are you sure you want to delete this field?'}</p>
        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={closeDeleteConfirmModal}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-700"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={confirmDeleteBlankFields}
            className="inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null

  const unsavedFieldsPromptModal = unsavedFieldsPromptOpen ? createPortal(
    <div className="fixed inset-0 z-[95] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 animate-[fadeIn_0.15s_ease-out]" onClick={() => setUnsavedFieldsPromptOpen(false)} />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="unsaved-fields-title"
        className="relative w-full max-w-md rounded-xl border border-gray-200 bg-white p-6 shadow-xl dark:border-gray-800 dark:bg-gray-900 animate-[fadeIn_0.2s_ease-out]"
      >
        <h3 id="unsaved-fields-title" className="text-lg font-semibold text-gray-900 dark:text-gray-100">Save field changes?</h3>
        <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
          You have unsaved changes to your extraction fields. Save them before moving on, or discard to continue without saving.
        </p>
        <div className="mt-6 flex items-center justify-end gap-2.5">
          <button
            type="button"
            onClick={discardFieldChangesAndContinue}
            className="rounded-lg border border-gray-300 px-4 py-2 text-sm dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 active:scale-[0.97] transition-all duration-150"
          >
            Discard changes
          </button>
          <button
            type="button"
            onClick={saveFieldChangesAndContinue}
            className="inline-flex items-center gap-2 rounded-lg bg-navy-800 px-4 py-2 text-sm font-medium text-white hover:bg-navy-900 shadow-sm hover:shadow-md active:scale-[0.97] transition-all duration-150 disabled:opacity-50"
          >
            <CheckCircle2 className="h-4 w-4" />
            Save & continue
          </button>
        </div>
      </div>
    </div>,
    document.body,
  ) : null

  if (fullPage) {
    return (
      <>
        {panel}
        {blankFieldEditorModal}
        {deleteConfirmModal}
        {unsavedFieldsPromptModal}
      </>
    )
  }

  return createPortal(
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ width: '100vw', height: '100vh' }}>
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        {panel}
      </div>
      {blankFieldEditorModal}
      {deleteConfirmModal}
      {unsavedFieldsPromptModal}
    </>,
    document.body,
  )
}

// ── Grid view ────────────────────────────────────────────────────────────────

// Fills the row at whatever width the sidebar leaves, instead of jumping
// between fixed breakpoint column counts.
const TEMPLATE_GRID_COLUMNS = 'grid-cols-[repeat(auto-fill,minmax(min(100%,21rem),1fr))]'
const FIELD_PREVIEW_COUNT = 5

function humanizeKey(key) {
  return String(key || '')
    .split(/[_\s]+/)
    .filter(Boolean)
    .map(w => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

// Icon and subtitle for a template card: where the template came from, which
// says more at a glance than "No description" on most of them.
function templateKind(template) {
  const method = template.creation_method
  if (template.ns_enabled || method === 'ns_purchase_order') {
    return { Icon: Boxes, label: `NetSuite · ${nsEntityDisplayName(template.ns_entity_type)}` }
  }
  if (template.bc_enabled || method === 'bc_purchase_order') {
    return { Icon: Building2, label: `Business Central · ${humanizeKey(template.bc_entity_type) || 'Purchase Order'}` }
  }
  const sourceDocs = parseSourceDocs(template.source_documents)
  if (sourceDocs.length > 0) {
    return {
      Icon: FileText,
      label: sourceDocs.length === 1 ? `Built from ${sourceDocs[0]}` : `Built from ${sourceDocs.length} documents`,
      title: sourceDocs.join('\n'),
    }
  }
  if (template.source_template_name) {
    return { Icon: Copy, label: `Copied from ${template.source_template_name}` }
  }
  const Icon = method === 'plugin_auto' ? Bot : method === 'ai_suggest' ? Sparkles : FileText
  return { Icon, label: getMethodLabel(method) }
}

function CopyableId({ value, label }) {
  const [copied, setCopied] = useState(false)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value)
      setCopied(true)
      setTimeout(() => setCopied(false), 1500)
    } catch {
      // Clipboard blocked; the id is still on screen to select by hand.
    }
  }

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied' : `Copy ${label}`}
      aria-label={`Copy ${label} ${value}`}
      className="group/copy inline-flex min-w-0 items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[11px] text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500/40 dark:text-gray-500 dark:hover:bg-gray-800 dark:hover:text-gray-200"
    >
      <span className="truncate">{value}</span>
      {copied
        ? <Check className="h-3 w-3 flex-shrink-0 text-emerald-500 animate-fade-in" />
        : <Copy className="h-3 w-3 flex-shrink-0 opacity-0 transition-opacity group-hover/copy:opacity-100 group-focus-visible/copy:opacity-100" />}
    </button>
  )
}

function TemplateCard({ template, aiJob, access }) {
  const href = `/templates/${template.id}`
  const cardLink = useCardLink(href)
  const ready = isAnalyzerReady(template)
  const kind = templateKind(template)
  const description = getTemplateDescription(template)
  // The list sends field_preview (the first few fields) plus counts rather than
  // every field; template.fields is still used when a caller has the full set.
  const fields = useMemo(
    () => (template.fields || template.field_preview || [])
      .filter(f => f.is_active !== false)
      .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)),
    [template.fields, template.field_preview],
  )
  const fieldCount = template.field_count || fields.length || 0
  const requiredCount = template.required_field_count ?? fields.filter(f => f.is_required).length
  const preview = fields.slice(0, FIELD_PREVIEW_COUNT)
  const moreCount = Math.max(0, fieldCount - preview.length)
  const analyzerId = template.analyzer?.azure_analyzer_id
  const aiRunning = aiJob && aiJob.status !== 'completed'
  const aiProgress = aiJob?.progress_percent || 0
  const touchedAt = template.updated_at || template.created_at

  // A small pop when a build flips the badge to ready. Not a CSS class: the
  // grid re-inserts cards when it reorders them, which would replay it.
  const badgeRef = useRef(null)
  const wasReady = useRef(ready)
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (ready && !wasReady.current && !reduced) {
      badgeRef.current?.animate?.(
        [{ opacity: 0, transform: 'scale(0.8)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 420, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
      )
    }
    wasReady.current = ready
  }, [ready])

  return (
    <article
      {...cardLink}
      onPointerMove={trackSpotlight}
      style={{ '--card-glow': 'var(--navy-500-eff)' }}
      className="collection-card group relative flex h-full cursor-pointer flex-col rounded-2xl border border-gray-200 bg-white p-5 shadow-[0_1px_2px_rgba(16,24,40,0.04)] transition-[transform,box-shadow,border-color] duration-300 ease-[cubic-bezier(0.22,1,0.36,1)] hover:border-gray-300 hover:shadow-[0_18px_40px_-22px_rgba(16,24,40,0.35)] motion-safe:hover:-translate-y-1 motion-safe:active:translate-y-0 motion-safe:active:scale-[0.985] has-[a:focus-visible]:ring-2 has-[a:focus-visible]:ring-navy-500/50 has-[a:focus-visible]:ring-offset-2 dark:border-gray-800 dark:bg-gray-900 dark:hover:border-gray-700 dark:hover:shadow-[0_18px_40px_-22px_rgba(0,0,0,0.8)] dark:has-[a:focus-visible]:ring-offset-gray-950"
    >
      <span aria-hidden="true" className="pointer-events-none absolute inset-x-8 top-0 h-px origin-center scale-x-0 bg-gradient-to-r from-transparent via-navy-500/70 to-transparent transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-x-100" />

      <div className="flex-1">
        <div className="flex items-start gap-3">
          <div
            className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl transition-transform duration-500 ease-[cubic-bezier(0.34,1.56,0.64,1)] motion-safe:group-hover:-rotate-6 motion-safe:group-hover:scale-110 ${
              ready
                ? 'bg-navy-100 text-navy-600 dark:bg-gray-800 dark:text-navy-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
            }`}
          >
            <kind.Icon className="h-5 w-5" aria-hidden="true" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5">
              <h3 className="truncate text-[15px] font-semibold leading-6 tracking-tight text-gray-900 dark:text-gray-50">
                <Link to={href} className="rounded-sm transition-colors group-hover:text-navy-700 focus:outline-none dark:group-hover:text-white">
                  {template.name}
                </Link>
              </h3>
              <TemplateOpCoChip template={template} />
              {access?.is_restricted && (
                <span title="Only people and groups with access can use this template" className="flex-shrink-0 text-gray-400 dark:text-gray-500">
                  <Lock className="h-3.5 w-3.5" aria-hidden="true" />
                  <span className="sr-only">Restricted access</span>
                </span>
              )}
            </div>
            <p className="truncate text-xs text-gray-500 dark:text-gray-400" title={kind.title || kind.label}>{kind.label}</p>
          </div>
          <span ref={badgeRef} className="flex-shrink-0">
            <AnalyzerStatusBadge template={template} />
          </span>
        </div>

        {description && (
          <p className="mt-3 line-clamp-2 text-[13px] leading-5 text-gray-600 dark:text-gray-400" title={description}>
            {description}
          </p>
        )}

        <div className="mt-4">
          <div className="mb-2 flex items-center justify-between gap-3">
            <p className="whitespace-nowrap text-[11px] font-medium text-gray-500 dark:text-gray-400">
              <span className="font-semibold tabular-nums text-gray-900 dark:text-gray-100">{fieldCount}</span> field{fieldCount !== 1 ? 's' : ''}
              {requiredCount > 0 && (
                <>
                  <span aria-hidden="true" className="mx-1 text-gray-300 dark:text-gray-600">·</span>
                  <span className="tabular-nums">{requiredCount}</span> required
                </>
              )}
            </p>
            {analyzerId && <CopyableId value={analyzerId} label="analyzer ID" />}
          </div>
          {preview.length > 0 ? (
            <ul className="flex flex-wrap gap-1.5" aria-label="Fields">
              {preview.map((field) => {
                const name = field.display_name || field.field_name
                return (
                  <li
                    key={field.id ?? field.field_name}
                    title={field.is_required ? `${name} (required)` : name}
                    className="inline-flex min-w-0 max-w-[10rem] items-center gap-0.5 rounded-md bg-gray-50 px-1.5 py-0.5 text-[11px] font-medium text-gray-600 ring-1 ring-inset ring-gray-200 dark:bg-gray-800/60 dark:text-gray-300 dark:ring-gray-700/70"
                  >
                    <span className="truncate">{name}</span>
                    {field.is_required && <span aria-hidden="true" className="text-navy-500 dark:text-navy-300">*</span>}
                  </li>
                )
              })}
              {moreCount > 0 && (
                <li className="inline-flex items-center px-1 py-0.5 text-[11px] font-medium text-gray-400 dark:text-gray-500">
                  +{moreCount} more
                </li>
              )}
            </ul>
          ) : fieldCount === 0 && (
            <p className="rounded-lg border border-dashed border-gray-200 px-3 py-2 text-xs text-gray-400 dark:border-gray-700 dark:text-gray-500">
              No fields yet. Open the template to add them.
            </p>
          )}
        </div>

        {aiRunning && (
          <div className="mt-3 rounded-lg bg-navy-50/70 px-2.5 py-2 dark:bg-navy-500/10">
            <div className="flex items-center justify-between gap-2 text-[11px] font-medium text-navy-700 dark:text-navy-200">
              <span className="inline-flex items-center gap-1.5">
                <Sparkles className="h-3 w-3 motion-safe:animate-pulse" aria-hidden="true" />
                AI is suggesting fields
              </span>
              {aiProgress > 0 && <span className="tabular-nums">{aiProgress}%</span>}
            </div>
            <div className="relative mt-1.5 h-1 overflow-hidden rounded-full bg-navy-100 dark:bg-navy-400/15">
              {aiProgress > 0
                ? <div className="h-full rounded-full bg-navy-500 transition-[width] duration-700 ease-out" style={{ width: `${aiProgress}%` }} />
                : <div className="collection-progress-bar absolute inset-y-0 left-0 w-1/3 rounded-full bg-navy-500" />}
            </div>
          </div>
        )}
        {aiJob?.status === 'completed' && (
          <Link
            to={`${href}?suggestion_job=${aiJob.id}`}
            className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-navy-50/70 px-2.5 py-2 text-[11px] font-semibold text-navy-700 transition-colors hover:bg-navy-100 dark:bg-navy-500/10 dark:text-navy-200 dark:hover:bg-navy-500/20"
          >
            <span className="inline-flex items-center gap-1.5">
              <Sparkles className="h-3 w-3" aria-hidden="true" />
              AI suggested fields are ready
            </span>
            <span className="inline-flex items-center gap-0.5">Review <ArrowRight className="h-3 w-3" aria-hidden="true" /></span>
          </Link>
        )}
      </div>

      <footer className="mt-4 flex items-center justify-between gap-3 border-t border-gray-100 pt-3 dark:border-gray-800">
        <div className="flex min-w-0 items-center gap-2">
          {template.created_by_name && (
            <>
              {template.created_by && (
                <UserAvatar user={{ id: template.created_by, username: template.created_by_name }} size="sm" className="!h-6 !w-6 !text-[10px]" />
              )}
              <span className="truncate text-xs font-medium text-gray-700 dark:text-gray-300" title={template.created_by_name}>{template.created_by_name}</span>
              <span aria-hidden="true" className="text-gray-300 dark:text-gray-600">·</span>
            </>
          )}
          <time
            dateTime={touchedAt || undefined}
            title={`${template.updated_at ? 'Updated' : 'Created'} ${formatDateTime(touchedAt)}`}
            className="whitespace-nowrap text-xs text-gray-500 dark:text-gray-400"
          >
            {template.updated_at ? 'Updated ' : ''}{formatRecent(touchedAt)}
          </time>
        </div>
        <span aria-hidden="true" className="inline-flex flex-shrink-0 items-center gap-1 text-xs font-semibold text-navy-600 opacity-0 transition-[opacity,transform] duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] motion-safe:-translate-x-1.5 group-hover:translate-x-0 group-hover:opacity-100 group-has-[a:focus-visible]:translate-x-0 group-has-[a:focus-visible]:opacity-100 dark:text-navy-300">
          Open <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </footer>

    </article>
  )
}

// Shared by the list header, rows and skeleton so the columns always line up.
// Spare width is shared out across the columns (the fr weights) rather than
// all going to the name, so wide screens don't leave a gap after it.
const TEMPLATE_LIST_COLS = 'grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 md:grid-cols-[minmax(0,2.4fr)_minmax(6rem,0.6fr)_minmax(10.5rem,1fr)_auto] lg:grid-cols-[minmax(16rem,2.6fr)_minmax(6rem,0.6fr)_minmax(10.5rem,0.9fr)_minmax(12.5rem,1fr)_minmax(8.5rem,0.8fr)_6.5rem]'

// Memoised: the page re-renders on every AI-job poll and search keystroke, and
// a row only needs to when its own template or job changes.
const TemplateListRow = React.memo(function TemplateListRow({ template, aiJob }) {
  const href = `/templates/${template.id}`
  const cardLink = useCardLink(href)
  const { glowRef, ...glowHandlers } = useRowGlow()
  const ready = isAnalyzerReady(template)
  const kind = templateKind(template)
  const description = getTemplateDescription(template)
  const fieldCount = template?.field_count || template?.fields?.length || 0
  const requiredCount = template.required_field_count ?? 0
  const touchedAt = template.updated_at || template.created_at
  const shownDate = formatDate(template.updated_at) || formatDate(template.created_at)
  const aiRunning = aiJob && aiJob.status !== 'completed'

  // Same pop as the card when the badge flips to ready.
  const badgeRef = useRef(null)
  const wasReady = useRef(ready)
  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (ready && !wasReady.current && !reduced) {
      badgeRef.current?.animate?.(
        [{ opacity: 0, transform: 'scale(0.8)' }, { opacity: 1, transform: 'scale(1)' }],
        { duration: 520, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)' },
      )
    }
    wasReady.current = ready
  }, [ready])

  return (
    <article
      {...cardLink}
      {...glowHandlers}
      data-ready={ready || undefined}
      className={`tl-row group cursor-pointer rounded-xl px-3 py-3 ${TEMPLATE_LIST_COLS}`}
    >
      <span aria-hidden="true" className="tl-row__bg">
        <span ref={glowRef} className="tl-row__glow" />
      </span>
      <span aria-hidden="true" className="tl-row__bar" />

      {/* Template */}
      <div className="flex min-w-0 items-center gap-3.5">
        <div
          title={kind.title || kind.label}
          className={`tl-row__icon flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl ${
            ready
              ? 'bg-navy-100 text-navy-600 dark:bg-gray-800 dark:text-navy-300'
              : 'bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300'
          }`}
        >
          <kind.Icon className="h-[18px] w-[18px]" aria-hidden="true" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 items-center gap-2">
            <Link
              to={href}
              className="truncate rounded-sm text-[15px] font-semibold tracking-tight text-gray-900 transition-colors duration-300 ease-out focus:outline-none group-hover:text-navy-700 dark:text-gray-50 dark:group-hover:text-white"
            >
              {template.name}
            </Link>
            <TemplateOpCoChip template={template} />
            {template.creation_method === 'bc_purchase_order' && (
              <span className="inline-flex flex-shrink-0 items-center gap-1 rounded px-1.5 py-0.5 text-[10px] font-semibold bg-navy-100 text-navy-700 dark:bg-navy-900/30 dark:text-navy-300">
                <Building2 className="h-2.5 w-2.5" /> Business Central
              </span>
            )}
            {aiRunning && (
              <span className="inline-flex flex-shrink-0 items-center gap-1 rounded-full bg-navy-50 px-1.5 py-0.5 text-[10px] font-semibold text-navy-700 ring-1 ring-inset ring-navy-100 dark:bg-navy-500/10 dark:text-navy-200 dark:ring-navy-400/20">
                <Sparkles className="h-2.5 w-2.5 motion-safe:animate-pulse" aria-hidden="true" />
                AI{aiJob.progress_percent > 0 ? ` ${aiJob.progress_percent}%` : '…'}
              </span>
            )}
          </div>
          <p
            title={description || undefined}
            className={`mt-0.5 truncate text-[13px] ${description ? 'text-gray-500 dark:text-gray-400' : 'italic text-gray-400 dark:text-gray-500'}`}
          >
            {description || 'No description'}
          </p>
        </div>
      </div>

      {/* Fields */}
      <div className="hidden md:block">
        <p className="flex items-baseline gap-1">
          <span className="text-base font-semibold tabular-nums text-gray-900 dark:text-gray-100">{fieldCount}</span>
          <span className="text-xs text-gray-400 dark:text-gray-500">{fieldCount === 1 ? 'field' : 'fields'}</span>
        </p>
        {requiredCount > 0 && (
          <p className="mt-0.5 text-[11px] tabular-nums text-gray-400 dark:text-gray-500">{requiredCount} required</p>
        )}
      </div>

      {/* Status */}
      <div className="hidden md:block">
        <span ref={badgeRef} className="inline-flex">
          <AnalyzerStatusBadge template={template} />
        </span>
      </div>

      {/* Created By */}
      <div className="hidden min-w-0 items-center gap-2.5 lg:flex">
        {template.created_by && (
          <span className="tl-row__avatar inline-flex flex-shrink-0">
            <UserAvatar user={{ id: template.created_by, username: template.created_by_name }} size="sm" className="!h-7 !w-7 !text-[11px] ring-2 ring-white dark:ring-gray-900" />
          </span>
        )}
        <span className="truncate text-sm font-medium text-gray-700 dark:text-gray-300" title={template.created_by_name || undefined}>{template.created_by_name || '-'}</span>
      </div>

      {/* Modified */}
      <div className="hidden lg:block">
        <time dateTime={touchedAt || undefined} title={formatDateTime(touchedAt) || undefined} className="block text-sm tabular-nums text-gray-700 dark:text-gray-300">
          {shownDate || '-'}
        </time>
        {touchedAt && <p className="mt-0.5 text-xs text-gray-400 dark:text-gray-500">{formatTimeAgo(touchedAt)}</p>}
      </div>

      {/* Open */}
      <div className="flex justify-end">
        <span aria-hidden="true" className="tl-open">
          <span className="tl-open__bg" />
          <span className="tl-open__label">Open</span>
          <ChevronRight className="tl-open__icon h-4 w-4" />
        </span>
      </div>
    </article>
  )
})

function TemplateListView({ templates, aiJobs }) {
  const total = templates.length
  const readyCount = templates.filter(isAnalyzerReady).length
  const buildCount = total - readyCount
  const readyPct = total ? Math.round((readyCount / total) * 100) : 0

  return (
    <section className="animate-view-in overflow-clip rounded-2xl border border-gray-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_8px_24px_-16px_rgba(16,24,40,0.12)] dark:border-gray-800 dark:bg-gray-900 dark:shadow-none">
      <div className="flex flex-wrap items-center gap-x-5 gap-y-2 px-5 pb-3 pt-4">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          <span className="tabular-nums">{total}</span>{' '}
          <span className="font-normal text-gray-500 dark:text-gray-400">{total === 1 ? 'template' : 'templates'}</span>
        </p>
        <span className="h-4 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />
        <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-60 motion-safe:animate-ping [animation-iteration-count:2]" />
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <span className="font-semibold tabular-nums">{readyCount}</span> ready
        </span>
        {buildCount > 0 && (
          <span className="inline-flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
            <span className="h-2 w-2 rounded-full bg-amber-500" aria-hidden="true" />
            <span className="font-semibold tabular-nums">{buildCount}</span> need a build
          </span>
        )}
        <div className="ml-auto hidden items-center gap-2.5 sm:flex" title={`${readyCount} of ${total} analyzers ready`}>
          <div className="h-1.5 w-36 overflow-hidden rounded-full bg-amber-100 dark:bg-amber-400/15">
            <div
              className="tl-meter h-full rounded-full bg-gradient-to-r from-emerald-400 to-emerald-500"
              style={{ width: `${readyPct}%` }}
            />
          </div>
          <span className="text-xs font-semibold tabular-nums text-gray-500 dark:text-gray-400">{readyPct}%</span>
        </div>
      </div>

      <div
        aria-hidden="true"
        className={`sticky top-16 z-10 border-y border-gray-100 bg-gray-50 px-5 py-2.5 dark:border-gray-800 dark:bg-gray-900 ${TEMPLATE_LIST_COLS}`}
      >
        {['Template', 'Fields', 'Status', 'Created By', 'Modified'].map((label, i) => (
          <span
            key={label}
            className={`text-[11px] font-semibold uppercase tracking-[0.08em] text-gray-500 dark:text-gray-400 ${i === 0 ? '' : i < 3 ? 'hidden md:block' : 'hidden lg:block'}`}
          >
            {label}
          </span>
        ))}
        <span />
      </div>

      <CollectionGrid
        items={templates}
        getKey={t => t.id}
        enterFrom="translateY(10px)"
        className="tl-rows relative px-2 py-2 [&>div+div]:border-t [&>div+div]:border-gray-100 dark:[&>div+div]:border-gray-800/70"
      >
        {(template) => <TemplateListRow template={template} aiJob={aiJobs[String(template.id)]} />}
      </CollectionGrid>
    </section>
  )
}

function TemplateListSkeleton() {
  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-center gap-4 px-5 pb-3 pt-4">
        <div className="skeleton h-4 w-28" />
        <div className="skeleton h-3 w-16" />
        <div className="skeleton ml-auto hidden h-1.5 w-36 rounded-full sm:block" />
      </div>
      <div className="border-y border-gray-100 bg-gray-50/70 px-5 py-3 dark:border-gray-800 dark:bg-gray-800/40">
        <div className="skeleton h-2.5 w-20" />
      </div>
      <div className="divide-y divide-gray-100 px-2 py-2 dark:divide-gray-800/70">
        {[...Array(6)].map((_, i) => (
          <div key={i} className={`px-3 py-3 ${TEMPLATE_LIST_COLS}`} style={{ opacity: 1 - i * 0.12 }}>
            <div className="flex items-center gap-3.5">
              <div className="skeleton h-10 w-10 rounded-xl" />
              <div className="flex-1 space-y-2">
                <div className="skeleton h-3.5 w-48 max-w-full" />
                <div className="skeleton h-2.5 w-72 max-w-full" />
              </div>
            </div>
            <div className="skeleton hidden h-4 w-12 md:block" />
            <div className="skeleton hidden h-5 w-28 rounded-full md:block" />
            <div className="hidden items-center gap-2.5 lg:flex">
              <div className="skeleton h-7 w-7 rounded-full" />
              <div className="skeleton h-3 w-24" />
            </div>
            <div className="hidden space-y-1.5 lg:block">
              <div className="skeleton h-3 w-20" />
              <div className="skeleton h-2.5 w-14" />
            </div>
            <div className="skeleton ml-auto h-8 w-8 rounded-lg" />
          </div>
        ))}
      </div>
    </div>
  )
}

function TemplateCardSkeleton() {
  return (
    <div className="flex flex-col rounded-2xl border border-gray-200 bg-white p-5 dark:border-gray-800 dark:bg-gray-900">
      <div className="flex items-start gap-3">
        <div className="skeleton h-10 w-10 rounded-xl" />
        <div className="flex-1 space-y-2">
          <div className="skeleton h-4 w-2/5" />
          <div className="skeleton h-3 w-3/5" />
        </div>
        <div className="skeleton h-5 w-24 rounded-full" />
      </div>
      <div className="mt-4 skeleton h-3 w-24" />
      <div className="mt-2 flex flex-wrap gap-1.5">
        <div className="skeleton h-5 w-20" />
        <div className="skeleton h-5 w-16" />
        <div className="skeleton h-5 w-24" />
        <div className="skeleton h-5 w-14" />
      </div>
      <div className="mt-4 flex items-center gap-2 border-t border-gray-100 pt-3 dark:border-gray-800">
        <div className="skeleton h-6 w-6 rounded-full" />
        <div className="skeleton h-3 w-40" />
      </div>
    </div>
  )
}

export default function Templates() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const toast = useToast()
  const { user: authUser } = useAuth()
  const features = useFeatures()
  const isAdmin = authUser?.role === 'admin'
  const isReader = isReadOnlyUser(authUser)

  const [templates, setTemplates] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [accessInfo, setAccessInfo] = useState({}) // templateId -> { is_restricted, access: [...] }

  const [createOpen, setCreateOpen] = useState(false)
  const [jsonImportOpen, setJsonImportOpen] = useState(false)
  const [jsonImportedId, setJsonImportedId] = useState(null)
  const [creating, setCreating] = useState(false)
  const [aiJobs, setAiJobs] = useState({}) // templateId -> job object
  const [sortBy, setSortBy] = useState('updated') // updated | name | created
  const [filterStatus, setFilterStatus] = useState('all') // all | has_analyzer | no_analyzer
  const [searchQuery, setSearchQuery] = useState('')
  const [viewMode, setViewMode] = useStoredViewMode('templates') // grid | list

  // Opening the create page from New Template, and closing it again: a navy
  // curtain wipes across the page content (runCurtainTransition).
  const openCreateFlow = () => {
    const next = new URLSearchParams(searchParams)
    next.set('create', '1')
    runCurtainTransition({
      enabled: isAdmin,
      label: 'New template',
      apply: () => {
        setSearchParams(next, { replace: false })
        setCreateOpen(true)
      },
    })
  }

  const closeCreateFlow = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('create')
    setSearchParams(next, { replace: false })
  }

  // Leaving the create page by its close button plays the curtain in reverse.
  // Other exits (after a create, which navigates on) stay instant.
  const leaveCreateFlow = () => {
    runCurtainTransition({
      enabled: isAdmin && createOpen,
      back: true,
      label: 'Templates',
      apply: () => {
        closeCreateFlow()
        setCreateOpen(false)
      },
    })
  }

  const sortedTemplates = useMemo(() => {
    let filtered = templates
    if (filterStatus === 'has_analyzer') {
      filtered = templates.filter(t => !!t.analyzer?.is_active)
    } else if (filterStatus === 'no_analyzer') {
      filtered = templates.filter(t => !t.analyzer?.is_active)
    }
    const q = searchQuery.trim().toLowerCase()
    if (q) {
      filtered = filtered.filter(t =>
        (t.name || '').toLowerCase().includes(q) ||
        (getTemplateDescription(t) || '').toLowerCase().includes(q)
      )
    }
    return [...filtered].sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return (a.name || '').localeCompare(b.name || '')
        case 'created':
          return new Date(b.created_at || 0) - new Date(a.created_at || 0)
        case 'updated':
        default:
          return new Date(b.updated_at || b.created_at || 0) - new Date(a.updated_at || a.created_at || 0)
      }
    })
  }, [templates, sortBy, filterStatus, searchQuery])

  const loadTemplates = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true)
      const res = await templatesApi.list()
      const loadedTemplates = res.data?.templates || []
      setTemplates(loadedTemplates)
      setError('')

      if (isAdmin && loadedTemplates.length > 0) {
        adminApi.getTemplateAccessSummary()
          .then(summaryRes => setAccessInfo(summaryRes.data || {}))
          .catch(() => setAccessInfo({}))
      }
    } catch (e) {
      console.error(e)
      setError('Failed to load templates')
    } finally {
      if (showLoading) setLoading(false)
    }
  }

  // Fetch active field_suggestion jobs to show progress badges
  const hasActiveJobs = useRef(false)
  const loadAiJobs = async () => {
    try {
      // One request for all three statuses instead of three separate ones — this
      // page already fires ~9 requests on mount, and the browser caps concurrent
      // HTTP/1.1 connections per origin at 6, with the notifications SSE stream
      // holding one of those open for the whole session. Three fewer connections
      // needed here measurably shortens how long the rest of the page waits.
      const res = await jobsApi.list({
        entity_type: 'template', job_type: 'field_suggestion',
        status: 'running,pending,completed', per_page: 100,
      })
      const jobs = res.data?.jobs || []
      const running = jobs.filter(j => j.status === 'running')
      const pending = jobs.filter(j => j.status === 'pending')
      const completed = jobs.filter(j => j.status === 'completed')
      hasActiveJobs.current = running.length > 0 || pending.length > 0
      const map = {}
      completed.forEach(j => { if (!map[j.entity_id]) map[j.entity_id] = j })
      // The template page restores only the latest suggestion job and skips it
      // once it has been accepted or discarded (result_consumed), so the
      // "suggestions ready" badge must not keep advertising it either.
      Object.keys(map).forEach(k => { if (map[k].result_consumed) delete map[k] })
      pending.forEach(j => { map[j.entity_id] = j })
      running.forEach(j => { map[j.entity_id] = j })
      setAiJobs(map)
    } catch { /* silent */ }
  }

  useEffect(() => {
    loadTemplates(true)
    const jobsTimer = setTimeout(() => {
      loadAiJobs()
    }, 250)
    const interval = setInterval(() => {
      if (hasActiveJobs.current) loadAiJobs()
    }, 30000)
    return () => {
      clearTimeout(jobsTimer)
      clearInterval(interval)
    }
  }, [])

  useEffect(() => {
    setCreateOpen(searchParams.get('create') === '1')
  }, [searchParams])

  const handleCreateTemplate = async (payload) => {
    if (!payload.name) return
    setCreating(true)
    const {
      access_user_ids,
      access_group_ids,
      connector_mode,
      connector_webhook_id,
      connector_name,
      connector_url,
      connector_events,
      connector_update_mode,
      connector_secret,
      connector_basic_auth_username,
      connector_basic_auth_password,
      prefill_fields,
      manager_id,
      ns_entity_label,
      ...createPayload
    } = payload

    const nsEntityLabel = ns_entity_label || 'Document'

    // Only a failure of the create call itself means no template exists and
    // "Create failed" is the truth. Everything past this point (approver
    // assignment, access, field import, webhooks, the success toast/redirect)
    // runs against an already-created template, so a hiccup there must never
    // show "Create failed" — that previously happened whenever any of those
    // follow-up steps threw something its own inner try/catch didn't already
    // swallow, misreporting a successful creation as a total failure.
    let created
    try {
      const res = await templatesApi.create(createPayload)
      created = res.data
    } catch (e) {
      console.error(e)
      toast.error('Create failed', e?.response?.data?.error || 'Unable to create template')
      setCreating(false)
      return
    }

    try {
      const isBcMethod = payload.creation_method === 'bc_purchase_order'

      // Assign approver / template manager if selected
      if ((payload.approver_id || payload.manager_id) && created.id) {
        try {
          const assignments = {}
          if (payload.approver_id) assignments.approver_id = payload.approver_id
          if (payload.manager_id) assignments.manager_id = payload.manager_id
          await templatesApi.updateAssignments(created.id, assignments)
        } catch (e) {
          console.warn('Approver/manager assignment failed:', e)
        }
      }

      // Optional access assignment from wizard
      if (created.id) {
        const userIds = Array.isArray(access_user_ids) ? access_user_ids : []
        const groupIds = Array.isArray(access_group_ids) ? access_group_ids : []
        if (userIds.length > 0 || groupIds.length > 0) {
          try {
            await Promise.all([
              adminApi.setTemplateAccess(created.id, {
                is_restricted: true,
                user_ids: userIds,
              }),
              groupsApi.setTemplateGroupAccess(created.id, groupIds),
            ])
          } catch (e) {
            console.warn('Template access assignment failed:', e)
            toast.warning('Template created', 'Access selections could not be fully applied. You can update them in Access & Ownership.')
          }
        }
      }

      // Field prefill / reconciliation.
      // - "Start from blank": prefill_fields are brand-new fields, safe to import as-is.
      // - "Copy existing template": the backend already copied the source template's
      //   fields verbatim, so we must reconcile against what the user edited in the
      //   Configure Fields step (renames/required changes on kept fields, deletions,
      //   and any newly added fields) rather than blindly re-importing.
      if (created.id && prefill_fields && Object.keys(prefill_fields).length > 0) {
        if (payload.creation_method === undefined && payload.source_template_id) {
          try {
            const existingFieldsRes = await templatesApi.getFields(created.id)
            const existingFields = existingFieldsRes.data?.fields || existingFieldsRes.data || []
            const existingByName = {}
            existingFields.forEach((f) => { existingByName[f.field_name] = f })

            const keptKeys = new Set(Object.keys(prefill_fields))

            // Delete fields the user removed in the wizard
            const deletions = existingFields.filter((f) => !keptKeys.has(f.field_name))
            await Promise.all(deletions.map((f) => templatesApi.deleteField(created.id, f.id).catch(() => null)))

            // Update or create each kept/added field
            await Promise.all(Object.entries(prefill_fields).map(([fieldName, def]) => {
              const existing = existingByName[fieldName]
              const body = {
                display_name: def.label || fieldName,
                field_type: def.type || 'text',
                description: def.description || '',
                is_required: !!def.required,
              }
              if (existing) {
                return templatesApi.updateField(created.id, existing.id, body).catch(() => null)
              }
              return templatesApi.addField(created.id, { field_name: fieldName, ...body }).catch(() => null)
            }))
          } catch (e) {
            console.warn('Template field reconciliation failed:', e)
            toast.warning('Template created', 'Template was created, but your field edits could not be fully applied. You can adjust them in the Fields tab.')
          }
        } else {
          try {
            await templatesApi.importFields(created.id, {
              fields: prefill_fields,
              clear_existing: false,
            })
          } catch (e) {
            console.warn('Template prefill field import failed:', e)
            toast.warning('Template created', 'Template was created, but suggested fields could not be imported. You can add them in the Fields tab.')
          }
        }
      }

      const isNsMethod = payload.creation_method === 'ns_purchase_order'

      const routeToWebhooks = connector_mode === 'select_existing' || connector_mode === 'create_webhook'

      if (created.id && connector_mode === 'select_existing' && connector_webhook_id) {
        try {
          const linked = await templatesApi.linkWebhook(created.id, connector_webhook_id)
          if (linked?.data?.requires_payload_mapping) {
            toast.warning('Map Payload needed', 'The connector you linked requires a Map Payload. Open the template\'s Connectors tab and set it before approving requests, or nothing will be sent.')
          }
        } catch (e) {
          console.warn('Template webhook link failed:', e)
          toast.warning('Template created', 'Could not link the selected connector. You can link it in the Webhooks tab.')
        }
      }

      if (created.id && connector_mode === 'create_webhook' && connector_name && connector_url) {
        try {
          const webhookRes = await api.post('/webhooks', {
            name: connector_name,
            url: connector_url,
            events: connector_events || 'request.approved',
            update_mode: connector_update_mode || 'append',
            secret: connector_secret || undefined,
            basic_auth_username: connector_basic_auth_username || undefined,
            basic_auth_password: connector_basic_auth_password || undefined,
          })
          const newWebhookId = webhookRes?.data?.webhook?.id || webhookRes?.data?.id
          if (newWebhookId) {
            await templatesApi.linkWebhook(created.id, newWebhookId)
          } else {
            toast.warning('Template created', 'Connector was created but could not be linked automatically. Please link it in the Webhooks tab.')
          }
        } catch (e) {
          console.warn('Template webhook create/link failed:', e)
          toast.warning('Template created', e?.response?.data?.error || 'Could not create and link connector. You can finish this in the Webhooks tab.')
        }
      }

      if (isBcMethod) {
        const count   = prefill_fields ? Object.keys(prefill_fields).length : (created.bc_fields_imported || 0)
        const warning = created.bc_warning
        if (warning) {
          toast.warning('Business Central template created', warning)
        } else if (count === 0) {
          toast.warning('Business Central template created', 'No fields were added. Add fields manually in the Fields tab.')
        } else {
          toast.success('Business Central template created', `${count} BC Purchase Order field${count !== 1 ? 's' : ''} added successfully.`)
        }
        closeCreateFlow()
        navigate(`/templates/${created.id}${routeToWebhooks ? '?tab=webhooks' : '?tab=fields'}`)
      } else if (isNsMethod) {
        // prefill_fields (from the document-upload AI mapping step) may already
        // populate fields on creation; otherwise ns_fields_imported is 0 because
        // an NS template intentionally creates no fields up front when the
        // wizard's checklist/upload mapping hasn't produced any yet — they come
        // from the PDF mapping / Fields tab step instead. Reading that 0 as a
        // failure was alarming and wrong, so we only warn when there is an
        // explicit backend warning.
        const count   = prefill_fields ? Object.keys(prefill_fields).length : (created.ns_fields_imported || 0)
        const warning = created.ns_warning
        if (warning) {
          toast.warning('NetSuite template created', warning)
        } else if (count === 0) {
          toast.success(
            'NetSuite template created',
            `Next: fetch this account's data and validations, then upload a ${nsEntityLabel} PDF to map fields.`,
          )
        } else {
          toast.success('NetSuite template created', `${count} NS ${nsEntityLabel} field${count !== 1 ? 's' : ''} added successfully.`)
        }
        closeCreateFlow()
        navigate(`/templates/${created.id}${routeToWebhooks ? '?tab=webhooks' : '?tab=fields'}`)
      } else {
        toast.success('Template created', 'Now add fields and build your analyzer.')
        closeCreateFlow()
        navigate(`/templates/${created.id}${routeToWebhooks ? '?tab=webhooks' : ''}`)
      }
    } catch (e) {
      // The template itself is already created at this point (see the try/catch
      // above) — a failure here means a follow-up step (access, fields,
      // webhooks, the redirect) hit something its own inner try/catch didn't
      // cover. Say so honestly instead of "Create failed", and still land the
      // user back on the list so they aren't stuck looking at a dead modal.
      console.error(e)
      toast.warning('Template created', 'Some follow-up setup could not be completed. Check the template in the list and finish it there.')
      closeCreateFlow()
      navigate('/templates')
      loadTemplates(false)
    } finally {
      setCreating(false)
    }
  }

  if (isAdmin && createOpen) {
    return (
      <div className="space-y-6">
        <CreateTemplateModal
          open={createOpen}
          onClose={leaveCreateFlow}
          onCreate={handleCreateTemplate}
          creating={creating}
          existingTemplates={templates}
          isAdmin={isAdmin}
          fullPage
          onOpenJsonImport={features.netsuite ? () => { setJsonImportedId(null); setJsonImportOpen(true) } : null}
        />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex flex-col gap-4">
        <div className="flex items-center gap-3.5">
          <div className="hidden sm:flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-navy-700 to-navy-900 text-white shadow-sm ring-1 ring-navy-900/10">
            <FileStack className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-gray-100">Templates</h1>
              {!loading && (
                <span className="inline-flex items-center rounded-full bg-navy-50 px-2.5 py-0.5 text-xs font-semibold text-navy-700 ring-1 ring-inset ring-navy-200 dark:bg-navy-800/50 dark:text-navy-200 dark:ring-navy-700">
                  {templates.length}
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Create templates in the web app, then select them in the plugin during extraction.
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Search */}
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search templates…"
              aria-label="Search templates"
              className="h-9 w-40 sm:w-56 pl-9 pr-8 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-gray-200 placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 transition-colors"
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                aria-label="Clear search"
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* View toggle */}
          <ViewModeToggle value={viewMode} onChange={setViewMode} />

          {/* Divider */}
          <span className="hidden sm:block h-6 w-px bg-gray-200 dark:bg-gray-700" aria-hidden="true" />

          <select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            aria-label="Filter templates by analyzer status"
            className="h-9 px-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 dark:bg-gray-800 bg-white cursor-pointer transition-colors hover:border-gray-300 dark:hover:border-gray-600"
          >
            <option value="all">All Templates</option>
            <option value="has_analyzer">Analyzer Ready</option>
            <option value="no_analyzer">No Analyzer</option>
          </select>
          <select
            value={sortBy}
            onChange={e => setSortBy(e.target.value)}
            aria-label="Sort templates"
            className="h-9 px-3 border border-gray-200 dark:border-gray-700 rounded-lg text-sm font-medium text-gray-700 dark:text-gray-200 focus:outline-none focus:ring-2 focus:ring-navy-500/30 focus:border-navy-500 dark:bg-gray-800 bg-white cursor-pointer transition-colors hover:border-gray-300 dark:hover:border-gray-600"
          >
            <option value="updated">Last Modified</option>
            <option value="name">Name (A–Z)</option>
            <option value="created">Date Created</option>
          </select>
          <button
            onClick={() => !isReader && openCreateFlow()}
            disabled={isReader}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className={`inline-flex items-center gap-2 h-9 px-4 rounded-lg text-sm font-semibold transition-all sm:ml-auto ${isReader ? 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'cta-new relative overflow-hidden bg-navy-800 text-white shadow-sm hover:bg-navy-900 hover:shadow-md ring-1 ring-navy-900/20'}`}
          >
            {!isReader && <span aria-hidden="true" className="cta-new__sheen" />}
            <Plus className="cta-new__icon h-4 w-4" />
            New Template
          </button>
        </div>
      </div>

      {loading && (
        viewMode === 'list' ? (
          <TemplateListSkeleton />
        ) : (
          <div className={`grid gap-4 ${TEMPLATE_GRID_COLUMNS}`}>
            {[...Array(6)].map((_, i) => <TemplateCardSkeleton key={i} />)}
          </div>
        )
      )}

      {!loading && error && (
        <div className="rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/20 p-6">
          <div className="flex items-start gap-3">
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-red-100 dark:bg-red-900/40">
              <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-300">Couldn’t load templates</h3>
              <p className="text-sm text-red-600 dark:text-red-400 mt-0.5">{error}</p>
              <button
                onClick={loadTemplates}
                className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium transition-colors"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && sortedTemplates.length === 0 && (
        <div className="rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 p-12 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-navy-50 to-navy-50 dark:from-navy-900/40 dark:to-navy-900/20 ring-1 ring-navy-100 dark:ring-navy-800">
            <FileStack className="h-8 w-8 text-navy-500 dark:text-navy-300" />
          </div>
          <h2 className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">
            {searchQuery.trim()
              ? 'No matching templates'
              : filterStatus === 'all' ? 'No templates yet' : 'No templates match this filter'}
          </h2>
          <p className="mx-auto mt-1 max-w-sm text-sm text-gray-500 dark:text-gray-400">
            {searchQuery.trim()
              ? `No templates match “${searchQuery.trim()}”. Try a different search term.`
              : filterStatus === 'all'
              ? 'Start by creating a template with the fields you want extracted.'
              : 'Try a different filter, or create a new template.'}
          </p>
          <button
            onClick={() => !isReader && openCreateFlow()}
            disabled={isReader}
            title={isReader ? READ_ONLY_MESSAGE : undefined}
            className={`mt-5 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold transition-all ${isReader ? 'bg-gray-200 dark:bg-gray-800 text-gray-500 dark:text-gray-400 cursor-not-allowed' : 'bg-navy-800 text-white shadow-sm hover:bg-navy-900 hover:shadow-md active:scale-[0.98]'}`}
          >
            <Plus className="h-4 w-4" />
            Create First Template
          </button>
        </div>
      )}

      {!loading && !error && sortedTemplates.length > 0 && viewMode === 'list' && (
        <TemplateListView
          templates={sortedTemplates}
          aiJobs={aiJobs}
        />
      )}

      {!loading && !error && sortedTemplates.length > 0 && viewMode === 'grid' && (
        <CollectionGrid items={sortedTemplates} getKey={t => t.id} className={`grid gap-4 ${TEMPLATE_GRID_COLUMNS}`}>
          {(template) => (
            <TemplateCard
              template={template}
              aiJob={aiJobs[String(template.id)]}
              access={isAdmin ? accessInfo[String(template.id)] : null}
            />
          )}
        </CollectionGrid>
      )}

      <CreateTemplateModal
        open={createOpen}
        onClose={closeCreateFlow}
        onCreate={handleCreateTemplate}
        creating={creating}
        existingTemplates={templates}
        isAdmin={isAdmin}
        fullPage={false}
        onOpenJsonImport={features.netsuite ? () => { setJsonImportedId(null); setJsonImportOpen(true) } : null}
      />
      {jsonImportOpen && (
        <TemplateJsonImportModal
          onClose={() => {
            setJsonImportOpen(false)
            if (jsonImportedId) navigate(`/templates/${jsonImportedId}`)
          }}
          onApplied={(result) => {
            setJsonImportedId(result?.template_id || null)
            loadTemplates(false)
          }}
        />
      )}
    </div>
  )
}
